import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Search, MapPin, Plus, Edit2, Trash2, X, Navigation, Clock, Camera, Shuffle, LogOut, Shield, Settings, User, ChevronLeft, ChevronRight, Mic, RefreshCw, UtensilsCrossed, Target, Store, Calendar, Map, Info, Zap, Utensils, Star, Rocket, Sparkles, Volume2, Archive, ArchiveRestore } from 'lucide-react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Checkbox } from './components/ui/checkbox';
import Login from './Login';
import AdminPanel from './AdminPanel';
import GroupsAdmin from './components/GroupsAdmin';
import RestaurantMap from './RestaurantMap';
import UserProfile from './components/UserProfile';
import AddToHomeScreen from './components/AddToHomeScreen';
import VersionBadge from './components/VersionBadge';
import ActivityTab from './components/ActivityTab';
import { registerBiometric, removeBiometric, hasBiometricEnrolled, isBiometricAvailable } from './utils/biometric';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Timezone helpers for the backup schedule.
// Backend stores wall-clock HH:MM + day + an IANA tz (e.g. America/Chicago).
const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};
// Human label for a schedule tz (e.g. "America/Chicago" -> "CDT").
const tzShortLabel = (tzName) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tzName, timeZoneName: 'short' }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value || tzName;
  } catch {
    return tzName || 'Local';
  }
};
const formatHHMM12 = (hhmm) => {
  if (!hhmm || !hhmm.includes(':')) return hhmm || '';
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
};

// Check if this is preview environment
const isPreviewEnvironment = API_URL.includes('preview') || API_URL.includes('localhost');

// Configure axios interceptor to include auth token in all requests
axios.interceptors.request.use(
  (config) => {
    // Read token from localStorage to ensure it's always current
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration globally
let sessionExpiredShown = false;

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Silently handle 401 errors — clear session and let React show login page
    if (error.response?.status === 401) {
      const isLoginEndpoint = error.config?.url?.includes('/auth/login');
      const isLogoutEndpoint = error.config?.url?.includes('/auth/logout');
      const isPublicEndpoint = error.config?.url?.includes('/public/');
      
      if (!isLoginEndpoint && !isLogoutEndpoint && !isPublicEndpoint && !sessionExpiredShown) {
        const errorDetail = error.response?.data?.detail || '';
        const isAuthError = errorDetail.toLowerCase().includes('token') || 
                            errorDetail.toLowerCase().includes('expired') ||
                            errorDetail.toLowerCase().includes('unauthorized') ||
                            errorDetail.toLowerCase().includes('not authenticated');
        
        if (isAuthError) {
          sessionExpiredShown = true;
          console.log('Auth error - clearing session silently');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('token');
          localStorage.removeItem('user_email');
          localStorage.removeItem('display_name');
          localStorage.removeItem('is_admin');
          delete axios.defaults.headers.common['Authorization'];
          // Don't alert or redirect — React state will handle showing the login page
        }
      }
    }
    return Promise.reject(error);
  }
);

// Helper function to get full photo URL (handles both local and Cloudinary URLs)
const getPhotoUrl = (url) => {
  if (!url) return '';
  // If URL already starts with http:// or https://, it's a full URL (Cloudinary)
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // Otherwise, it's a local URL that needs the API_URL prefix
  return `${API_URL}${url}`;
};

// Returns YYYY-MM-DD using user's LOCAL timezone (avoids the UTC shift bug where
// 7pm CST stored as next day's UTC date). Use this for date defaults.
const getLocalDateString = (d) => {
  const date = d instanceof Date ? d : new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [authToken, setAuthToken] = useState('');
  
  // Update axios header whenever authToken changes
  useEffect(() => {
    if (authToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      console.log('🔑 Auth token set in axios headers');
    } else {
      delete axios.defaults.headers.common['Authorization'];
      console.log('🔓 Auth token removed from axios headers');
    }
  }, [authToken]);
  
  const [showAdminPanel, setShowAdminPanel] = useState(() => localStorage.getItem('das.showAdminPanel') === 'true');
  const [adminTab, setAdminTab] = useState(() => localStorage.getItem('das.adminTab') || 'analytics'); // 'analytics', 'users', 'settings', 'backup'
  const [viewingAsUser, setViewingAsUser] = useState(null);
  const [originalAdminContext, setOriginalAdminContext] = useState(null);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showProfilePage, setShowProfilePage] = useState(false);
  // Merge mode: when set, holds the "primary" restaurant the user clicked first.
  // Clicking another restaurant's Merge button while this is set will fire the
  // merge backend call.
  const [mergePrimary, setMergePrimary] = useState(null); // {id, name}
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    newDisplayName: '',
    newEmail: '',
    oldPin: '',
    newPin: '',
    confirmPin: ''
  });
  const [isCheckInDialogOpen, setIsCheckInDialogOpen] = useState(false);
  const [detectedRestaurant, setDetectedRestaurant] = useState(null);
  const [checkInLocation, setCheckInLocation] = useState(null);
  const [userGroups, setUserGroups] = useState('');
  const [ownedGroup, setOwnedGroup] = useState('');
  const [joinGroupName, setJoinGroupName] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [backupEmail, setBackupEmail] = useState('wasone@airmail.net');
  const [backupSchedule, setBackupSchedule] = useState('weekly');
  const [backupEnabled, setBackupEnabled] = useState(false);
  const [backupSettingsSaved, setBackupSettingsSaved] = useState(false);
  const [backupTime, setBackupTime] = useState('02:00'); // wall-clock HH:MM in backupTz
  const [backupTz, setBackupTz] = useState(getBrowserTimezone());
  const [backupDayOfWeek, setBackupDayOfWeek] = useState(6); // 0=Mon ... 6=Sun (LOCAL day)
  const [backupDayOfMonth, setBackupDayOfMonth] = useState(1);
  const [lastBackupAt, setLastBackupAt] = useState(null);
  const [lastBackupStatus, setLastBackupStatus] = useState(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateRestaurant, setDuplicateRestaurant] = useState(null);
  const [pendingRestaurantData, setPendingRestaurantData] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  
  // SMTP Settings state
  const [smtpSettings, setSmtpSettings] = useState({
    host: 'SMTP.NameBrightMail.com',
    port: '587',
    user: 'support@dineasaurus.com',
    password: '',
    fromEmail: 'support@dineasaurus.com'
  });
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [footerSettings, setFooterSettings] = useState({
    siteName: 'DineASaurus.com',
    siteUrl: 'https://dineasaurus.com',
    createdBy: 'Bill Casso',
    version: '',
    copyrightYears: '2025'
  });
  const loadedVersionRef = useRef(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [savingFooter, setSavingFooter] = useState(false);
  
  const [restaurants, setRestaurants] = useState([]);
  const [allUsersRestaurants, setAllUsersRestaurants] = useState([]);
  const [filteredRestaurants, setFilteredRestaurants] = useState([]);
  const [showArchived, setShowArchived] = useState(() => localStorage.getItem('das.showArchived') === '1');
  const showArchivedMounted = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [showNearest, setShowNearest] = useState(false);
  const [showLatestVisitsView, setShowLatestVisitsView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    price: [],
    location: [],
    patio: null,
    dogFriendly: null,
    heineken: null,
    ownership: [],
    ethnicity: []
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [sortBy, setSortBy] = useState('name');
  const [isVisitDialogOpen, setIsVisitDialogOpen] = useState(false);
  const [isQuickAddVisitOpen, setIsQuickAddVisitOpen] = useState(false);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState('');
  const [selectedRestaurantForVisit, setSelectedRestaurantForVisit] = useState(null);
  const [visitDate, setVisitDate] = useState('');
  const [visitUserName, setVisitUserName] = useState('');
  const [visitFavorites, setVisitFavorites] = useState('');
  const [visitDislikes, setVisitDislikes] = useState('');
  const [visitNotes, setVisitNotes] = useState('');
  const [visitRating, setVisitRating] = useState('');
  const [visitStaffNotes, setVisitStaffNotes] = useState('');
  const [visitBathroomRating, setVisitBathroomRating] = useState('');
  const [visitCost, setVisitCost] = useState('');
  const [visitGuests, setVisitGuests] = useState('');
  const [isVisitHistoryOpen, setIsVisitHistoryOpen] = useState(false);
  const [selectedRestaurantHistory, setSelectedRestaurantHistory] = useState(null);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [editingAddressValue, setEditingAddressValue] = useState('');
  const [visitHistory, setVisitHistory] = useState([]);
  const [isLatestVisitsOpen, setIsLatestVisitsOpen] = useState(false);
  const [latestVisits, setLatestVisits] = useState([]);
  const [isEditVisitOpen, setIsEditVisitOpen] = useState(false);
  const [selectedVisitForEdit, setSelectedVisitForEdit] = useState(null);
  const [isPhotosOpen, setIsPhotosOpen] = useState(false);
  const [selectedRestaurantPhotos, setSelectedRestaurantPhotos] = useState(null);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [isTryThisDialogOpen, setIsTryThisDialogOpen] = useState(false);
  const [tryThisRestaurantName, setTryThisRestaurantName] = useState('');
  const [onDeckRestaurants, setOnDeckRestaurants] = useState([]);
  const [onDeckSortBy, setOnDeckSortBy] = useState('distance'); // 'name' or 'distance'
  const [isEditOnDeckOpen, setIsEditOnDeckOpen] = useState(false);
  const [selectedOnDeckRestaurant, setSelectedOnDeckRestaurant] = useState(null);
  const [editOnDeckName, setEditOnDeckName] = useState('');
  const [editOnDeckAddress, setEditOnDeckAddress] = useState('');
  const [isPhotoViewerOpen, setIsPhotoViewerOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [photoViewerPhotos, setPhotoViewerPhotos] = useState([]);
  const [restaurantPhotos, setRestaurantPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [addRestaurantPhotos, setAddRestaurantPhotos] = useState([]);
  const [addVisitPhotos, setAddVisitPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false); // Ref for immediate check
  const [isRestaurantDetailsOpen, setIsRestaurantDetailsOpen] = useState(false);
  const [selectedRestaurantDetails, setSelectedRestaurantDetails] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [selectedVisitDetail, setSelectedVisitDetail] = useState(null);
  const [fetchingGoogleRatings, setFetchingGoogleRatings] = useState({});
  const [previousTab, setPreviousTab] = useState(null);
  // Panels expanded by default on both mobile and desktop
  const [isNearestCollapsed, setIsNearestCollapsed] = useState(false);
  const [isRecentCollapsed, setIsRecentCollapsed] = useState(false);
  const [searchHistory, setSearchHistory] = useState(() => {
    const saved = localStorage.getItem('searchHistory');
    return saved ? JSON.parse(saved) : [];
  });
  const [isListening, setIsListening] = useState(false);

  const restaurantImages = [
    'https://images.unsplash.com/photo-1710846929339-72634edf6f5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzYwMzEwNzMzfDA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1569096651661-820d0de8b4ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxyZXN0YXVyYW50JTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzYwMzEwNzMzfDA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1523368749929-6b2bf370dbf8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxyZXN0YXVyYW50JTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzYwMzEwNzMzfDA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1579027989536-b7b1f875659b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHw0fHxyZXN0YXVyYW50JTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzYwMzEwNzMzfDA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1726678640183-2b64ed27ffdf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxjYWZlJTIwc3RvcmVmcm9udHxlbnwwfHx8fDE3NjAzMTA3NDR8MA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1678528854861-7a05ff744221?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxjYWZlJTIwc3RvcmVmcm9udHxlbnwwfHx8fDE3NjAzMTA3NDR8MA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1571218371219-0a0649fb9748?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxjYWZlJTIwc3RvcmVmcm9udHxlbnwwfHx8fDE3NjAzMTA3NDR8MA&ixlib=rb-4.1.0&q=85',
    'https://images.unsplash.com/photo-1690700668983-c730d30a6bdd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxjYWZlJTIwc3RvcmVmcm9udHxlbnwwfHx8fDE3NjAzMTA3NDR8MA&ixlib=rb-4.1.0&q=85',
    'https://images.pexels.com/photos/34263625/pexels-photo-34263625.jpeg',
    'https://images.pexels.com/photos/34261595/pexels-photo-34261595.jpeg',
    'https://images.pexels.com/photos/34233539/pexels-photo-34233539.jpeg',
    'https://images.pexels.com/photos/34223125/pexels-photo-34223125.jpeg'
  ];

  const [formData, setFormData] = useState({
    name: '',
    location: '',
    fullAddress: '',
    favoriteItems: '',
    dislikes: '',
    price: '',
    patio: false,
    dogFriendly: false,
    heineken: false,
    staff: '',
    ownership: '',
    lastVisit: '',
    menuLink: '',
    imageUrl: '',
    latitude: '',
    longitude: '',
    ethnicity: '',
    restroomRating: '',
    initialRating: '',
    googleRating: '',
    userEmail: '',
    firstVisitDate: getLocalDateString(), // Default to today (local time)
    initialGuests: '',
    initialCost: ''
  });

  const [uniqueLocations, setUniqueLocations] = useState([]);
  const [locationsByState, setLocationsByState] = useState({});
  const [uniquePrices, setUniquePrices] = useState([]);
  const [uniqueOwnership, setUniqueOwnership] = useState([]);
  const [uniqueCuisines, setUniqueCuisines] = useState([]);
  const [recentVisits, setRecentVisits] = useState([]);
  const [nearbyRestaurants, setNearbyRestaurants] = useState([]);
  const [nearbyStatus, setNearbyStatus] = useState('idle'); // idle | loading | loaded | error | denied | no-https
  const [photoMontage, setPhotoMontage] = useState([]);
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('das.activeTab') || 'dashboard');

  // Persist navigation state so a hard refresh lands you on the same tab/view
  // instead of the default dashboard.
  useEffect(() => {
    try { localStorage.setItem('das.activeTab', activeTab || 'dashboard'); } catch (_) {}
  }, [activeTab]);
  useEffect(() => {
    try { localStorage.setItem('das.adminTab', adminTab || 'analytics'); } catch (_) {}
  }, [adminTab]);
  useEffect(() => {
    try { localStorage.setItem('das.showAdminPanel', showAdminPanel ? 'true' : 'false'); } catch (_) {}
  }, [showAdminPanel]);
  const [allRestaurantsLoaded, setAllRestaurantsLoaded] = useState(false);
  const [allRestaurantsList, setAllRestaurantsList] = useState([]);
  const initialLoadComplete = React.useRef(false);
  const [sortColumn, setSortColumn] = useState('name');
  const [isFromRandomPick, setIsFromRandomPick] = useState(false);
  const [appVersion, setAppVersion] = useState('1.0');
  const [sortOrder, setSortOrder] = useState('asc');
  const [allUsersSortColumn, setAllUsersSortColumn] = useState('name');
  const [allUsersSortOrder, setAllUsersSortOrder] = useState('asc');
  const [ethnicityOther, setEthnicityOther] = useState('');
  
  // All Dashboard state (for admin - all users' data)
  const [allRecentVisits, setAllRecentVisits] = useState([]);
  const [allNearbyRestaurants, setAllNearbyRestaurants] = useState([]);

  // Helper function to format dates consistently to "Mon DD, YYYY" format
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    
    // If already in "Mon DD, YYYY" format, return as is
    if (/^[A-Za-z]{3}\s\d{1,2},\s\d{4}$/.test(dateStr)) {
      return dateStr;
    }
    
    // If in old "Mon YYYY" format (without day), try to keep it but it's incomplete
    if (/^[A-Za-z]{3}\s\d{4}$/.test(dateStr)) {
      return dateStr; // Return as-is for old data
    }
    
    // If in YYYY-MM-DD format, convert to "Mon DD, YYYY" in user's local timezone
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      try {
        const dateObj = new Date(dateStr + 'T12:00:00');
        return dateObj.toLocaleDateString('en-US', { 
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      } catch (e) {
        return dateStr; // Return original if parsing fails
      }
    }
    
    // Return as-is for any other format
    return dateStr;
  };

  const formatDateNoYear = (dateStr) => {
    if (!dateStr) return '';
    
    // If already in "Mon DD, YYYY" format, remove year
    if (/^[A-Za-z]{3}\s\d{1,2},\s\d{4}$/.test(dateStr)) {
      return dateStr.replace(/,\s\d{4}$/, '');
    }
    
    // If in YYYY-MM-DD format, convert to "Mon DD" (no year) in user's local timezone
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      try {
        const dateObj = new Date(dateStr + 'T12:00:00');
        return dateObj.toLocaleDateString('en-US', { 
          month: 'short',
          day: 'numeric'
        });
      } catch (e) {
        return dateStr;
      }
    }
    
    // Return as-is for any other format
    return dateStr;
  };

  // Helper function to convert price text to dollar signs
  const formatPriceToDollars = (price) => {
    if (!price) return '–';
    const priceLower = price.toLowerCase();
    if (priceLower === 'cheap') return '$';
    if (priceLower === 'bargain') return '$$';
    if (priceLower === 'reasonable' || priceLower === 'moderate') return '$$$';
    if (priceLower === 'expensive') return '$$$$';
    if (priceLower === 'stupid') return '$$$$$';
    return price; // Return as-is if unknown format
  };

  // Helper function to get cuisine flag code or emoji
  const getCuisineIcon = (cuisine) => {
    if (!cuisine) return null;
    const cuisineLower = cuisine.toLowerCase().trim();
    
    // Exact matches for country cuisines first (highest priority)
    const countryFlags = {
      'american': { type: 'flag', code: 'us' },
      'brazilian': { type: 'flag', code: 'br' },
      'british': { type: 'flag', code: 'gb' },
      'chinese': { type: 'flag', code: 'cn' },
      'cuban': { type: 'flag', code: 'cu' },
      'english': { type: 'flag', code: 'gb' },
      'ethiopian': { type: 'flag', code: 'et' },
      'french': { type: 'flag', code: 'fr' },
      'german': { type: 'flag', code: 'de' },
      'greek': { type: 'flag', code: 'gr' },
      'indian': { type: 'flag', code: 'in' },
      'irish': { type: 'flag', code: 'ie' },
      'italian': { type: 'flag', code: 'it' },
      'japanese': { type: 'flag', code: 'jp' },
      'korean': { type: 'flag', code: 'kr' },
      'lebanese': { type: 'flag', code: 'lb' },
      'mexican': { type: 'flag', code: 'mx' },
      'moroccan': { type: 'flag', code: 'ma' },
      'peruvian': { type: 'flag', code: 'pe' },
      'polish': { type: 'flag', code: 'pl' },
      'portuguese': { type: 'flag', code: 'pt' },
      'russian': { type: 'flag', code: 'ru' },
      'spanish': { type: 'flag', code: 'es' },
      'thai': { type: 'flag', code: 'th' },
      'turkish': { type: 'flag', code: 'tr' },
      'vietnamese': { type: 'flag', code: 'vn' }
    };
    
    // Check exact match first
    if (countryFlags[cuisineLower]) {
      return countryFlags[cuisineLower];
    }
    
    // Food type emojis (exact matches preferred)
    const foodEmojis = {
      'bbq': { type: 'emoji', value: '🥩' },
      'barbecue': { type: 'emoji', value: '🥩' },
      'breakfast': { type: 'emoji', value: '🍳' },
      'brunch': { type: 'emoji', value: '🥞' },
      'burger': { type: 'emoji', value: '🍔' },
      'burgers': { type: 'emoji', value: '🍔' },
      'cafe': { type: 'emoji', value: '☕' },
      'coffee': { type: 'emoji', value: '☕' },
      'deli': { type: 'emoji', value: '🥪' },
      'dessert': { type: 'emoji', value: '🍰' },
      'diner': { type: 'emoji', value: '🍽️' },
      'mediterranean': { type: 'emoji', value: '🫒' },
      'pizza': { type: 'emoji', value: '🍕' },
      'ramen': { type: 'emoji', value: '🍜' },
      'seafood': { type: 'emoji', value: '🐟' },
      'steak': { type: 'emoji', value: '🥩' },
      'steakhouse': { type: 'emoji', value: '🥩' },
      'street vendor': { type: 'emoji', value: '🚚' },
      'street food': { type: 'emoji', value: '🚚' },
      'strip club': { type: 'emoji', value: '🎭' },
      'sushi': { type: 'emoji', value: '🍣' },
      'sandwich': { type: 'emoji', value: '🥪' },
      'sandwiches': { type: 'emoji', value: '🥪' },
      'taco': { type: 'emoji', value: '🌮' },
      'tacos': { type: 'emoji', value: '🌮' },
      'vegan': { type: 'emoji', value: '🥗' },
      'vegetarian': { type: 'emoji', value: '🥗' },
      'wings': { type: 'emoji', value: '🍗' },
      'bakery': { type: 'emoji', value: '🥖' },
      'bar': { type: 'emoji', value: '🍺' },
      'pub': { type: 'emoji', value: '🍺' },
      'grill': { type: 'emoji', value: '🍖' },
      'fusion': { type: 'emoji', value: '🌏' },
      'gastropub': { type: 'emoji', value: '🍻' }
    };
    
    // Check exact match for food types
    if (foodEmojis[cuisineLower]) {
      return foodEmojis[cuisineLower];
    }
    
    // Fallback: check if cuisine contains these keywords (for compound names like "Italian Pizza")
    // Check countries first
    for (const [country, icon] of Object.entries(countryFlags)) {
      if (cuisineLower.includes(country)) {
        return icon;
      }
    }
    
    // Then check food types
    for (const [food, icon] of Object.entries(foodEmojis)) {
      if (cuisineLower.includes(food)) {
        return icon;
      }
    }
    
    return null; // No icon for unknown cuisines
  };

  const getOwnershipIcon = (ownership) => {
    if (!ownership) return null;
    const ownershipLower = ownership.toLowerCase().trim();
    
    // Check for exact matches first
    if (ownershipLower === 'local') return '🏡';
    if (ownershipLower === 'franchise') return '🏢';
    if (ownershipLower === 'chain') return '🔗';
    
    // Check for partial matches (for combined types like "Franchise/Local")
    if (ownershipLower.includes('franchise')) return '🏢';
    if (ownershipLower.includes('chain')) return '🔗';
    if (ownershipLower.includes('local')) return '🏡';
    
    return null; // No icon for other types
  };

  const formatDaysBack = (dateString) => {
    if (!dateString) return '–';
    
    try {
      // Parse as local date (not UTC) by appending T00:00:00
      const parts = dateString.split('-');
      const visitDate = new Date(parts[0], parts[1] - 1, parts[2]);
      const today = new Date();
      
      // Reset time to midnight for accurate day calculation
      visitDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      const diffTime = today - visitDate;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return '-1';
      return `-${diffDays}`;
    } catch (error) {
      return '–';
    }
  };

  // Restore session from localStorage on app mount
  useEffect(() => {
    const restoreSession = async () => {
      const token = localStorage.getItem('auth_token');
      const userEmail = localStorage.getItem('user_email');
      const displayName = localStorage.getItem('display_name');
      const isAdmin = localStorage.getItem('is_admin');
      
      if (token && userEmail) {
        console.log('🔄 Restoring session from localStorage...');
        console.log('Email:', userEmail);
        console.log('Is Admin:', isAdmin);
        console.log('Token present:', token ? 'YES' : 'NO');
        
        // Restore auth state FIRST
        setIsAuthenticated(true);
        setAuthToken(token);
        setUserEmail(userEmail);
        setDisplayName(displayName || userEmail);
        setIsAdmin(isAdmin === 'true');
        
        // CRITICAL: Set axios header immediately before any API calls
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        console.log('🔑 Axios Authorization header set during restoration');
        
        // Set the appropriate tab based on user type
        if (isAdmin === 'true') {
          setActiveTab('allDashboard');
        } else {
          setActiveTab('dashboard');
        }
        
        // Small delay to ensure state is set
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Mark initial load as complete
        initialLoadComplete.current = true;
        
        // Fetch data based on user type - ALL IN PARALLEL for fast load
        try {
          if (isAdmin === 'true') {
            console.log('Fetching admin data in parallel...');
            await Promise.all([
              fetchAllUsersRestaurants(),
              fetchAllRecentVisits(),
              fetchPhotoMontage(true),
              fetchOnDeckRestaurants()
            ]);
          } else {
            console.log('Fetching user data in parallel...');
            await Promise.all([
              fetchRestaurants(),
              fetchRecentVisits(),
              loadAllRestaurants(),
              fetchAllUsersRestaurants(),
              fetchPhotoMontage(),
              fetchOnDeckRestaurants()
            ]);
          }
          getUserLocationAndNearby();
          
          console.log('✅ Session restored successfully with data');
        } catch (error) {
          console.error('Error fetching data during session restore:', error);
          // If session restore fails (expired token), silently show login
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_email');
          localStorage.removeItem('display_name');
          localStorage.removeItem('is_admin');
          setIsAuthenticated(false);
          setAuthToken('');
          delete axios.defaults.headers.common['Authorization'];
        }
      } else {
        console.log('No session to restore - token or email missing');
      }
    };
    
    restoreSession();
    
    return () => {
      // Cleanup on unmount if needed
    };
  }, []);

  // Detect mobile preview mode and add class to body
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isMobilePreview = urlParams.get('mobile') === 'true';
    
    console.log('==== MOBILE DETECTION ====');
    console.log('Current URL:', window.location.href);
    console.log('Mobile param:', urlParams.get('mobile'));
    console.log('Is mobile preview:', isMobilePreview);
    
    if (isMobilePreview) {
      document.body.classList.add('mobile-preview-mode');
      document.body.style.border = '5px solid red'; // Debug visual
      console.log('✅ MOBILE PREVIEW MODE ACTIVATED');
      console.log('Body classes:', document.body.className);
    } else {
      console.log('❌ NOT in mobile preview mode');
    }
    
    return () => {
      document.body.classList.remove('mobile-preview-mode');
      document.body.style.border = '';
    };
  }, []);

  // Prevent accidental browser close/exit
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Standard way to trigger the confirmation dialog
      e.preventDefault();
      // Chrome requires returnValue to be set
      e.returnValue = '';
      // Some browsers display this message, others show a default message
      return '';
    };

    // Add event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup: remove event listener when component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []); // Empty dependency array means this runs once on mount

  // Check for existing authentication on load
  useEffect(() => {
    console.log('🔍 APP MOUNT - Checking for saved credentials...');
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const email = localStorage.getItem('user_email') || sessionStorage.getItem('user_email');
    const display_name = localStorage.getItem('display_name') || sessionStorage.getItem('display_name');
    const is_admin = localStorage.getItem('is_admin') || sessionStorage.getItem('is_admin');
    const saved_groups = localStorage.getItem('user_groups') || sessionStorage.getItem('user_groups');
    const saved_owned_group = localStorage.getItem('owned_group') || sessionStorage.getItem('owned_group');
    
    console.log('Token found:', token ? 'YES' : 'NO');
    console.log('Email found:', email || 'NO');
    
    if (token && email) {
      setAuthToken(token);
      setUserEmail(email);
      setDisplayName(display_name || email);
      const adminStatus = is_admin === 'true';
      setIsAdmin(adminStatus);
      setIsAuthenticated(true);
      if (saved_groups) setUserGroups(saved_groups);
      if (saved_owned_group) setOwnedGroup(saved_owned_group);
      
      console.log('✅ AUTO-LOGIN SUCCESS');
      console.log('Email:', email);
      console.log('Display Name:', display_name);
      console.log('is_admin from storage:', is_admin, '(type:', typeof is_admin, ')');
      console.log('adminStatus:', adminStatus);
      console.log('Groups:', saved_groups);
      console.log('Owned Group:', saved_owned_group);
      
      // Footer settings now hardcoded
      
      // Set default tab based on user type on page load — but only if the user
      // doesn't already have a persisted tab in localStorage. This way a hard
      // refresh keeps you on whatever tab you were last using.
      const savedTab = localStorage.getItem('das.activeTab');
      if (savedTab) {
        console.log('Restoring activeTab from localStorage:', savedTab);
        setActiveTab(savedTab);
      } else if (adminStatus) {
        console.log('Setting activeTab to: allDashboard (admin user)');
        setActiveTab('allDashboard');
      } else {
        console.log('Setting activeTab to: dashboard (My Dashboard for regular user)');
        setActiveTab('dashboard');
      }
    } else {
      console.log('❌ AUTO-LOGIN FAILED - No credentials found');
    }
  }, []);

  // Load SMTP settings when admin tab changes to 'settings'
  useEffect(() => {
    if (adminTab === 'settings' && authToken && isAdmin) {
      const loadSmtpSettings = async () => {
        try {
          const response = await fetch(`${API_URL}/api/admin/smtp-settings`, {
            headers: { 'Authorization': `Bearer ${authToken}` },
          });
          if (response.ok) {
            const data = await response.json();
            setSmtpSettings(prev => ({
              ...prev,
              host: data.host || prev.host,
              port: data.port || prev.port,
              user: data.user || prev.user,
              fromEmail: data.fromEmail || prev.fromEmail,
              // Don't overwrite password from server (it's always empty for security)
            }));
          }
        } catch (err) {
          console.error('Error loading SMTP settings:', err);
        }
      };
      
      const loadFooterSettings = async () => {
        try {
          const response = await fetch(`${API_URL}/api/admin/footer-settings`, {
            headers: { 'Authorization': `Bearer ${authToken}` },
          });
          if (response.ok) {
            const data = await response.json();
            setFooterSettings({
              siteName: data.siteName || 'DineASaurus.com',
              siteUrl: data.siteUrl || 'https://dineasaurus.com',
              createdBy: data.createdBy || 'Bill Casso',
              version: data.version || '',
              copyrightYears: data.copyrightYears || '2025',
            });
          }
        } catch (err) {
          console.error('Error loading footer settings:', err);
        }
      };
      
      loadSmtpSettings();
      loadFooterSettings();
    }
  }, [adminTab, authToken, isAdmin]);

  // On initial load with existing auth, fetch data once
  useEffect(() => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const is_admin = localStorage.getItem('is_admin') || sessionStorage.getItem('is_admin');
    const adminStatus = is_admin === 'true';
    
    console.log('=== DATA FETCH EFFECT TRIGGERED ===');
    console.log('isAuthenticated:', isAuthenticated);
    console.log('token present:', !!token);
    console.log('adminStatus:', adminStatus);
    console.log('initialLoadComplete.current:', initialLoadComplete.current);
    
    // Only run on initial page load with existing credentials
    // Not on fresh logins (handleLogin will fetch data)
    if (token && isAuthenticated && !initialLoadComplete.current) {
      console.log('Initial load detected - fetching data from useEffect');
      initialLoadComplete.current = true;
      
      // Delay slightly to ensure axios header is set
      setTimeout(() => {
        if (adminStatus) {
          // Admin user - fetch "All" data for All Dashboard
          console.log('Fetching ADMIN data (all users)...');
          fetchAllUsersRestaurants();
          fetchAllRecentVisits();
          fetchPhotoMontage(true); // Fetch all users' photos
          getUserLocationAndNearby();
        } else {
          // Regular user - fetch "My" data
          console.log('Fetching REGULAR USER data (personal)...');
          fetchRestaurants();
          fetchRecentVisits();
          loadAllRestaurants();
          fetchAllUsersRestaurants();
          getUserLocationAndNearby();
          fetchPhotoMontage(); // Fetch user's photos
        }
      }, 100);
    } else {
      console.log('Skipping useEffect data fetch - not initial load or no auth');
    }
  }, [isAuthenticated]); // Run when authentication state changes

  // Load footer settings for all authenticated users (using public endpoint)
  // Also polls the version and shows an "update available" banner when a new
  // deploy is detected.
  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    const checkVersion = async ({ initial = false } = {}) => {
      try {
        // Cache-bust so proxies/CDNs don't serve a stale version
        const response = await fetch(`${API_URL}/api/public/footer-settings?ts=${Date.now()}`, {
          cache: 'no-store',
        });
        if (!response.ok || cancelled) return;
        const data = await response.json();
        const nextSettings = {
          siteName: data.siteName || 'DineASaurus.com',
          siteUrl: data.siteUrl || 'https://dineasaurus.com',
          createdBy: data.createdBy || 'Bill Casso',
          version: data.version || '',
          copyrightYears: data.copyrightYears || '2025',
        };
        setFooterSettings(nextSettings);

        // Lock the "loaded" version on first successful fetch. Anything newer
        // after that means a new deploy went out and we should nudge the user.
        if (initial || !loadedVersionRef.current) {
          loadedVersionRef.current = nextSettings.version;
        } else if (nextSettings.version && nextSettings.version !== loadedVersionRef.current) {
          setUpdateAvailable(true);
        }
      } catch (err) {
        if (initial) {
          console.log('Could not load footer settings, using defaults');
        }
      }
    };

    // Initial fetch
    checkVersion({ initial: true });

    // Poll every 5 minutes
    const intervalId = setInterval(() => checkVersion(), 5 * 60 * 1000);

    // Re-check whenever the tab regains focus (covers phones/tablets waking up)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
  }, [isAuthenticated]);

  // Handle restaurant parameter from email links
  const urlRestaurantHandled = useRef(false);
  
  // Check for restaurant parameter on initial load and store it
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const restaurantId = urlParams.get('restaurant');
    if (restaurantId) {
      console.log('Storing pending restaurant ID:', restaurantId);
      sessionStorage.setItem('pendingRestaurantId', restaurantId);
      // Clear the URL parameter immediately
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // Run once on mount
  
  // Open restaurant when authenticated and data is loaded
  useEffect(() => {
    if (isAuthenticated && restaurants.length > 0 && !urlRestaurantHandled.current) {
      const pendingRestaurantId = sessionStorage.getItem('pendingRestaurantId');
      
      if (pendingRestaurantId) {
        console.log('Processing pending restaurant ID:', pendingRestaurantId);
        // Find the restaurant in the list
        let restaurant = restaurants.find(r => r.id === pendingRestaurantId);
        
        if (!restaurant && allUsersRestaurants.length > 0) {
          console.log('Restaurant not found in user list, trying all users restaurants');
          restaurant = allUsersRestaurants.find(r => r.id === pendingRestaurantId);
        }
        
        if (restaurant) {
          console.log('Found restaurant:', restaurant.name);
          urlRestaurantHandled.current = true;
          sessionStorage.removeItem('pendingRestaurantId');
          // Open the restaurant details dialog
          setTimeout(() => {
            setSelectedRestaurantDetails(restaurant);
            setIsRestaurantDetailsOpen(true);
          }, 500);
        } else {
          console.log('Restaurant not found with ID:', pendingRestaurantId);
        }
      }
    }
  }, [isAuthenticated, restaurants, allUsersRestaurants]);

  const handleLogin = async (email, token, display_name, is_admin, groups = '', owned_group = '') => {
    console.log('');
    console.log('🔥🔥🔥 ========================================');
    console.log('🔥🔥🔥 handleLogin CALLED - START OF FUNCTION');
    console.log('🔥🔥🔥 ========================================');
    console.log('Email:', email);
    console.log('Token:', token ? 'Present' : 'Missing');
    console.log('Display Name:', display_name);
    console.log('Is Admin:', is_admin, '(type:', typeof is_admin, ')');
    console.log('Boolean(is_admin):', Boolean(is_admin));
    console.log('Groups:', groups);
    console.log('Owned Group:', owned_group);
    
    // Clear any existing data first
    setRestaurants([]);
    setFilteredRestaurants([]);
    setRecentVisits([]);
    setAllUsersRestaurants([]);
    
    console.log('handleLogin called with email:', email);
    
    // Mark that we're doing a fresh login so useEffect doesn't also fetch data
    console.log('Setting initialLoadComplete.current = true to prevent useEffect fetch');
    initialLoadComplete.current = true;
    
    setUserEmail(email);
    setDisplayName(display_name);
    setIsAdmin(Boolean(is_admin)); // Ensure boolean
    setAuthToken(token);
    setUserGroups(groups);
    setOwnedGroup(owned_group || '');
    
    // Set axios header immediately before any API calls
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    sessionExpiredShown = false; // Reset for the new session
    
    // Store auth data in localStorage for session persistence
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user_email', email);
    localStorage.setItem('display_name', display_name || email);
    localStorage.setItem('is_admin', String(Boolean(is_admin)));
    localStorage.setItem('user_groups', groups);
    localStorage.setItem('owned_group', owned_group || '');
    console.log('Auth data stored in localStorage');
    
    console.log('State set - Is Admin:', Boolean(is_admin));
    
    // If admin user, set to All Dashboard (don't show Admin Panel by default)
    if (Boolean(is_admin)) {
      console.log('Admin user detected - setting to All Dashboard');
      setActiveTab('allDashboard');
    } else {
      console.log('Regular user - setting to dashboard (My Dashboard)');
      setActiveTab('dashboard');
    }
    
    setIsAuthenticated(true);
    console.log('isAuthenticated set to true');
    
    // Manually fetch data immediately after setting auth header
    // Run ALL fetches in PARALLEL for faster login
    try {
      console.log('=== STARTING PARALLEL DATA FETCH IN handleLogin ===');
      
      if (Boolean(is_admin)) {
        // Admin user - fetch "All" data for All Dashboard in PARALLEL
        console.log('Admin user - fetching all data in parallel...');
        await Promise.all([
          fetchAllUsersRestaurants(),
          fetchAllRecentVisits(),
          fetchPhotoMontage(true),
          fetchOnDeckRestaurants()
        ]);
        getUserLocationAndNearby();
        console.log('All admin data fetch completed');
      } else {
        // Regular user - fetch "My" data for My Dashboard in PARALLEL
        console.log('Regular user - fetching user data in parallel...');
        await Promise.all([
          fetchRestaurants(),
          fetchRecentVisits(),
          loadAllRestaurants(),
          fetchAllUsersRestaurants(),
          fetchPhotoMontage(),
          fetchOnDeckRestaurants()
        ]);
        getUserLocationAndNearby();
        console.log('All user data fetch completed');
      }
      console.log('=== PARALLEL DATA FETCH COMPLETED ===');
    } catch (error) {
      console.error('Error fetching data after login:', error);
    }
  };


  const handleViewAsUser = async (user) => {
    // Store original admin context
    setOriginalAdminContext({
      email: userEmail,
      displayName: displayName,
      token: authToken,
      isAdmin: isAdmin
    });

    // Clear current data
    setRestaurants([]);
    setAllRestaurantsList([]);
    setAllRestaurantsLoaded(false);
    setRecentVisits([]);
    setAllUsersRestaurants([]);

    // Update context to the selected user
    setUserEmail(user.email);
    setDisplayName(user.display_name);
    setIsAdmin(false);
    setViewingAsUser(user);
    setShowAdminPanel(false);
    setActiveTab('dashboard');

    // Fetch data for the selected user - pass user directly to avoid state timing issues
    try {
      await fetchRestaurants(user);
      await fetchRecentVisits(user);
      await fetchAllUsersRestaurants(); // Load all restaurants for duplicate checking
      getUserLocationAndNearby();
    } catch (error) {
      console.error('Error fetching data for user:', error);
    }
  };

  const handleReturnToAdmin = async () => {
    if (originalAdminContext) {
      // Restore admin context
      setUserEmail(originalAdminContext.email);
      setDisplayName(originalAdminContext.displayName);
      setAuthToken(originalAdminContext.token);
      setIsAdmin(originalAdminContext.isAdmin);
      setViewingAsUser(null);
      setOriginalAdminContext(null);
      setShowAdminPanel(true);

      // Fetch admin data
      try {
        await fetchRestaurants();
        await fetchRecentVisits();
      } catch (error) {
        console.error('Error restoring admin context:', error);
      }
    }
  };

  const handleLogout = async () => {
    console.log('');
    console.log('🚪🚪🚪 ========================================');
    console.log('🚪🚪🚪 LOGOUT INITIATED');
    console.log('🚪🚪🚪 ========================================');
    
    // Set flag to prevent session expired alert during logout
    window.isLoggingOut = true;
    
    // Always clear local state first - don't wait for API
    console.log('Clearing localStorage and sessionStorage...');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_email');
    localStorage.removeItem('display_name');
    localStorage.removeItem('is_admin');
    // Clear persisted nav state so the next user logs in to the default view
    localStorage.removeItem('das.activeTab');
    localStorage.removeItem('das.adminTab');
    localStorage.removeItem('das.showAdminPanel');
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('user_email');
    sessionStorage.removeItem('display_name');
    sessionStorage.removeItem('is_admin');
    delete axios.defaults.headers.common['Authorization'];
    
    // Try to notify server (don't block on this)
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${authToken}` },
        timeout: 3000 // 3 second timeout
      });
    } catch (error) {
      console.error('Logout API error (ignored):', error.message);
    }
    
    console.log('Resetting application state...');
    setIsAuthenticated(false);
    setShowAdminPanel(false);
    setUserEmail('');
    setDisplayName('');
    setIsAdmin(false);
    setAuthToken('');
    setRestaurants([]);
    setFilteredRestaurants([]);
    setRecentVisits([]);
    setLatestVisits([]);
    setPhotoMontage([]);
    setNearbyRestaurants([]);
    setAllRestaurantsList([]);
    setVisitHistory([]);
    setRestaurantPhotos([]);
    setSearchQuery('');
    
    // Reset the initial load flag so data can be fetched on next login
    console.log('Resetting initialLoadComplete ref to false');
    initialLoadComplete.current = false;
    
    // Reset the logging out flag
    window.isLoggingOut = false;
    
    console.log('🚪🚪🚪 LOGOUT COMPLETED');
    console.log('');
  };

  const handleUpdateDisplayName = async () => {
    if (!settingsForm.newDisplayName.trim()) {
      setSettingsError('Display name cannot be empty');
      return;
    }

    try {
      const response = await axios.put(
        `${API_URL}/api/auth/update-profile`,
        { new_display_name: settingsForm.newDisplayName },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      setDisplayName(response.data.display_name);
      localStorage.setItem('display_name', response.data.display_name);
      sessionStorage.setItem('display_name', response.data.display_name);
      
      setSettingsSuccess('Display name updated successfully!');
      setSettingsError('');
      setSettingsForm({ ...settingsForm, newDisplayName: '' });
      
      setTimeout(() => setSettingsSuccess(''), 3000);
    } catch (error) {
      setSettingsError(error.response?.data?.detail || 'Failed to update display name');
    }
  };

  const handleUpdateEmail = async () => {
    if (!settingsForm.newEmail.trim()) {
      setSettingsError('Email cannot be empty');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(settingsForm.newEmail)) {
      setSettingsError('Please enter a valid email address');
      return;
    }

    try {
      const response = await axios.put(
        `${API_URL}/api/auth/update-profile`,
        { new_email: settingsForm.newEmail },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      setUserEmail(response.data.email);
      localStorage.setItem('user_email', response.data.email);
      sessionStorage.setItem('user_email', response.data.email);
      
      setSettingsSuccess('Email updated successfully!');
      setSettingsError('');
      setSettingsForm({ ...settingsForm, newEmail: '' });
      
      setTimeout(() => setSettingsSuccess(''), 3000);
    } catch (error) {
      setSettingsError(error.response?.data?.detail || 'Failed to update email');
    }
  };

  const handleChangePIN = async () => {
    if (!settingsForm.oldPin || !settingsForm.newPin || !settingsForm.confirmPin) {
      setSettingsError('All PIN fields are required');
      return;
    }

    if (settingsForm.newPin.length < 4 || settingsForm.newPin.length > 12) {
      setSettingsError('New Password or PIN must be 4-12 characters');
      return;
    }

    if (settingsForm.newPin !== settingsForm.confirmPin) {
      setSettingsError('New Password or PIN and confirmation do not match');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/auth/change-pin`,
        {
          old_pin: settingsForm.oldPin,
          new_pin: settingsForm.newPin
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      setSettingsSuccess('PIN changed successfully!');
      setSettingsError('');
      setSettingsForm({
        ...settingsForm,
        oldPin: '',
        newPin: '',
        confirmPin: ''
      });
      
      // Clear remembered email and biometric since PIN changed
      localStorage.removeItem('remembered_email');
      localStorage.removeItem(`biometric_${userEmail}`);
      alert('PIN changed successfully! Biometric authentication has been disabled. You can re-enable it on your next login.');
      
      setTimeout(() => setSettingsSuccess(''), 3000);
    } catch (error) {
      setSettingsError(error.response?.data?.detail || 'Failed to change PIN');
    }
  };

  // Load backup settings from backend
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  const loadSchedulerStatus = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/backup-scheduler-status`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setSchedulerStatus(r.data);
    } catch {
      setSchedulerStatus({ error: 'Could not fetch scheduler status' });
    }
  };

  const loadBackupSettings = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/backup-settings`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const settings = response.data;
      if (settings) {
        setBackupEmail(settings.email || 'wasone@airmail.net');
        setBackupSchedule(settings.schedule || 'weekly');
        setBackupEnabled(settings.enabled || false);
        setBackupTime(settings.time || '02:00');
        setBackupTz(settings.tz || getBrowserTimezone());
        setBackupDayOfWeek(typeof settings.day_of_week === 'number' ? settings.day_of_week : 6);
        setBackupDayOfMonth(typeof settings.day_of_month === 'number' ? settings.day_of_month : 1);
        setLastBackupAt(settings.last_backup_at || null);
        setLastBackupStatus(settings.last_backup_status || null);
        setBackupSettingsSaved(true);
      }
    } catch (error) {
      console.log('No backup settings found, using defaults');
    }
  };

  // Save backup settings to backend
  const saveBackupSettings = async () => {
    if (!backupEmail || !backupEmail.includes('@')) {
      setSettingsError('Please enter a valid email address');
      return;
    }
    
    try {
      // Send wall-clock day+time along with the timezone they're expressed in.
      await axios.post(
        `${API_URL}/api/admin/backup-settings`,
        { 
          email: backupEmail, 
          schedule: backupSchedule, 
          enabled: backupEnabled,
          time: backupTime,
          day_of_week: backupDayOfWeek,
          day_of_month: backupDayOfMonth,
          tz: backupTz
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      // Refresh from server so UI reflects what was actually saved
      loadBackupSettings();
      setBackupSettingsSaved(true);
      setSettingsSuccess('✅ Backup settings saved!');
      setSettingsError('');
      setTimeout(() => setSettingsSuccess(''), 3000);
    } catch (error) {
      setSettingsError(`Failed to save settings: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleUserBackup = async () => {
    if (!backupEmail || !backupEmail.includes('@')) {
      setSettingsError('Please enter a valid email address');
      return;
    }
    
    try {
      setSettingsSuccess('⏳ Creating and sending backup...');
      const response = await axios.post(
        `${API_URL}/api/admin/backup-database`,
        { email: backupEmail },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const result = response.data;
      setSettingsSuccess(`✅ Backup sent to ${result.email_sent_to}`);
      setSettingsError('');
      setTimeout(() => setSettingsSuccess(''), 5000);
      // Refresh last_backup_at from server so the panel shows it
      loadBackupSettings();
    } catch (error) {
      setSettingsError(`Backup failed: ${error.response?.data?.detail || error.message}`);
      setSettingsSuccess('');
    }
  };

  const handleRunBackupNow = async () => {
    if (!window.confirm('Trigger the scheduled backup now? An email will be sent to the configured backup address.')) return;
    try {
      setSettingsSuccess('⏳ Running scheduled backup...');
      const response = await axios.post(
        `${API_URL}/api/admin/run-backup-now`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const r = response.data;
      setSettingsSuccess(`✅ Backup triggered. Email queued to ${r.email_sent_to} (${r.total_records} records, ${r.size_kb} KB).`);
      setSettingsError('');
      setTimeout(() => setSettingsSuccess(''), 6000);
      loadBackupSettings();
    } catch (error) {
      setSettingsError(`Run-now backup failed: ${error.response?.data?.detail || error.message}`);
      setSettingsSuccess('');
    }
  };

  const handleDownloadBackup = async () => {
    try {
      setSettingsSuccess('⏳ Creating backup...');
      
      const response = await axios.post(
        `${API_URL}/api/admin/backup-database`,
        { email: null, download_only: true },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const result = response.data;
      const backupData = result.backup_data;
      
      // Create a blob and download
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dineasaurus-backup-${getLocalDateString()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSettingsSuccess(`✅ Backup downloaded! (${result.total_records} records)`);
      setSettingsError('');
      setTimeout(() => setSettingsSuccess(''), 5000);
      // Refresh last_backup_at from server so the panel shows it
      loadBackupSettings();
    } catch (error) {
      setSettingsError(`Backup failed: ${error.response?.data?.detail || error.message}`);
      setSettingsSuccess('');
    }
  };

  // Load backup settings when settings dialog opens or admin Backup tab opens
  useEffect(() => {
    if (showSettingsDialog && authToken) {
      loadBackupSettings();
    }
  }, [showSettingsDialog, authToken]);

  useEffect(() => {
    if (showAdminPanel && adminTab === 'backup' && authToken) {
      loadBackupSettings();
      loadSchedulerStatus();
      const id = setInterval(loadSchedulerStatus, 30000);
      return () => clearInterval(id);
    }
  }, [showAdminPanel, adminTab, authToken]);

  // Fetch nearby restaurants when location becomes available
  useEffect(() => {
    if (userLocation && authToken) {
      console.log('📍 Location available, fetching nearby restaurants...');
      fetchAllNearbyRestaurants();
    }
  }, [userLocation, authToken]);

  const handleUserRestore = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Strong warning before restore
    if (!window.confirm(`⚠️⚠️⚠️ CRITICAL WARNING ⚠️⚠️⚠️\n\nThis will PERMANENTLY DELETE all current data and REPLACE it with the backup file.\n\nFile: ${file.name}\nSize: ${(file.size / 1024).toFixed(2)} KB\n\nTHIS CANNOT BE UNDONE!\n\nAre you ABSOLUTELY SURE?`)) {
      event.target.value = '';
      return;
    }

    try {
      console.log('📁 Reading backup file:', file.name);
      setSettingsError(''); // Clear previous errors
      setSettingsSuccess('⏳ Reading backup file...');
      
      // Read file
      const fileText = await file.text();
      console.log('📄 File size:', fileText.length, 'characters');
      
      const backupData = JSON.parse(fileText);
      console.log('✅ JSON parsed successfully');
      console.log('📦 Backup date:', backupData.backup_date);
      console.log('📊 Collections:', Object.keys(backupData.collections || {}));

      if (!backupData.collections) {
        throw new Error('Invalid backup file - missing collections');
      }

      setSettingsSuccess('⏳ Uploading to server...');
      console.log('🚀 Sending restore request to server...');
      
      const response = await axios.post(
        `${API_URL}/api/admin/restore-database`,
        backupData,
        { 
          headers: { Authorization: `Bearer ${authToken}` },
          timeout: 60000 // 60 second timeout
        }
      );

      console.log('✅ Server response:', response.data);
      const result = response.data;
      const collectionsInfo = Object.entries(result.restored_collections)
        .map(([name, count]) => `${name}: ${count}`)
        .join(', ');
      
      alert(`✅ Database restored!\n\nBackup Date: ${result.backup_date}\nTotal: ${result.total_records} records\n\nRestored: ${collectionsInfo}\n\n🔄 Refreshing page...`);
      
      // Logout and refresh
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/';
    } catch (error) {
      console.error('❌ Restore error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error occurred';
      console.error('Error details:', errorMsg);
      
      // Check if it's an authentication error
      if (errorMsg.includes('token') || errorMsg.includes('401') || errorMsg.includes('Authentication')) {
        setSettingsError(`Authentication expired. Please log out and log back in, then try again.`);
        alert(`❌ Authentication Error!\n\nYour login session has expired.\n\nPlease:\n1. Click OK\n2. Log out (click your email, then Logout)\n3. Log back in\n4. Try the restore again`);
      } else {
        setSettingsError(`Restore failed: ${errorMsg}`);
        alert(`❌ Restore Failed!\n\n${errorMsg}\n\nCheck the browser console (F12) for more details.`);
      }
    } finally {
      event.target.value = '';
      setSettingsSuccess(''); // Clear the uploading message
    }
  };

  // Show login page if not authenticated
  // Moved to end of component to avoid hooks ordering issue
  
  useEffect(() => {
    fetchRestaurants();
    fetchRecentVisits();
    getUserLocationAndNearby();
    // Don't fetch on-deck here - it's fetched during session restoration
  }, []);

  const fetchRecentVisits = async (impersonateUser = null) => {
    console.log('fetchRecentVisits called');
    try {
      // Use the latest_visits endpoint which sorts by insertion order (newest first)
      const userToImpersonate = impersonateUser || viewingAsUser;
      const params = userToImpersonate ? { limit: 10, impersonate_user: userToImpersonate.email } : { limit: 10 };
      const response = await axios.get(`${API_URL}/api/latest_visits`, { params });
      console.log('fetchRecentVisits response:', response.data.slice(0, 3).map(v => ({
        restaurant: v.restaurantName,
        date: v.visitDate,
        rating: v.rating,
        favorites: v.favoriteItems
      })));
      setRecentVisits(response.data.slice(0, 10));
      console.log('recentVisits state updated');
    } catch (error) {
      console.error('Error fetching recent visits:', error);
    }
  };

  const getUserLocationAndNearby = () => {
    // Check if geolocation is available and if we're in a secure context
    if (!navigator.geolocation) {
      console.log('Geolocation is not supported by this browser');
      setNearbyStatus('error');
      return;
    }
    
    // Check if we're on a secure context (HTTPS or localhost)
    if (window.location.protocol !== 'https:' && !window.location.hostname.includes('localhost') && !window.location.hostname.includes('127.0.0.1')) {
      console.warn('Geolocation requires HTTPS. Current protocol:', window.location.protocol);
      setNearbyStatus('no-https');
      return;
    }
    
    console.log('🗺️ Requesting location permission...');
    setNearbyStatus('loading');
    
    // Always request fresh location to ensure browser prompts for permission
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const location = { latitude, longitude };
        
        console.log('🗺️ Location obtained:', latitude, longitude);
        setUserLocation(location);
        
        // Cache location for future use
        sessionStorage.setItem('cached_location', JSON.stringify(location));
        sessionStorage.setItem('cache_timestamp', Date.now().toString());
        
        console.log('🗺️ Fetching nearby restaurants...');
        fetchNearestRestaurants(latitude, longitude);
      },
      (error) => {
        console.error('❌ Location error:', error.code, error.message);
        if (error.code === error.PERMISSION_DENIED) {
          console.log('User denied location permission - check browser settings');
          setNearbyStatus('denied');
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          console.log('Location information unavailable');
          setNearbyStatus('error');
        } else if (error.code === error.TIMEOUT) {
          console.log('Location request timed out');
          setNearbyStatus('error');
        }
      },
      {
        timeout: 15000, // 15 second timeout
        enableHighAccuracy: false,
        maximumAge: 0 // Force fresh location request
      }
    );
  };
  
  const fetchNearestRestaurants = async (latitude, longitude) => {
    try {
      const params = viewingAsUser ? { impersonate_user: viewingAsUser.email } : {};
      const response = await axios.post(
        `${API_URL}/api/restaurants/nearest`,
        { latitude, longitude },
        { params }
      );
      
      // Sort by distance (closest first) and take top 10 for dashboard display
      const sorted = response.data.sort((a, b) => {
        return (a.distance || 999) - (b.distance || 999);
      });
      
      // Show only top 10 closest for dashboard display
      setNearbyRestaurants(sorted.slice(0, 10));
      setNearbyStatus('loaded');
      console.log('🗺️ Nearby restaurants loaded:', sorted.length);
    } catch (error) {
      console.error('Error finding nearby restaurants:', error);
      setNearbyStatus('error');
    }
  };

  const fetchPhotoMontage = async (fetchAllUsers = false) => {
    try {
      let allRestaurants;
      
      if (fetchAllUsers) {
        // Fetch all users' restaurants for All Dashboard
        const response = await axios.get(`${API_URL}/api/all-restaurants`);
        allRestaurants = response.data;
      } else {
        // Fetch current user's restaurants (or impersonated user)
        const params = viewingAsUser ? { impersonate_user: viewingAsUser.email } : {};
        const response = await axios.get(`${API_URL}/api/restaurants`, { params });
        allRestaurants = response.data;
      }
      
      // Collect all photos from all restaurants with restaurant info
      const allPhotos = [];
      allRestaurants.forEach(restaurant => {
        if (restaurant.photos && restaurant.photos.length > 0) {
          restaurant.photos.forEach(photo => {
            const thumbnailUrl = typeof photo === 'string' ? photo : photo.thumbnail;
            const originalUrl = typeof photo === 'string' ? photo : photo.original;
            const photoId = typeof photo === 'object' ? photo.id : null;
            const rotation = typeof photo === 'object' ? photo.rotation || 0 : 0;
            
            allPhotos.push({
              thumbnailUrl,
              originalUrl,
              photoId,
              rotation,
              restaurantId: restaurant.id,
              restaurantName: restaurant.name,
              restaurantLocation: restaurant.location,
              restaurant: restaurant
            });
          });
        }
      });
      
      // Shuffle array using Fisher-Yates algorithm
      for (let i = allPhotos.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [allPhotos[i], allPhotos[j]] = [allPhotos[j], allPhotos[i]];
      }
      
      // Take up to 20 random photos
      setPhotoMontage(allPhotos.slice(0, 20));
    } catch (error) {
      console.error('Error fetching photo montage:', error);
    }
  };

  // Fetch all recent visits from all users (for All Dashboard)
  const fetchAllRecentVisits = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/latest_visits`, { 
        params: { limit: 10, all_users: true } 
      });
      console.log('All Recent Visits API Response:', response.data);
      console.log('Number of visits returned:', response.data.length);
      setAllRecentVisits(response.data.slice(0, 10));
    } catch (error) {
      console.error('Error fetching all recent visits:', error);
    }
  };

  // Fetch all nearest restaurants from all users (for All Dashboard)
  const fetchAllNearbyRestaurants = async () => {
    if (!userLocation) {
      console.log('User location not available for All Dashboard');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/api/restaurants/nearest`,
        { latitude: userLocation.latitude, longitude: userLocation.longitude },
        { params: { all_users: true } }
      );
      
      // Sort by distance and take top 10 for dashboard display
      const sorted = response.data.sort((a, b) => {
        const distA = parseFloat(a.distance) || 999;
        const distB = parseFloat(b.distance) || 999;
        return distA - distB;
      });
      
      // Debug: Check what rating fields and distances are in the data
      console.log('🔍 All Nearby Restaurants - First 5:', sorted.slice(0, 5).map(r => ({
        name: r.name,
        distance: r.distance,
        lat: r.latitude,
        lng: r.longitude
      })));
      
      setAllNearbyRestaurants(sorted.slice(0, 10));
      console.log('✅ Set allNearbyRestaurants with', sorted.slice(0, 10).length, 'restaurants');
    } catch (error) {
      console.error('Error fetching all nearby restaurants:', error);
    }
  };


  useEffect(() => {
    // Extract unique values from restaurants for dynamic filters
    if (restaurants.length > 0) {
      // Unique locations - extract City, State from full addresses
      const locations = restaurants
        .map(r => r.location)
        .filter(l => l)
        .map(location => {
          // Parse location to extract City, State from full addresses
          const parts = location.split(',').map(p => p.trim());
          
          // Look for state (2 letters) or "STATE ZIPCODE" pattern
          let stateIndex = -1;
          let state = '';
          
          // Find the state part
          for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            // Check if it's just 2 letters (state abbreviation)
            if (/^[A-Z]{2}$/i.test(part)) {
              stateIndex = i;
              state = part;
              break;
            }
            // Check if it starts with 2 letters followed by space and numbers (STATE ZIPCODE)
            const match = part.match(/^([A-Z]{2})\s+\d+$/i);
            if (match) {
              stateIndex = i;
              state = match[1];
              break;
            }
          }
          
          // If we found a state, get the city (part before state)
          if (stateIndex > 0) {
            const city = parts[stateIndex - 1];
            return `${city}, ${state}`;
          } else if (stateIndex === 0 && parts.length === 1) {
            // Just a state by itself
            return parts[0];
          }
          
          // Fallback: return original if we can't parse it
          return location;
        })
        .filter(l => {
          // Filter out invalid entries
          const trimmed = l.trim();
          
          // Exclude pure numbers
          if (/^\d+$/.test(trimmed)) return false;
          
          // Exclude "ST ZIPCODE" format
          if (/^[A-Z]{2}\s+\d+$/i.test(trimmed)) return false;
          
          // Validate City, State format
          const parts = trimmed.split(',').map(p => p.trim());
          if (parts.length === 2) {
            const [city, state] = parts;
            // State should be 2 letters, city should not be pure numbers
            if (!/^[A-Z]{2}$/i.test(state) || /^\d+$/.test(city)) {
              return false;
            }
          }
          
          return true;
        });
      
      // Remove duplicates and sort
      const uniqueLocs = [...new Set(locations)].sort();
      setUniqueLocations(uniqueLocs);

      // Group locations by state
      const grouped = {};
      uniqueLocs.forEach(location => {
        // Parse location format: "City, State"
        const parts = location.split(',').map(p => p.trim());
        let state, city;
        
        if (parts.length === 2) {
          city = parts[0];
          state = parts[1];
        } else if (parts.length === 1) {
          // Handle edge case where location is just one word
          state = parts[0];
          city = parts[0];
        } else {
          // If more than 2 parts, take last as state, second to last as city
          state = parts[parts.length - 1];
          city = parts[parts.length - 2];
        }
        
        if (!grouped[state]) {
          grouped[state] = [];
        }
        grouped[state].push({ fullLocation: location, city });
      });
      
      // Sort cities within each state
      Object.keys(grouped).forEach(state => {
        grouped[state].sort((a, b) => a.city.localeCompare(b.city));
      });
      
      setLocationsByState(grouped);

      // Unique prices
      const prices = [...new Set(restaurants.map(r => r.price).filter(p => p))].sort((a, b) => {
        const priceOrder = { 'Cheap': 1, 'Reasonable': 2, 'Expensive': 3 };
        return (priceOrder[a] || 4) - (priceOrder[b] || 4);
      });
      setUniquePrices(prices);

      // Unique ownership types
      const ownership = [...new Set(restaurants.map(r => r.ownership).filter(o => o))].sort();
      setUniqueOwnership(ownership);

      // Unique cuisines/ethnicities
      const cuisines = [...new Set(restaurants.map(r => r.ethnicity).filter(e => e))].sort();
      setUniqueCuisines(cuisines);
      
      // Fetch photo montage when restaurants are loaded
      fetchPhotoMontage();
    }
  }, [restaurants]);

  // Extract unique values from allUsersRestaurants for dynamic filters when on All Dashboard
  useEffect(() => {
    if (activeTab === 'allDashboard' && allUsersRestaurants.length > 0) {
      // Unique locations - extract City, State from full addresses
      const locations = allUsersRestaurants
        .map(r => r.location)
        .filter(l => l)
        .map(location => {
          // Parse location to extract City, State from full addresses
          const parts = location.split(',').map(p => p.trim());
          
          // Look for state (2 letters) or "STATE ZIPCODE" pattern
          let stateIndex = -1;
          let state = '';
          
          // Find the state part
          for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            if (/^[A-Z]{2}$/i.test(part)) {
              stateIndex = i;
              state = part;
              break;
            }
            const match = part.match(/^([A-Z]{2})\s+\d+$/i);
            if (match) {
              stateIndex = i;
              state = match[1];
              break;
            }
          }
          
          if (stateIndex > 0) {
            const city = parts[stateIndex - 1];
            return `${city}, ${state}`;
          } else if (stateIndex === 0 && parts.length === 1) {
            return parts[0];
          }
          
          return location;
        })
        .filter(l => {
          const trimmed = l.trim();
          if (/^\d+$/.test(trimmed)) return false;
          if (/^[A-Z]{2}\s+\d+$/i.test(trimmed)) return false;
          
          const parts = trimmed.split(',').map(p => p.trim());
          if (parts.length === 2) {
            const [city, state] = parts;
            if (!/^[A-Z]{2}$/i.test(state) || /^\d+$/.test(city)) {
              return false;
            }
          }
          
          return true;
        });
      
      const uniqueLocs = [...new Set(locations)].sort();
      setUniqueLocations(uniqueLocs);

      // Group locations by state
      const grouped = {};
      uniqueLocs.forEach(location => {
        const parts = location.split(',').map(p => p.trim());
        let state, city;
        
        if (parts.length === 2) {
          city = parts[0];
          state = parts[1];
        } else if (parts.length === 1) {
          state = parts[0];
          city = parts[0];
        } else {
          state = parts[parts.length - 1];
          city = parts[parts.length - 2];
        }
        
        if (!grouped[state]) {
          grouped[state] = [];
        }
        grouped[state].push({ fullLocation: location, city });
      });
      
      Object.keys(grouped).forEach(state => {
        grouped[state].sort((a, b) => a.city.localeCompare(b.city));
      });
      
      setLocationsByState(grouped);

      // Unique prices
      const prices = [...new Set(allUsersRestaurants.map(r => r.price).filter(p => p))].sort((a, b) => {
        const priceOrder = { 'Cheap': 1, 'Reasonable': 2, 'Expensive': 3 };
        return (priceOrder[a] || 4) - (priceOrder[b] || 4);
      });
      setUniquePrices(prices);

      // Unique ownership types
      const ownership = [...new Set(allUsersRestaurants.map(r => r.ownership).filter(o => o))].sort();
      setUniqueOwnership(ownership);

      // Unique cuisines/ethnicities
      const cuisines = [...new Set(allUsersRestaurants.map(r => r.ethnicity).filter(e => e))].sort();
      setUniqueCuisines(cuisines);
    }
  }, [allUsersRestaurants, activeTab]);

  // Auto-load restaurants when All Restaurants tab is selected OR when filters are active on Dashboard
  useEffect(() => {
    const hasFilters = filters.price.length > 0 || 
                       filters.location.length > 0 || 
                       filters.ownership.length > 0 ||
                       filters.ethnicity.length > 0 ||
                       filters.patio !== null || 
                       filters.dogFriendly !== null || 
                       filters.heineken !== null;
    
    const needsAllRestaurants = activeTab === 'allRestaurants' || 
                                 (activeTab === 'dashboard' && hasFilters);
    
    if (needsAllRestaurants && !allRestaurantsLoaded) {
      loadAllRestaurants();
    }
  }, [activeTab, viewingAsUser, filters]);

  // Auto-search for restaurant website when adding new restaurant
  useEffect(() => {
    // Only search if adding new restaurant and both name and location are filled
    // Don't search if menuLink is already filled
    if (isAddOpen && formData.name && formData.location && !formData.menuLink) {
      // Debounce the search to avoid too many requests
      const timer = setTimeout(() => {
        searchRestaurantWebsite(formData.name, formData.location);
      }, 1000); // Wait 1 second after user stops typing
      
      return () => clearTimeout(timer);
    }
  }, [isAddOpen, formData.name, formData.location, formData.menuLink]);

  // Debounce search query to reduce filtering operations
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Memoize filtered restaurants to avoid expensive recalculations
  useEffect(() => {
    // Don't filter when showing latest visits - showLatestVisits() handles it
    if (showLatestVisitsView) {
      return;
    }
    
    // Use appropriate data source based on active tab
    let sourceData;
    if (activeTab === 'allDashboard') {
      sourceData = allUsersRestaurants; // Admin All Dashboard - shows all restaurants
    } else if (activeTab === 'allUsersRestaurants') {
      // All Restaurants tab - show all shared restaurants (based on group membership)
      sourceData = allUsersRestaurants;
    } else if (activeTab === 'allRestaurants') {
      sourceData = allRestaurantsList; // My Restaurants tab (personal list)
    } else if (activeTab === 'dashboard') {
      sourceData = allRestaurantsList; // My Dashboard - when filtered, show My Restaurants data
    } else {
      sourceData = restaurants; // Fallback
    }
    
    // Early return if no data
    if (!sourceData || sourceData.length === 0) {
      setFilteredRestaurants([]);
      return;
    }
    
    let filtered = sourceData;
    
    // Don't pre-filter when showing nearest - let the full filtering logic handle it
    
    const hasActiveFilters = filters.price.length > 0 || 
                            filters.location.length > 0 || 
                            filters.ownership.length > 0 ||
                            filters.ethnicity.length > 0 ||
                            filters.patio !== null || 
                            filters.dogFriendly !== null || 
                            filters.heineken !== null;

    // Apply search filter - searches across ALL text fields (using debounced query)
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.name.toLowerCase().includes(query) ||
        r.location.toLowerCase().includes(query) ||
        (r.favoriteItems && r.favoriteItems.toLowerCase().includes(query)) ||
        (r.dislikes && r.dislikes.toLowerCase().includes(query)) ||
        (r.ethnicity && r.ethnicity.toLowerCase().includes(query)) ||
        (r.ownership && r.ownership.toLowerCase().includes(query)) ||
        (r.staff && r.staff.toLowerCase().includes(query)) ||
        (r.price && r.price.toLowerCase().includes(query)) ||
        (r.fullAddress && r.fullAddress.toLowerCase().includes(query))
      );
      
      // Add to search history only when debounced query updates
      if (debouncedSearchQuery.trim().length > 0 && debouncedSearchQuery === searchQuery) {
        setSearchHistory(prev => {
          const newHistory = [debouncedSearchQuery, ...prev.filter(s => s !== debouncedSearchQuery)].slice(0, 10);
          localStorage.setItem('searchHistory', JSON.stringify(newHistory));
          return newHistory;
        });
      }
    } else if (!showNearest && !hasActiveFilters && !showLatestVisitsView) {
      // For "My Restaurants" and "All Restaurants" tabs, show all restaurants by default
      // For Dashboard tabs, don't show results unless searching/filtering
      if (activeTab === 'allRestaurants' || activeTab === 'allUsersRestaurants') {
        // Continue to show all restaurants in the list
      } else {
        // Dashboard tabs: Don't show results unless searching, using "Find Nearest", filters are active, or showing latest visits
        setFilteredRestaurants([]);
        return;
      }
    }

    // Apply price filter
    if (filters.price.length > 0) {
      filtered = filtered.filter(r => filters.price.includes(r.price));
    }

    // Apply location filter
    if (filters.location.length > 0) {
      filtered = filtered.filter(r => {
        // Extract City, State from restaurant's location (which may be full address)
        const parts = r.location.split(',').map(p => p.trim());
        
        // Look for state pattern
        let cityState = r.location;
        for (let i = 0; i < parts.length; i++) {
          const part = parts[i];
          // Check if it's just 2 letters (state)
          if (/^[A-Z]{2}$/i.test(part) && i > 0) {
            cityState = `${parts[i - 1]}, ${part}`;
            break;
          }
          // Check if it's "STATE ZIPCODE"
          const match = part.match(/^([A-Z]{2})\s+\d+$/i);
          if (match && i > 0) {
            cityState = `${parts[i - 1]}, ${match[1]}`;
            break;
          }
        }
        
        return filters.location.includes(cityState);
      });
    }

    // Apply patio filter
    if (filters.patio !== null) {
      filtered = filtered.filter(r => r.patio === filters.patio);
    }

    // Apply dog friendly filter
    if (filters.dogFriendly !== null) {
      filtered = filtered.filter(r => r.dogFriendly === filters.dogFriendly);
    }

    // Apply heineken filter
    if (filters.heineken !== null) {
      filtered = filtered.filter(r => r.heineken === filters.heineken);
    }

    // Apply ownership filter
    if (filters.ownership.length > 0) {
      filtered = filtered.filter(r => filters.ownership.includes(r.ownership));
    }

    // Apply ethnicity/cuisine filter
    if (filters.ethnicity.length > 0) {
      filtered = filtered.filter(r => filters.ethnicity.includes(r.ethnicity));
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      // When showing nearest restaurants, always sort by distance first
      if (showNearest && a.distance !== undefined && b.distance !== undefined) {
        return (a.distance || 999) - (b.distance || 999);
      }
      
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'price') {
        const priceOrder = { 'Cheap': 1, 'Reasonable': 2, 'Expensive': 3, '': 4 };
        return (priceOrder[a.price] || 4) - (priceOrder[b.price] || 4);
      } else if (sortBy === 'lastVisit') {
        return (b.lastVisit || '').localeCompare(a.lastVisit || '');
      }
      return 0;
    });

    // When showing nearest, limit to top 20 closest restaurants
    if (showNearest) {
      filtered = filtered.slice(0, 20);
    }

    setFilteredRestaurants(filtered);
  }, [debouncedSearchQuery, searchQuery, restaurants, allUsersRestaurants, allRestaurantsList, filters, sortBy, showNearest, userLocation, activeTab]);

  const fixRestaurantLocation = async (restaurant) => {
    if (!restaurant) return;
    const current = restaurant.fullAddress || restaurant.location || '';
    
    const useGps = window.confirm(
      `📍 Fix Location for "${restaurant.name}"\n\n` +
      `Click OK to use your phone's current GPS location (you must be AT the restaurant).\n\n` +
      `Click Cancel to enter the address manually instead.`
    );
    
    const callBackend = async (body) => {
      try {
        const res = await axios.post(
          `${API_URL}/api/restaurants/${restaurant.id}/recalculate-geo`,
          body,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (res.data.success) {
          const { new_coordinates: n, old_coordinates: o, source } = res.data;
          const oldTxt = o && o.lat != null ? `\nOld: ${o.lat.toFixed(5)}, ${o.lng.toFixed(5)}` : '';
          const savedNote = res.data.address_saved ? `\nAddress saved: ${res.data.address_saved}` : '';
          // Locally patch the open detail panel so the new address/coords show immediately
          setSelectedRestaurantHistory((prev) => prev && prev.id === restaurant.id
            ? {
                ...prev,
                latitude: n.lat,
                longitude: n.lng,
                fullAddress: res.data.address_saved || prev.fullAddress,
                distance: 0,
              }
            : prev
          );
          fetchRestaurants();

          // Use confirm() so the user can either accept the result (OK)
          // or cancel and enter a different address manually.
          const keep = window.confirm(
            `✅ Location updated (source: ${source})${oldTxt}\n` +
            `New: ${n.lat.toFixed(5)}, ${n.lng.toFixed(5)}${savedNote}\n\n` +
            `Matched: ${res.data.matched_address}\n\n` +
            `Click OK to keep this address.\n` +
            `Click Cancel to enter a DIFFERENT address.`
          );
          if (!keep) {
            const corrected = window.prompt(
              'Enter the correct address for this restaurant:',
              res.data.address_saved || current
            );
            if (corrected === null) return; // user cancelled the prompt
            const newAddr = corrected.trim();
            if (!newAddr) return;
            await callBackend({ address: newAddr });
          }
        } else {
          alert(`⚠️ ${res.data.message || 'Could not update location.'}`);
        }
      } catch (err) {
        alert(`❌ Failed: ${err.response?.data?.detail || err.message}`);
      }
    };
    
    if (useGps) {
      if (!navigator.geolocation) {
        alert('Your browser does not support geolocation. Falling back to manual entry.');
      } else {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            async (pos) => {
              await callBackend({
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude
              });
              resolve();
            },
            (err) => {
              alert(`Could not get GPS location: ${err.message}\n\nFalling back to manual entry.`);
              const corrected = window.prompt('Enter the correct address:', current);
              if (corrected !== null) {
                const newAddr = corrected.trim();
                if (newAddr) callBackend({ address: newAddr });
              }
              resolve();
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
          );
        });
        return;
      }
    }
    
    const corrected = window.prompt(
      'Enter the correct address for this restaurant:',
      current
    );
    if (corrected === null) return;
    const newAddr = corrected.trim();
    if (!newAddr) return;
    await callBackend({ address: newAddr });
  };

  const fetchRestaurants = async (impersonateUser = null) => {
    try {
      console.log('fetchRestaurants: Making API call with headers:', axios.defaults.headers.common);
      const userToImpersonate = impersonateUser || viewingAsUser;
      const params = userToImpersonate ? { impersonate_user: userToImpersonate.email } : {};
      if (showArchived) params.include_archived = true;
      const response = await axios.get(`${API_URL}/api/restaurants`, { params });
      console.log('fetchRestaurants: Success! Received', response.data.length, 'restaurants');
      
      // Try to fetch all visits to calculate correct average ratings (with timeout)
      let restaurantsWithCorrectRatings = [...response.data];
      try {
        const visitsParams = { limit: 10000, ...params };
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        
        const visitsResponse = await axios.get(`${API_URL}/api/latest_visits`, { 
          params: visitsParams,
          signal: controller.signal 
        });
        clearTimeout(timeoutId);
        
        const allVisits = visitsResponse.data;
        
        // Calculate average rating for each restaurant from visits
        restaurantsWithCorrectRatings = response.data.map(restaurant => {
          const restaurantVisits = allVisits.filter(v => v.restaurantId === restaurant.id && v.rating && v.rating > 0);
          let calculatedAverageRating = null;
          const oldRating = restaurant.averageRating || restaurant.overallRating;
          if (restaurantVisits.length > 0) {
            const avgRating = restaurantVisits.reduce((sum, v) => sum + v.rating, 0) / restaurantVisits.length;
            calculatedAverageRating = parseFloat(avgRating.toFixed(1));
            if (oldRating !== calculatedAverageRating && oldRating) {
              console.log(`⚠️ ${restaurant.name}: OLD=${oldRating} → NEW=${calculatedAverageRating} (${restaurantVisits.length} visits: [${restaurantVisits.map(v => v.rating).join(',')}])`);
            }
          }
          return { ...restaurant, averageRating: calculatedAverageRating };
        });
        
        console.log('Restaurants with recalculated ratings:', restaurantsWithCorrectRatings.slice(0, 3).map(r => ({
          name: r.name,
          visitCount: r.visitCount,
          averageRating: r.averageRating,
          oldRating: response.data.find(orig => orig.id === r.id)?.averageRating
        })));
      } catch (visitError) {
        if (visitError.name === 'AbortError' || visitError.code === 'ECONNABORTED') {
          console.warn('fetchRestaurants: Visits fetch timed out, using stored ratings');
        } else {
          console.warn('fetchRestaurants: Could not fetch visits for rating calculation:', visitError.message);
        }
      }
      
      // Calculate distances if user location is available
      let restaurantsWithDistance = [...restaurantsWithCorrectRatings];
      if (userLocation) {
        restaurantsWithDistance = restaurantsWithCorrectRatings.map(restaurant => {
          if (restaurant.latitude && restaurant.longitude) {
            const distance = calculateDistance(
              userLocation.latitude,
              userLocation.longitude,
              restaurant.latitude,
              restaurant.longitude
            );
            return { ...restaurant, distance };
          }
          return { ...restaurant, distance: null };
        });
      }
      
      setRestaurants(restaurantsWithDistance); // Force new array reference
      console.log('✅ SET RESTAURANTS - Sample ratings:', JSON.stringify(restaurantsWithDistance.slice(0, 3).map(r => ({ name: r.name, rating: r.averageRating }))));
      // Don't set filtered restaurants here - let the useEffect handle it
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      console.error('Error details:', error.response?.status, error.response?.data);
    }
  };

  // Footer settings removed - now hardcoded

  const fetchAllUsersRestaurants = async () => {
    try {
      // Use the shared restaurants endpoint which filters by group membership
      const response = await axios.get(`${API_URL}/api/restaurants/shared`, { params: showArchived ? { include_archived: true } : {} });
      console.log('Fetched shared restaurants (based on groups):', response.data.length);
      
      // Try to fetch all visits to calculate correct average ratings (with timeout)
      // Use latest_visits with high limit and all_users=true
      let restaurantsWithCorrectRatings = [...response.data];
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        
        const visitsResponse = await axios.get(`${API_URL}/api/latest_visits`, {
          params: { limit: 10000, all_users: true },
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        const allVisits = visitsResponse.data;
        
        // Calculate average rating for each restaurant from visits
        restaurantsWithCorrectRatings = response.data.map(restaurant => {
          const restaurantVisits = allVisits.filter(v => v.restaurantId === restaurant.id && v.rating && v.rating > 0);
          let calculatedAverageRating = null;
          if (restaurantVisits.length > 0) {
            const avgRating = restaurantVisits.reduce((sum, v) => sum + v.rating, 0) / restaurantVisits.length;
            calculatedAverageRating = parseFloat(avgRating.toFixed(1));
          }
          return { ...restaurant, averageRating: calculatedAverageRating };
        });
        console.log('Recalculated ratings for shared restaurants');
      } catch (visitError) {
        if (visitError.name === 'AbortError' || visitError.code === 'ECONNABORTED') {
          console.warn('fetchAllUsersRestaurants: Visits fetch timed out, using stored ratings');
        } else {
          console.warn('fetchAllUsersRestaurants: Could not fetch visits for rating calculation:', visitError.message);
        }
      }
      
      // Calculate distances if user location is available
      let restaurantsWithDistance = [...restaurantsWithCorrectRatings];
      if (userLocation) {
        restaurantsWithDistance = restaurantsWithCorrectRatings.map(restaurant => {
          if (restaurant.latitude && restaurant.longitude) {
            const distance = calculateDistance(
              userLocation.latitude,
              userLocation.longitude,
              restaurant.latitude,
              restaurant.longitude
            );
            return { ...restaurant, distance };
          }
          return { ...restaurant, distance: null };
        });
      }
      
      setAllUsersRestaurants(restaurantsWithDistance);
    } catch (error) {
      console.error('Error fetching shared restaurants:', error);
      // If not authenticated or error, set empty
      setAllUsersRestaurants([]);
    }
  };

  const fetchGoogleRating = async (restaurantId, showAlert = true) => {
    try {
      console.log(`🔍 Fetching Google rating for restaurant ID: ${restaurantId}`);
      setFetchingGoogleRatings(prev => ({ ...prev, [restaurantId]: true }));
      
      const response = await axios.post(`${API_URL}/api/restaurants/${restaurantId}/fetch-google-rating`);
      console.log('Google rating API response:', response.data);
      
      if (response.data.success) {
        // Update the restaurant in both lists
        setRestaurants(prev => 
          prev.map(r => 
            r.id === restaurantId 
              ? { ...r, googleRating: response.data.googleRating }
              : r
          )
        );
        
        setAllUsersRestaurants(prev => 
          prev.map(r => 
            r.id === restaurantId 
              ? { ...r, googleRating: response.data.googleRating }
              : r
          )
        );
        
        console.log(`✅ Fetched Google rating: ${response.data.googleRating}/5 (${response.data.totalRatings} reviews)`);
        if (showAlert) {
          alert(`✅ Google rating updated: ${response.data.googleRating}/5 (${response.data.totalRatings || 0} reviews)`);
        }
        return { success: true, rating: response.data.googleRating };
      } else {
        console.warn(`⚠️ Could not fetch Google rating: ${response.data.message}`);
        if (showAlert) {
          alert(`Could not fetch Google rating:\n${response.data.message}`);
        }
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Error fetching Google rating:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      if (showAlert) {
        alert(`Failed to fetch Google rating:\n${errorMsg}`);
      }
      return { success: false, error: errorMsg };
    } finally {
      setFetchingGoogleRatings(prev => ({ ...prev, [restaurantId]: false }));
    }
  };

  const fetchAllGoogleRatings = async () => {
    try {
      // First, refresh the restaurant list to get latest data
      console.log('📋 Refreshing restaurant list before bulk fetch...');
      await fetchRestaurants();
      
      // Get fresh restaurant data from state (need to use a small delay to ensure state is updated)
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Get all restaurants that have coordinates but no Google rating
      // Check the current restaurants state
      const currentRestaurants = [...restaurants];
      const restaurantsToFetch = currentRestaurants.filter(r => 
        r.latitude && r.longitude && !r.googleRating
      );
      
      const totalWithCoordinates = currentRestaurants.filter(r => r.latitude && r.longitude).length;
      const alreadyHaveRatings = currentRestaurants.filter(r => r.googleRating).length;
      
      console.log(`📊 Stats:
        - Total restaurants: ${currentRestaurants.length}
        - With coordinates: ${totalWithCoordinates}
        - Already have Google ratings: ${alreadyHaveRatings}
        - Need to fetch: ${restaurantsToFetch.length}
      `);
      
      if (restaurantsToFetch.length === 0) {
        alert(`All set! 🎉\n\n${alreadyHaveRatings}/${totalWithCoordinates} restaurants with coordinates already have Google ratings.`);
        return;
      }
      
      const confirmed = window.confirm(
        `Fetch Google ratings for ${restaurantsToFetch.length} restaurants?\n\n` +
        `Total restaurants: ${currentRestaurants.length}\n` +
        `With coordinates: ${totalWithCoordinates}\n` +
        `Already have ratings: ${alreadyHaveRatings}\n` +
        `Need to fetch: ${restaurantsToFetch.length}\n\n` +
        `Estimated time: ${Math.ceil(restaurantsToFetch.length * 2 / 60)} minute(s)\n\n` +
        `You can continue using the app while this runs.`
      );
      
      if (!confirmed) return;
      
      console.log(`🚀 Starting bulk fetch for ${restaurantsToFetch.length} restaurants`);
      
      let successCount = 0;
      let failCount = 0;
      let skippedCount = 0;
      
      // Process restaurants one at a time with a delay to avoid rate limiting
      for (let i = 0; i < restaurantsToFetch.length; i++) {
        const restaurant = restaurantsToFetch[i];
        console.log(`[${i + 1}/${restaurantsToFetch.length}] Fetching rating for: ${restaurant.name} (${restaurant.location})`);
        
        try {
          const result = await fetchGoogleRating(restaurant.id, false);
          
          if (result.success) {
            successCount++;
            console.log(`  ✅ Success: ${result.rating}/5`);
          } else {
            if (result.message && result.message.includes('not found')) {
              skippedCount++;
              console.log(`  ⏭️  Skipped: ${result.message}`);
            } else {
              failCount++;
              console.log(`  ❌ Failed: ${result.message || result.error}`);
            }
          }
        } catch (error) {
          failCount++;
          console.error(`  ❌ Error: ${error.message}`);
        }
        
        // Wait 2 seconds between requests to avoid rate limiting
        if (i < restaurantsToFetch.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
      console.log(`✅ Bulk fetch complete: ${successCount} successful, ${failCount} failed, ${skippedCount} skipped (not found)`);
      alert(
        `Bulk fetch complete! 🎉\n\n` +
        `✅ Successfully fetched: ${successCount}\n` +
        `⏭️  Skipped (not found on Google): ${skippedCount}\n` +
        `❌ Failed: ${failCount}\n\n` +
        `Total processed: ${successCount + failCount + skippedCount}/${restaurantsToFetch.length}\n\n` +
        `Refreshing table now...`
      );
      
      // Refresh the restaurant list to show updated ratings
      await fetchRestaurants();
      console.log('🔄 Restaurant list refreshed - all ratings should now be visible');
      
    } catch (error) {
      console.error('❌ Bulk fetch error:', error);
      alert(`Bulk fetch failed: ${error.message}`);
    }
  };


  const searchRestaurantWebsite = async (name, location) => {
    if (!name || !location) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/restaurant-lookup/search-website`, {
        params: { name, location }
      });
      
      if (response.data.website) {
        setFormData(prev => ({ ...prev, menuLink: response.data.website }));
      }
    } catch (error) {
      console.log('Website search failed:', error);
    }
  };

  const handleAddRestaurant = async (e) => {
    e.preventDefault();
    
    console.log('🔵 handleAddRestaurant called, isSubmitting:', isSubmitting, 'ref:', isSubmittingRef.current);
    
    // Use ref for immediate check
    if (isSubmittingRef.current || isSubmitting) {
      console.log('⚠️ Already submitting, blocking duplicate submission');
      return;
    }
    
    // First, check against On Deck list for close matches
    const newRestaurantName = formData.name.toLowerCase().trim();
    const newLat = formData.latitude ? parseFloat(formData.latitude) : null;
    const newLon = formData.longitude ? parseFloat(formData.longitude) : null;
    
    let onDeckMatch = null;
    
    for (const onDeckRest of onDeckRestaurants) {
      let isMatch = false;
      
      // Check name similarity (fuzzy match - first 7 chars or 80% similar)
      const onDeckName = onDeckRest.name.toLowerCase().trim();
      const nameMatch = onDeckName.substring(0, 7) === newRestaurantName.substring(0, 7);
      
      // Check location proximity (within 0.05 miles / ~80 meters)
      let locationMatch = false;
      if (newLat && newLon && onDeckRest.latitude && onDeckRest.longitude) {
        const distance = calculateDistance(newLat, newLon, onDeckRest.latitude, onDeckRest.longitude);
        locationMatch = distance <= 0.05; // 0.05 miles
      }
      
      // Match if EITHER name is similar OR location is very close
      if (nameMatch || locationMatch) {
        isMatch = true;
      }
      
      if (isMatch) {
        onDeckMatch = onDeckRest;
        break;
      }
    }
    
    // If found in On Deck, ask user
    if (onDeckMatch) {
      const confirmed = window.confirm(
        `📍 Found "${onDeckMatch.name}" in your On Deck list!\n\n` +
        `Is this the same restaurant you're adding?\n\n` +
        `If YES: We'll add it to your restaurants and remove it from On Deck.\n` +
        `If NO: We'll add your restaurant and keep the On Deck item.`
      );
      
      if (confirmed) {
        console.log('✅ User confirmed On Deck match, will remove after adding');
        // Store the On Deck restaurant ID to remove later
        formData.removeFromOnDeckId = onDeckMatch.id;
      }
    }
    
    // Check if restaurant with same name already exists (check ALL users' restaurants)
    // Match on first 7 characters or less
    const searchName = formData.name.toLowerCase().trim().substring(0, 7);
    const existingRestaurant = allUsersRestaurants.find(
      r => r.name.toLowerCase().trim().substring(0, 7) === searchName
    );

    if (existingRestaurant) {
      // Found a duplicate name - ask user if it's the same restaurant
      console.log('⚠️ Duplicate found, showing dialog');
      setDuplicateRestaurant(existingRestaurant);
      setPendingRestaurantData(formData);
      setShowDuplicateDialog(true);
    } else {
      // No duplicate - proceed with adding
      console.log('✅ No duplicate, proceeding with add');
      await addNewRestaurant();
    }
  };

  const addNewRestaurant = async () => {
    // Use ref for immediate check to prevent race conditions
    if (isSubmittingRef.current) {
      console.log('⚠️ addNewRestaurant: Already submitting (ref check), ignoring duplicate request');
      return;
    }
    
    // Set ref immediately before any async work
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    
    console.log('🟢 addNewRestaurant: Starting submission');
    
    try {
      const randomImage = restaurantImages[Math.floor(Math.random() * restaurantImages.length)];
      
      // Use the user-selected first visit date in YYYY-MM-DD format
      const visitDateStr = formData.firstVisitDate; // Keep in YYYY-MM-DD format
      
      const dataToSend = {
        ...formData,
        imageUrl: formData.imageUrl || randomImage,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        ethnicity: formData.ethnicity === 'Other' ? ethnicityOther : formData.ethnicity,
        restroomRating: formData.restroomRating ? parseFloat(formData.restroomRating) : null,
        lastVisit: visitDateStr
      };
      
      // Create the restaurant
      const restaurantResponse = await axios.post(`${API_URL}/api/restaurants`, dataToSend);
      const newRestaurant = restaurantResponse.data;
      
      // Upload photos if any were selected
      if (addRestaurantPhotos.length > 0) {
        await uploadPhotosToRestaurant(newRestaurant.id, addRestaurantPhotos);
      }
      
      // Automatically create first visit with the date in YYYY-MM-DD format
      const visitData = {
        restaurantId: newRestaurant.id,
        visitDate: visitDateStr, // Use YYYY-MM-DD format
        userName: formData.userEmail || '',
        favoriteItems: formData.favoriteItems || '',
        dislikes: formData.dislikes || '',
        notes: 'Initial visit when restaurant was added',
        rating: formData.initialRating ? parseFloat(formData.initialRating) : null,
        bathroomRating: formData.restroomRating ? parseFloat(formData.restroomRating) : null,
        guests: formData.initialGuests || '',
        cost: formData.initialCost ? parseFloat(formData.initialCost) : null
      };
      
      await axios.post(`${API_URL}/api/visits`, visitData);
      
      // If this was matched with an On Deck item, remove it
      if (formData.removeFromOnDeckId) {
        console.log('🗑️ Removing matched restaurant from On Deck:', formData.removeFromOnDeckId);
        try {
          await axios.delete(`${API_URL}/api/on-deck/${formData.removeFromOnDeckId}`);
          console.log('✅ Removed from On Deck successfully');
          // Refresh On Deck list
          await fetchOnDeckRestaurants();
        } catch (error) {
          console.error('Error removing from On Deck:', error);
          // Don't fail the whole operation if On Deck removal fails
        }
      }
      
      // Close dialog and reset form IMMEDIATELY for snappy UX
      setIsAddOpen(false);
      resetForm();
      setAddRestaurantPhotos([]);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      
      // Refresh data in background - fire and forget
      console.log('Refreshing data after add...');
      fetchRestaurants();
      fetchAllUsersRestaurants();
      fetchOnDeckRestaurants();
      getUserLocationAndNearby();
      
    } catch (error) {
      console.error('Error adding restaurant:', error);
      
      // Provide detailed error message
      let errorMessage = 'Error occurred while saving restaurant\n\n';
      
      if (error.response) {
        // Server responded with error
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 409) {
          errorMessage += 'Duplicate restaurant name detected.\n';
          errorMessage += data.detail || 'A restaurant with this name already exists.';
        } else if (status === 400) {
          errorMessage += 'Invalid data:\n';
          errorMessage += data.detail || 'Please check all fields are filled correctly.';
        } else if (status === 401) {
          errorMessage += 'Authentication failed. Your session has expired.\nPlease log out and log in again to continue.';
        } else if (status === 500) {
          errorMessage += 'Server error:\n';
          errorMessage += data.detail || 'An error occurred on the server.';
        } else {
          errorMessage += `Error ${status}: ${data.detail || data.message || 'Unknown error'}`;
        }
      } else if (error.request) {
        // No response from server
        errorMessage += 'Cannot connect to server.\n';
        errorMessage += 'Please check your internet connection.';
      } else {
        // Other error
        errorMessage += error.message || 'An unexpected error occurred.';
      }
      
      alert(errorMessage);
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleDuplicateResponse = async (isSameRestaurant) => {
    setShowDuplicateDialog(false);
    
    if (isSameRestaurant) {
      // It's the same restaurant - open Add Visit dialog
      setSelectedRestaurantForVisit(duplicateRestaurant);
      setSelectedRestaurantId(duplicateRestaurant.id);
      setIsAddOpen(false);
      resetForm();
      
      // Pre-fill visit form with data from the restaurant form
      if (pendingRestaurantData.favoriteItems) {
        setVisitFavorites(pendingRestaurantData.favoriteItems);
      }
      if (pendingRestaurantData.dislikes) {
        setVisitDislikes(pendingRestaurantData.dislikes);
      }
      if (pendingRestaurantData.initialRating) {
        setVisitRating(pendingRestaurantData.initialRating);
      }
      
      // Open the add visit dialog
      saveCurrentTab();
      setIsQuickAddVisitOpen(true);
      
      alert(`Recording a new visit to: ${duplicateRestaurant.name}`);
    } else {
      // It's a different restaurant - proceed with adding new
      await addNewRestaurant();
    }
    
    setDuplicateRestaurant(null);
    setPendingRestaurantData(null);
  };

  const handleEditRestaurant = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting) {
      console.log('Already submitting, ignoring duplicate submission');
      return;
    }
    
    setIsSubmitting(true);
    console.log('Starting restaurant edit...');
    
    try {
      // Get coordinates from form - parseFloat handles any precision
      let latitude = formData.latitude ? parseFloat(String(formData.latitude).trim()) : null;
      let longitude = formData.longitude ? parseFloat(String(formData.longitude).trim()) : null;
      
      console.log('Coordinates to save:', latitude, longitude);
      
      // Validate coordinates are valid numbers
      if (latitude !== null && isNaN(latitude)) latitude = null;
      if (longitude !== null && isNaN(longitude)) longitude = null;
      
      let location = formData.location;
      let website = formData.website;
      let phone = formData.phone;
      let rating = formData.rating;
      
      // Only try to geocode if: address changed AND no valid coordinates provided
      const addressChanged = formData.fullAddress && formData.fullAddress !== selectedRestaurant.fullAddress;
      const hasCoordinates = latitude !== null && longitude !== null;
      
      if (addressChanged && !hasCoordinates) {
        // Address changed but no coordinates - try to geocode with timeout
        console.log('Address changed without coordinates, attempting to geocode:', formData.fullAddress);
        try {
          const geocodePromise = geocodeLocation(formData.fullAddress);
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Geocode timeout')), 3000)
          );
          const coords = await Promise.race([geocodePromise, timeoutPromise]);
          if (coords) {
            latitude = coords.latitude;
            longitude = coords.longitude;
            console.log('New coordinates from geocode:', coords);
          }
        } catch (error) {
          console.error('Geocoding failed or timed out:', error);
        }
      }
      
      const dataToSend = {
        ...formData,
        location: location,
        latitude: latitude,
        longitude: longitude,
        website: website,
        phone: phone,
        rating: rating,
        ethnicity: formData.ethnicity === 'Other' ? ethnicityOther : formData.ethnicity,
        restroomRating: formData.restroomRating ? parseFloat(formData.restroomRating) : null,
        overallRating: formData.overallRating ? parseFloat(formData.overallRating) : null
      };
      const response = await axios.put(`${API_URL}/api/restaurants/${selectedRestaurant.id}`, dataToSend);
      const updatedRestaurant = response.data;
      
      // Close dialog immediately for snappy UX
      setIsEditOpen(false);
      setSelectedRestaurant(null);
      resetForm();
      setIsSubmitting(false);
      
      // Update the restaurant in local state immediately (no refetch needed)
      setRestaurants(prev => prev.map(r => r.id === updatedRestaurant.id ? { ...r, ...updatedRestaurant } : r));
      setAllUsersRestaurants(prev => prev.map(r => r.id === updatedRestaurant.id ? { ...r, ...updatedRestaurant } : r));
      setAllRestaurantsList(prev => prev.map(r => r.id === updatedRestaurant.id ? { ...r, ...updatedRestaurant } : r));
      
      // Light background refresh of just the restaurants list (no geolocation, no visits recalc)
      try {
        const params = viewingAsUser ? { impersonate_user: viewingAsUser.email } : {};
        const freshData = await axios.get(`${API_URL}/api/restaurants`, { params });
        if (freshData.data) {
          // Preserve existing calculated fields (distance, averageRating)
          const merged = freshData.data.map(fresh => {
            const existing = restaurants.find(r => r.id === fresh.id);
            return existing ? { ...existing, ...fresh } : fresh;
          });
          setRestaurants(merged);
        }
      } catch (refreshErr) {
        console.warn('Background refresh after edit failed, using local state:', refreshErr.message);
      }
      
    } catch (error) {
      console.error('Error updating restaurant:', error);
      alert('Error updating restaurant. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Archive = "closed / out of business". Hidden from lists, map, search and
  // Random Pick (server-side filter) but every visit/photo/stat is kept.
  const handleArchiveRestaurant = async (restaurant) => {
    const archiving = !restaurant.archived;
    const message = archiving
      ? `Mark "${restaurant.name}" as CLOSED?\n\n• Hidden from lists, map, search and Random Pick\n• All visits, photos and stats are kept\n• Your group gets a short "Restaurant closed" email\n\nYou can reopen it later with "Show closed restaurants".`
      : `Reopen "${restaurant.name}"?\n\nIt will appear in lists, the map and Random Pick again.`;
    if (!window.confirm(message)) return;
    try {
      await axios.post(`${API_URL}/api/restaurants/${restaurant.id}/${archiving ? 'archive' : 'unarchive'}`);
      fetchRestaurants();
      fetchAllUsersRestaurants();
      loadAllRestaurants();
    } catch (error) {
      alert(error.response?.data?.detail || 'Could not update the restaurant. Please try again.');
    }
  };

  const toggleShowArchived = () => {
    setShowArchived((prev) => {
      localStorage.setItem('das.showArchived', prev ? '0' : '1');
      return !prev;
    });
  };

  useEffect(() => {
    if (!showArchivedMounted.current) { showArchivedMounted.current = true; return; }
    if (!authToken) return;
    fetchRestaurants();
    fetchAllUsersRestaurants();
    loadAllRestaurants();
  }, [showArchived]);

  const renderShowClosedToggle = () => (
    <label className="show-closed-toggle" data-testid="show-closed-toggle">
      <input type="checkbox" checked={showArchived} onChange={toggleShowArchived} data-testid="show-closed-checkbox" />
      Show closed restaurants
    </label>
  );

  const canArchive = (restaurant) => isAdmin || restaurant.user_email === userEmail;

  const handleDeleteRestaurant = async (id) => {
    const warningMessage = 
      '⚠️ WARNING: This will permanently delete:\n\n' +
      '• The restaurant\n' +
      '• All visits to this restaurant\n' +
      '• All photos for this restaurant\n\n' +
      'This action cannot be undone.\n\n' +
      'Are you sure you want to continue?';
    
    if (window.confirm(warningMessage)) {
      try {
        const response = await axios.delete(`${API_URL}/api/restaurants/${id}`);
        
        // Show confirmation with details
        if (response.data.visits_deleted > 0) {
          alert(`Restaurant deleted successfully!\n\nDeleted: 1 restaurant, ${response.data.visits_deleted} visit(s), and all photos.`);
        }
        
        // Refresh data in background - fire and forget
        console.log('Refreshing data after delete...');
        fetchRestaurants();
        fetchAllUsersRestaurants();
        getUserLocationAndNearby();
      } catch (error) {
        console.error('Error deleting restaurant:', error);
        alert('Error deleting restaurant. Please try again.');
      }
    }
  };

  // Merge two restaurants together. First click selects the "primary" (its
  // name/address survive). Second click on a different restaurant fires the
  // backend merge endpoint. Set mergePrimary to null at any time to cancel.
  const handleMergeRestaurant = async (restaurant) => {
    if (!mergePrimary) {
      // First click — enter merge mode silently. The yellow banner at the top
      // and the highlighted row make it obvious what state we're in.
      setMergePrimary({ id: restaurant.id, name: restaurant.name });
      return;
    }
    if (mergePrimary.id === restaurant.id) {
      // Clicked the primary again — cancel merge mode.
      setMergePrimary(null);
      return;
    }
    // Second click — confirm and fire the merge.
    const ok = window.confirm(
      `🔀 MERGE CONFIRMATION\n\n` +
      `Primary (kept): "${mergePrimary.name}"\n` +
      `Secondary (deleted): "${restaurant.name}"\n\n` +
      `All of "${restaurant.name}"'s visits and photos will move to "${mergePrimary.name}". ` +
      `Missing fields on the primary will be filled in from the secondary. ` +
      `The primary's NAME and ADDRESS are preserved.\n\n` +
      `This cannot be undone. Continue?`
    );
    if (!ok) {
      setMergePrimary(null);
      return;
    }
    try {
      const res = await axios.post(
        `${API_URL}/api/restaurants/${mergePrimary.id}/merge/${restaurant.id}`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const moved = res.data?.moved_visits ?? 0;
      const filled = (res.data?.fields_filled_from_secondary || []).join(', ');
      const photoCount = res.data?.total_photos_after_merge ?? 0;
      alert(
        `✅ Merge complete!\n\n` +
        `Moved ${moved} visit(s) into "${mergePrimary.name}".\n` +
        (filled ? `Filled missing fields: ${filled}\n` : '') +
        `Photos after merge: ${photoCount}\n` +
        `"${restaurant.name}" has been deleted.`
      );
      const mergedSecondaryId = restaurant.id;
      setMergePrimary(null);

      // OPTIMISTIC UI UPDATE — remove the deleted secondary from EVERY local
      // restaurant state array immediately so whichever list/view the user is
      // looking at (dashboard nearby, My Restaurants, All Users, On Deck, etc.)
      // shows the change before the network refresh lands.
      const dropSecondary = (arr) => (arr || []).filter((r) => r.id !== mergedSecondaryId);
      setRestaurants((prev) => dropSecondary(prev));
      setAllUsersRestaurants((prev) => dropSecondary(prev));
      setFilteredRestaurants((prev) => dropSecondary(prev));
      setNearbyRestaurants((prev) => dropSecondary(prev));
      setAllNearbyRestaurants((prev) => dropSecondary(prev));
      setAllRestaurantsList((prev) => dropSecondary(prev));
      setOnDeckRestaurants((prev) => dropSecondary(prev));
      setRecentVisits((prev) => (prev || []).filter((v) => v.restaurantId !== mergedSecondaryId));

      // If the deleted restaurant was open in any detail panel/dialog, close it
      // so the user isn't staring at stale data for an id that no longer exists.
      if (selectedRestaurantHistory && selectedRestaurantHistory.id === mergedSecondaryId) {
        setIsVisitHistoryOpen(false);
        setSelectedRestaurantHistory(null);
      }
      if (selectedRestaurantDetails && selectedRestaurantDetails.id === mergedSecondaryId) {
        setSelectedRestaurantDetails(null);
      }
      if (selectedRestaurant && selectedRestaurant.id === mergedSecondaryId) {
        setSelectedRestaurant(null);
      }

      // Now do the full network refresh in the background so all aggregates
      // (visit counts, photo counts, ratings) match the server.
      await Promise.all([
        fetchRestaurants(),
        fetchAllUsersRestaurants(),
        getUserLocationAndNearby()
      ]);
      setRefreshTrigger((t) => t + 1);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      alert(`❌ Merge failed: ${detail}`);
    }
  };

  const openEditDialog = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setFormData({
      name: restaurant.name,
      location: restaurant.location,
      fullAddress: restaurant.fullAddress || '',
      favoriteItems: restaurant.favoriteItems,
      dislikes: restaurant.dislikes || '',
      price: restaurant.price || '',
      patio: restaurant.patio || false,
      dogFriendly: restaurant.dogFriendly || false,
      heineken: restaurant.heineken || false,
      staff: restaurant.staff || '',
      ownership: restaurant.ownership || '',
      lastVisit: restaurant.lastVisit || '',
      menuLink: restaurant.menuLink || '',
      imageUrl: restaurant.imageUrl || '',
      latitude: restaurant.latitude || '',
      longitude: restaurant.longitude || '',
      ethnicity: restaurant.ethnicity || '',
      restroomRating: restaurant.restroomRating || '',
      overallRating: restaurant.overallRating || ''
    });
    saveCurrentTab();
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location: '',
      fullAddress: '',
      favoriteItems: '',
      dislikes: '',
      price: '',
      patio: false,
      dogFriendly: false,
      heineken: false,
      staff: '',
      ownership: '',
      lastVisit: '',
      menuLink: '',
      imageUrl: '',
      latitude: '',
      longitude: '',
      ethnicity: '',
      restroomRating: '',
      initialRating: '',
      userEmail: '',
      firstVisitDate: getLocalDateString(), // Reset to today (local time)
      initialGuests: '',
      initialCost: ''
    });
    setEthnicityOther('');
    setDuplicateWarning(null);
  };

  const checkForDuplicate = (name) => {
    if (name.trim().length > 0) {
      // Helper function to normalize restaurant name for comparison
      const normalizeName = (restaurantName) => {
        let normalized = restaurantName.toLowerCase().trim();
        // Remove "The" from the beginning if present
        if (normalized.startsWith('the ')) {
          normalized = normalized.substring(4);
        }
        return normalized;
      };
      
      // Check against ALL users' restaurants (not just current user's)
      // Normalize and match on first 7 characters or less
      const searchName = normalizeName(name).substring(0, 7);
      const existing = allUsersRestaurants.find(r => {
        const existingNormalized = normalizeName(r.name).substring(0, 7);
        return existingNormalized === searchName;
      });
      
      if (existing) {
        const isOwnRestaurant = existing.user_email === userEmail;
        setDuplicateWarning({
          id: existing.id,
          name: existing.name,
          location: existing.location,
          ethnicity: existing.ethnicity,
          price: existing.price,
          ownerDisplayName: existing.ownerDisplayName,
          isOwnRestaurant: isOwnRestaurant
        });
      } else {
        setDuplicateWarning(null);
      }
    } else {
      setDuplicateWarning(null);
    }
  };

  const handleAddVisitFromWarning = () => {
    // Close Add Restaurant dialog
    setIsAddOpen(false);
    
    // Find the full restaurant object from allUsersRestaurants
    const existingRestaurant = allUsersRestaurants.find(r => r.id === duplicateWarning.id);
    
    if (existingRestaurant) {
      // Set up the Add Visit dialog with the existing restaurant
      setSelectedRestaurantForVisit(existingRestaurant);
      setSelectedRestaurantId(existingRestaurant.id);
      
      // Pre-fill visit form with data from the restaurant form if available
      if (formData.favoriteItems) {
        setVisitFavorites(formData.favoriteItems);
      }
      if (formData.dislikes) {
        setVisitDislikes(formData.dislikes);
      }
      if (formData.initialRating) {
        setVisitRating(formData.initialRating);
      }
      
      // Reset Add Restaurant form
      resetForm();
      setDuplicateWarning(null);
      
      // Open the Add Visit dialog
      saveCurrentTab();
      setIsQuickAddVisitOpen(true);
    }
  };


  const findNearestRestaurants = async () => {
    console.log('findNearestRestaurants called');
    
    // Toggle off if already showing
    if (showNearest) {
      setShowNearest(false);
      setUserLocation(null);
      console.log('Toggled off showNearest');
      return;
    }
    
    setLoading(true);
    
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      console.log('Geolocation not supported');
      alert('Geolocation is not supported by your browser.');
      setLoading(false);
      return;
    }
    
    // Check if we're on a secure context (HTTPS or localhost)
    const isSecure = window.location.protocol === 'https:' || 
                     window.location.hostname.includes('localhost') || 
                     window.location.hostname.includes('127.0.0.1');
    
    console.log('Is secure context:', isSecure);
    
    if (!isSecure) {
      alert('⚠️ Location services require a secure connection (HTTPS).\n\n' +
            'Your browser blocks location access on non-secure sites.\n\n' +
            'Please access this site via HTTPS or contact your administrator to enable SSL.');
      setLoading(false);
      return;
    }
    
    console.log('Requesting geolocation...');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        console.log('Geolocation success:', position.coords);
        const { latitude, longitude } = position.coords;
        setUserLocation({ latitude, longitude });
        
        try {
          console.log('Calling nearest API...');
          const response = await axios.post(`${API_URL}/api/restaurants/nearest`, {
            latitude,
            longitude
          });
          
          console.log('API response received:', response.data.length, 'restaurants');
          
          // Sort by distance and take top 20 nearest
          const sorted = response.data.sort((a, b) => {
            const distA = parseFloat(a.distance) || 999;
            const distB = parseFloat(b.distance) || 999;
            return distA - distB;
          });
          
          const nearest20 = sorted.slice(0, 20);
          
          if (nearest20.length === 0) {
            alert('No restaurants found in the database.');
            setLoading(false);
            return;
          }
          
          // Update both the filtered results AND the dashboard neaa);
          setNearbyRestaurants(nearest20); // Dashboard shows top 20 nearest
          setShowNearest(true);
          setShowLatestVisitsView(false); // Clear latest visits when showing nearest
          console.log('Set showNearest to true, displaying', nearest20.length, 'restaurants');
          setLoading(false);
        } catch (error) {
          console.error('Error finding nearest restaurants:', error);
          alert('Error finding nearby restaurants. Please try again.');
          setLoading(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Unable to get your location.\n\n';
        
        if (error.code === error.PERMISSION_DENIED) {
          errorMessage += 'You denied location permission. Please enable it in your browser settings.';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMessage += 'Location information is unavailable.';
        } else if (error.code === error.TIMEOUT) {
          errorMessage += 'Location request timed out. Please try again.';
        } else {
          errorMessage += 'Please enable location services in your browser settings.';
        }
        
        alert(errorMessage);
        setLoading(false);
      }
    );
  };

  const clearNearestFilter = () => {
    setShowNearest(false);
    setFilteredRestaurants([]);
    setUserLocation(null);
    // Refetch original restaurants without distance info
    fetchRestaurants();
  };

  const handleFilterChange = (category, value) => {
    // If on Dashboard, switch to My Restaurants tab when filter is applied
    if (activeTab === 'dashboard') {
      setActiveTab('allRestaurants');
    }
    // If on All Dashboard (admin), switch to All Restaurants tab when filter is applied
    if (activeTab === 'allDashboard') {
      setActiveTab('allUsersRestaurants');
    }
    
    setFilters(prev => {
      if (category === 'patio' || category === 'dogFriendly' || category === 'heineken') {
        return { ...prev, [category]: prev[category] === value ? null : value };
      } else {
        const current = prev[category];
        const updated = current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value];
        return { ...prev, [category]: updated };
      }
    });
  };

  const clearAllFilters = () => {
    setFilters({
      price: [],
      location: [],
      patio: null,
      dogFriendly: null,
      heineken: null,
      ownership: [],
      ethnicity: []
    });
    setSortBy('name');
  };

  const loadAllRestaurants = async () => {
    try {
      const params = viewingAsUser ? { impersonate_user: viewingAsUser.email } : {};
      if (showArchived) params.include_archived = true;
      const response = await axios.get(`${API_URL}/api/restaurants`, { params });
      let restaurants = response.data;
      
      // Try to fetch all visits to calculate correct average ratings (with timeout)
      try {
        const visitsParams = { limit: 10000, ...params };
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        
        const visitsResponse = await axios.get(`${API_URL}/api/latest_visits`, { 
          params: visitsParams,
          signal: controller.signal 
        });
        clearTimeout(timeoutId);
        
        const allVisits = visitsResponse.data;
        
        // Calculate average rating for each restaurant from visits
        restaurants = restaurants.map(restaurant => {
          const restaurantVisits = allVisits.filter(v => v.restaurantId === restaurant.id && v.rating && v.rating > 0);
          let calculatedAverageRating = null;
          if (restaurantVisits.length > 0) {
            const avgRating = restaurantVisits.reduce((sum, v) => sum + v.rating, 0) / restaurantVisits.length;
            calculatedAverageRating = parseFloat(avgRating.toFixed(1));
          }
          return { ...restaurant, averageRating: calculatedAverageRating };
        });
        console.log('✅ loadAllRestaurants: Recalculated ratings');
      } catch (visitError) {
        if (visitError.name === 'AbortError' || visitError.code === 'ECONNABORTED') {
          console.warn('loadAllRestaurants: Visits fetch timed out, using stored ratings');
        } else {
          console.warn('loadAllRestaurants: Could not fetch visits for rating calculation:', visitError.message);
        }
        // Continue with stored ratings from backend
      }
      
      // Add distance if user location is available
      if (userLocation) {
        restaurants = restaurants.map(r => {
          if (r.latitude && r.longitude) {
            const distance = calculateDistance(
              userLocation.latitude,
              userLocation.longitude,
              r.latitude,
              r.longitude
            );
            return { ...r, distance: distance };
          }
          return { ...r, distance: null };
        });
      }
      
      setAllRestaurantsList(restaurants);
      setAllRestaurantsLoaded(true);
    } catch (error) {
      console.error('Error loading all restaurants:', error);
      // Ensure loaded state is set even on error to prevent infinite loading
      setAllRestaurantsLoaded(true);
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles (was 6371 km)
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const getSortedRestaurants = () => {
    // Start with all restaurants
    let filtered = allRestaurantsList;
    
    // Apply search query filter
    if (searchQuery) {
      filtered = filtered.filter(r => 
        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.favoriteItems.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply cuisine/ethnicity filter
    if (filters.ethnicity.length > 0) {
      filtered = filtered.filter(r => filters.ethnicity.includes(r.ethnicity));
    }
    
    // Apply location filter
    if (filters.location.length > 0) {
      filtered = filtered.filter(r => {
        const parts = r.location.split(',').map(p => p.trim());
        let cityState = r.location;
        for (let i = 0; i < parts.length; i++) {
          const part = parts[i];
          if (/^[A-Z]{2}$/i.test(part) && i > 0) {
            cityState = `${parts[i - 1]}, ${part}`;
            break;
          }
          const match = part.match(/^([A-Z]{2})\s+\d+$/i);
          if (match && i > 0) {
            cityState = `${parts[i - 1]}, ${match[1]}`;
            break;
          }
        }
        return filters.location.includes(cityState);
      });
    }
    
    // Apply ownership filter
    if (filters.ownership.length > 0) {
      filtered = filtered.filter(r => filters.ownership.includes(r.ownership));
    }
    
    // Apply price filter
    if (filters.price.length > 0) {
      filtered = filtered.filter(r => filters.price.includes(r.price));
    }
    
    // Apply amenity filters
    if (filters.patio !== null) {
      filtered = filtered.filter(r => r.patio === filters.patio);
    }
    if (filters.dogFriendly !== null) {
      filtered = filtered.filter(r => r.dogFriendly === filters.dogFriendly);
    }
    if (filters.heineken !== null) {
      filtered = filtered.filter(r => r.heineken === filters.heineken);
    }
    
    // Then sort
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      if (sortColumn === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortColumn === 'distance') {
        const distA = a.distance !== null ? parseFloat(a.distance) : 999999;
        const distB = b.distance !== null ? parseFloat(b.distance) : 999999;
        comparison = distA - distB;
      } else if (sortColumn === 'location') {
        const distA = a.distance !== null ? parseFloat(a.distance) : 999999;
        const distB = b.distance !== null ? parseFloat(b.distance) : 999999;
        comparison = distA - distB;
      } else if (sortColumn === 'ethnicity') {
        comparison = (a.ethnicity || '').localeCompare(b.ethnicity || '');
      } else if (sortColumn === 'ownership') {
        comparison = (a.ownership || '').localeCompare(b.ownership || '');
      } else if (sortColumn === 'owner') {
        comparison = (a.owner || '').localeCompare(b.owner || '');
      } else if (sortColumn === 'price') {
        const priceA = a.price !== null ? parseFloat(a.price) : 0;
        const priceB = b.price !== null ? parseFloat(b.price) : 0;
        comparison = priceA - priceB;
      } else if (sortColumn === 'rating') {
        const ratingA = a.overallRating !== null ? parseFloat(a.overallRating) : 0;
        const ratingB = b.overallRating !== null ? parseFloat(b.overallRating) : 0;
        comparison = ratingA - ratingB;
      } else if (sortColumn === 'visitCount') {
        const countA = a.visitCount || 0;
        const countB = b.visitCount || 0;
        comparison = countA - countB;
      } else if (sortColumn === 'lastVisit') {
        const dateA = a.lastVisit ? new Date(a.lastVisit) : new Date(0);
        const dateB = b.lastVisit ? new Date(b.lastVisit) : new Date(0);
        comparison = dateA - dateB;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  };

  const hasActiveFilters = () => {
    return filters.price.length > 0 || 
           filters.location.length > 0 || 
           filters.ownership.length > 0 ||
           filters.ethnicity.length > 0 ||
           filters.patio !== null || 
           filters.dogFriendly !== null || 
           filters.heineken !== null;
  };

  const openVisitDialog = (restaurant) => {
    setSelectedRestaurantForVisit(restaurant);
    // Set default to today's date
    const today = getLocalDateString();
    setVisitDate(today);
    setVisitUserName('');
    setVisitFavorites('');
    setVisitDislikes('');
    setVisitNotes('');
    setVisitRating('');
    setVisitStaffNotes('');
    setVisitBathroomRating('');
    setVisitCost('');
    setVisitGuests('');
    saveCurrentTab();
    setIsVisitDialogOpen(true);
  };

  const openQuickAddVisit = () => {
    setSelectedRestaurantId('');
    const today = getLocalDateString();
    setVisitDate(today);
    setVisitUserName('');
    setVisitFavorites('');
    setVisitDislikes('');
    setVisitNotes('');
    setVisitRating('');
    setVisitStaffNotes('');
    setVisitBathroomRating('');
    setVisitCost('');
    setVisitGuests('');
    saveCurrentTab();
    setIsQuickAddVisitOpen(true);
  };

  // Smart Check In - determines if at existing restaurant or new location
  const handleSmartCheckIn = async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const userLat = position.coords.latitude;
        const userLon = position.coords.longitude;

        try {
          // Combine user's restaurants with shared group restaurants
          const allKnownRestaurants = [...restaurants];
          
          // Add group restaurants that aren't already in user's list
          const myRestaurantKeys = new Set(restaurants.map(r => `${r.name?.toLowerCase()}|${r.location?.toLowerCase()}`));
          for (const r of allUsersRestaurants) {
            const key = `${r.name?.toLowerCase()}|${r.location?.toLowerCase()}`;
            if (!myRestaurantKeys.has(key)) {
              allKnownRestaurants.push({ ...r, isFromGroup: true });
            }
          }
          
          // Calculate distances to all known restaurants
          const restaurantsWithDistance = allKnownRestaurants.map(r => {
            if (r.latitude && r.longitude) {
              const distance = calculateDistance(userLat, userLon, r.latitude, r.longitude);
              return { ...r, distance };
            }
            return { ...r, distance: null };
          }).filter(r => r.distance !== null);

          // Sort by distance
          restaurantsWithDistance.sort((a, b) => a.distance - b.distance);

          // Check if closest restaurant is within 0.1 miles (~160 meters)
          const THRESHOLD_MILES = 0.1;
          
          if (restaurantsWithDistance.length > 0 && restaurantsWithDistance[0].distance <= THRESHOLD_MILES) {
            // Found existing restaurant nearby - show dialog with options
            const nearestRestaurant = restaurantsWithDistance[0];
            
            setDetectedRestaurant(nearestRestaurant);
            setCheckInLocation({ lat: userLat, lon: userLon });
            setIsCheckInDialogOpen(true);
            setLoading(false);
          } else {
            // No restaurant found nearby - show the nice dialog for new restaurant
            setLoading(false);
            
            // Get address from coordinates first
            try {
              const nominatimUrl = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${userLat}&lon=${userLon}&zoom=18&addressdetails=1`;
              const response = await fetch(nominatimUrl, {
                headers: { 'User-Agent': 'Dine-A-Saurus-Restaurant-App' }
              });
              const data = await response.json();
              
              const address = data.address || {};
              const city = address.city || address.town || address.village || '';
              const state = address.state || '';
              const location = `${city}${city && state ? ', ' : ''}${state}`;
              const detectedAddress = data.display_name || '';
              
              // Set detected info and show the nice dialog
              setDetectedRestaurant({
                isNew: true,
                detectedAddress: detectedAddress,
                location: location,
                latitude: userLat,
                longitude: userLon
              });
              setCheckInLocation({ lat: userLat, lon: userLon });
              setIsCheckInDialogOpen(true);
              
            } catch (error) {
              console.error('Error getting address:', error);
              // Still show the dialog even if address detection fails
              setDetectedRestaurant({
                isNew: true,
                detectedAddress: '',
                location: '',
                latitude: userLat,
                longitude: userLon
              });
              setCheckInLocation({ lat: userLat, lon: userLon });
              setIsCheckInDialogOpen(true);
            }
          }
        } catch (error) {
          setLoading(false);
          console.error('Error:', error);
          alert('Error determining location. Please try again.');
        }
      },
      (error) => {
        setLoading(false);
        console.error('Geolocation error:', error);
        alert('Unable to get your location. Please enable location services.');
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };
  
  // Handle accepting detected restaurant for check-in
  const handleAcceptDetectedRestaurant = async () => {
    if (detectedRestaurant) {
      // If it's a group restaurant (not in user's list), first copy it to user's restaurants
      if (detectedRestaurant.isFromGroup) {
        try {
          // Create a copy of the restaurant for this user with all required fields
          const restaurantData = {
            name: detectedRestaurant.name || '',
            location: detectedRestaurant.location || '',
            ethnicity: detectedRestaurant.ethnicity || '',
            price: detectedRestaurant.price || '',
            notes: detectedRestaurant.notes || '',
            patio: detectedRestaurant.patio || false,
            fullAddress: detectedRestaurant.fullAddress || '',
            latitude: detectedRestaurant.latitude || null,
            longitude: detectedRestaurant.longitude || null,
            googleRating: detectedRestaurant.googleRating || null,
            ownership: detectedRestaurant.ownership || '',
            website: detectedRestaurant.website || '',
            phoneNumber: detectedRestaurant.phoneNumber || '',
            favoriteItems: detectedRestaurant.favoriteItems || '',  // Required field
            dislikes: detectedRestaurant.dislikes || '',
            dogFriendly: detectedRestaurant.dogFriendly || false,
            heineken: detectedRestaurant.heineken || false,
            staff: detectedRestaurant.staff || '',
            menuLink: detectedRestaurant.menuLink || ''
          };
          
          console.log('Creating restaurant from group:', restaurantData);
          
          const response = await axios.post(`${API_URL}/api/restaurants`, restaurantData, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          
          console.log('Restaurant created:', response.data);
          
          // Use the new restaurant ID for the visit
          setSelectedRestaurantId(response.data.id);
          // Refresh restaurants list
          await fetchRestaurants();
        } catch (error) {
          console.error('Error copying restaurant:', error.response?.data || error);
          alert('Failed to add restaurant to your list: ' + (error.response?.data?.detail || error.message));
          return;
        }
      } else {
        setSelectedRestaurantId(detectedRestaurant.id);
      }
      
      const today = getLocalDateString();
      setVisitDate(today);
      setVisitUserName('');
      setVisitFavorites('');
      setVisitDislikes('');
      setVisitNotes('');
      setVisitRating('');
      setVisitStaffNotes('');
      setVisitBathroomRating('');
      setVisitGuests('');
      setIsCheckInDialogOpen(false);
      saveCurrentTab();
      setIsQuickAddVisitOpen(true);
    }
  };
  
  // Handle choosing a different restaurant from the list
  const handleChooseDifferentRestaurant = () => {
    setIsCheckInDialogOpen(false);
    // Open the quick add visit dialog without pre-selecting a restaurant
    const today = getLocalDateString();
    setVisitDate(today);
    setVisitUserName('');
    setVisitFavorites('');
    setVisitDislikes('');
    setVisitNotes('');
    setVisitRating('');
    setVisitStaffNotes('');
    setVisitBathroomRating('');
    setVisitGuests('');
    setSelectedRestaurantId('');
    saveCurrentTab();
    setIsQuickAddVisitOpen(true);
  };
  
  // Handle adding a new restaurant from check-in
  const handleAddNewRestaurantFromCheckIn = () => {
    setIsCheckInDialogOpen(false);
    // Pre-fill location if available
    if (checkInLocation) {
      setFormData(prev => ({
        ...prev,
        latitude: checkInLocation.lat,
        longitude: checkInLocation.lon
      }));
    }
    // Reset form and open the add restaurant dialog
    resetForm();
    setIsAddOpen(true);
  };

  // Text-to-speech for restaurant info
  const handleSpeakRestaurantInfo = () => {
    if (!selectedRestaurantHistory) return;
    
    // Stop any ongoing speech
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      return;
    }
    
    // Check if Speech API is supported
    if (!window.speechSynthesis) {
      alert('❌ Text-to-speech is not supported in your browser. Try Chrome, Safari, or Edge.');
      return;
    }
    
    // Get visit count
    const visitCount = visitHistory.length;
    
    // Get last visit date and calculate days since
    let lastVisitInfo = '';
    if (visitHistory.length > 0) {
      // Sort visits by date to get most recent
      const sortedVisits = [...visitHistory].sort((a, b) => 
        new Date(b.visitDate) - new Date(a.visitDate)
      );
      const lastVisit = sortedVisits[0];
      const lastVisitDate = new Date(lastVisit.visitDate);
      const today = new Date();
      const diffTime = Math.abs(today - lastVisitDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      // Format the date nicely
      const dateOptions = { month: 'long', day: 'numeric', year: 'numeric' };
      const formattedDate = lastVisitDate.toLocaleDateString('en-US', dateOptions);
      
      if (diffDays === 0) {
        lastVisitInfo = `You last visited today.`;
      } else if (diffDays === 1) {
        lastVisitInfo = `You haven't been here since yesterday.`;
      } else {
        lastVisitInfo = `You haven't been here since ${formattedDate}, ${diffDays} days ago.`;
      }
    }
    
    // Gather favorites from all visits
    const allFavorites = visitHistory
      .map(v => v.favoriteItems)
      .filter(f => f && f.trim())
      .join(', ');
    const items = allFavorites.split(',').map(item => item.trim()).filter(item => item);
    const uniqueFavorites = [...new Set(items.map(item => item.toLowerCase()))];
    const favorites = uniqueFavorites.map(lower => 
      items.find(item => item.toLowerCase() === lower)
    ).join(', ');
    
    // Gather dislikes from all visits
    const allDislikes = visitHistory
      .map(v => v.dislikes)
      .filter(d => d && d.trim())
      .join(', ');
    const dislikeItems = allDislikes.split(',').map(item => item.trim()).filter(item => item);
    const uniqueDislikes = [...new Set(dislikeItems.map(item => item.toLowerCase()))];
    const dislikes = uniqueDislikes.map(lower => 
      dislikeItems.find(item => item.toLowerCase() === lower)
    ).join(', ');
    
    // Create speech text
    let speechText = `${selectedRestaurantHistory.name}. `;
    
    // Add visit count
    if (visitCount > 1) {
      speechText += `You have eaten here ${visitCount} times. `;
    } else if (visitCount === 1) {
      speechText += `You have eaten here once. `;
    }
    
    // Add last visit info
    if (lastVisitInfo) {
      speechText += `${lastVisitInfo} `;
    }
    
    // Add your average rating across visits
    const ratedVisits = visitHistory.filter(v => typeof v.rating === 'number' && v.rating > 0);
    if (ratedVisits.length > 0) {
      const avgRating = ratedVisits.reduce((s, v) => s + v.rating, 0) / ratedVisits.length;
      speechText += `Your rating is ${avgRating.toFixed(1)} out of 5. `;
    }
    
    // Add Google's rating if available
    if (selectedRestaurantHistory.googleRating) {
      const gr = Number(selectedRestaurantHistory.googleRating);
      if (!Number.isNaN(gr) && gr > 0) {
        speechText += `Google rates it ${gr.toFixed(1)} out of 5. `;
      }
    }
    
    // Distance + rough drive time (assumes ~35 mph average)
    if (typeof selectedRestaurantHistory.distance === 'number' && selectedRestaurantHistory.distance > 0) {
      const miles = selectedRestaurantHistory.distance;
      const minutes = Math.max(1, Math.round((miles / 35) * 60));
      const mileTxt = miles < 1 ? `${miles.toFixed(1)} miles` : `${miles.toFixed(0)} miles`;
      speechText += `It is ${mileTxt} away, about ${minutes} minute${minutes === 1 ? '' : 's'} by car. `;
    }
    
    // Average spend across visits that recorded a cost
    const visitsWithCost = visitHistory.filter(v => typeof v.cost === 'number' && v.cost > 0);
    if (visitsWithCost.length > 0) {
      const totalCost = visitsWithCost.reduce((s, v) => s + v.cost, 0);
      const avgCost = totalCost / visitsWithCost.length;
      speechText += `Your average spend here is ${Math.round(avgCost)} dollars. `;
    }
    
    // Other guests who have dined here with you (across all visits)
    const guestPool = visitHistory
      .map(v => (v.guests || '').toString())
      .flatMap(s => s.split(/[,;&]| and /i))
      .map(g => g.trim())
      .filter(g => g.length > 0);
    if (guestPool.length > 0) {
      const seen = new Set();
      const uniqueGuests = [];
      for (const g of guestPool) {
        const key = g.toLowerCase();
        if (!seen.has(key)) { seen.add(key); uniqueGuests.push(g); }
      }
      if (uniqueGuests.length > 0) {
        const list = uniqueGuests.length === 1
          ? uniqueGuests[0]
          : uniqueGuests.slice(0, -1).join(', ') + ' and ' + uniqueGuests[uniqueGuests.length - 1];
        speechText += `You have dined here with ${list}. `;
      }
    }
    
    // Add likes
    if (favorites) {
      speechText += `Your likes are: ${favorites}. `;
    }
    
    // Add dislikes
    if (dislikes) {
      speechText += `Your dislikes are: ${dislikes}.`;
    }
    
    if (!favorites && !dislikes && ratedVisits.length === 0 && visitsWithCost.length === 0) {
      speechText += 'No likes, dislikes, ratings, or spend recorded yet.';
    }
    
    console.log('🔊 Speaking:', speechText);
    
    // Use Web Speech API
    const utterance = new SpeechSynthesisUtterance(speechText);
    utterance.rate = 0.9; // Slightly slower for clarity
    utterance.pitch = 1;
    utterance.volume = 1; // Max volume (0 to 1)
    
    // Log errors to console (no alerts)
    utterance.onerror = (event) => {
      console.error('❌ Speech error:', event.error);
    };
    
    window.speechSynthesis.speak(utterance);
  };

  const handleRestaurantSelect = (restaurantId) => {
    setSelectedRestaurantId(restaurantId);
    if (restaurantId) {
      const restaurant = restaurants.find(r => r.id === restaurantId);
      setSelectedRestaurantForVisit(restaurant);
    } else {
      setSelectedRestaurantForVisit(null);
    }
  };

  const handleQuickAddVisit = async (e) => {
    e.preventDefault();
    
    console.log('🔵 handleQuickAddVisit called, isSubmitting:', isSubmitting);
    
    if (!selectedRestaurantId) {
      alert('Please select a restaurant');
      return;
    }
    
    if (isSubmitting) {
      console.log('⚠️ Already submitting visit, ignoring duplicate request');
      return;
    }
    
    console.log('🟢 handleQuickAddVisit: Starting submission');
    setIsSubmitting(true);
    try {
      // Use the visit date in YYYY-MM-DD format directly
      const visitData = {
        restaurantId: selectedRestaurantId,
        visitDate: visitDate, // Keep in YYYY-MM-DD format
        userName: visitUserName,
        favoriteItems: visitFavorites,
        dislikes: visitDislikes,
        notes: visitNotes,
        rating: visitRating ? parseFloat(visitRating) : null,
        staffNotes: visitStaffNotes,
        bathroomRating: visitBathroomRating ? parseFloat(visitBathroomRating) : null,
        cost: visitCost ? parseFloat(visitCost) : null,
        guests: visitGuests
      };

      await axios.post(`${API_URL}/api/visits`, visitData);
      
      // Upload photos if any were selected
      if (addVisitPhotos.length > 0) {
        await uploadPhotosToRestaurant(selectedRestaurantId, addVisitPhotos);
      }
      
      // Close dialog immediately for better UX
      setIsQuickAddVisitOpen(false);
      setSelectedRestaurantId('');
      setSelectedRestaurantForVisit(null);
      setAddVisitPhotos([]);
      
      // Refresh data in background (don't await - let it happen async)
      Promise.all([
        fetchRestaurants(),
        fetchRecentVisits(),
        fetchAllUsersRestaurants()
      ]).catch(err => console.error('Error refreshing data:', err));
      
    } catch (error) {
      console.error('Error adding visit:', error);
      if (error.response?.status === 401) {
        alert('Your session has expired. Please refresh the page and log in again.');
      } else {
        alert('Error adding visit. Please try again.');
      }
    } finally {
      console.log('🔴 handleQuickAddVisit: Completed, setting isSubmitting=false');
      setIsSubmitting(false);
    }
  };

  const handleAddVisit = async (e) => {
    e.preventDefault();
    
    console.log('🔵 handleAddVisit called, isSubmitting:', isSubmitting);
    
    if (isSubmitting) {
      console.log('⚠️ Already submitting visit, ignoring duplicate request');
      return;
    }
    
    console.log('🟢 handleAddVisit: Starting submission');
    setIsSubmitting(true);
    try {
      // Create visit record with date in YYYY-MM-DD format
      const visitData = {
        restaurantId: selectedRestaurantForVisit.id,
        visitDate: visitDate, // Keep in YYYY-MM-DD format
        userName: visitUserName.trim(),
        favoriteItems: visitFavorites.trim(),
        dislikes: visitDislikes.trim(),
        notes: visitNotes.trim(),
        rating: visitRating ? parseFloat(visitRating) : null,
        staffNotes: visitStaffNotes.trim(),
        bathroomRating: visitBathroomRating ? parseFloat(visitBathroomRating) : null,
        cost: visitCost ? parseFloat(visitCost) : null,
        guests: visitGuests.trim()
      };

      await axios.post(`${API_URL}/api/visits`, visitData);
      
      // Upload photos if any were selected
      if (addVisitPhotos.length > 0) {
        await uploadPhotosToRestaurant(selectedRestaurantForVisit.id, addVisitPhotos);
      }
      
      // Merge items into restaurant if provided
      const updateData = {};
      
      if (visitFavorites.trim()) {
        const existingFavorites = selectedRestaurantForVisit.favoriteItems || '';
        const existingItems = existingFavorites.split(',').map(item => item.trim()).filter(item => item);
        const newItems = visitFavorites.split(',').map(item => item.trim()).filter(item => item);
        const allItems = [...new Set([...existingItems, ...newItems])];
        updateData.favoriteItems = allItems.join(', ');
      }

      if (visitDislikes.trim()) {
        const existingDislikes = selectedRestaurantForVisit.dislikes || '';
        const existingItems = existingDislikes.split(',').map(item => item.trim()).filter(item => item);
        const newItems = visitDislikes.split(',').map(item => item.trim()).filter(item => item);
        const allItems = [...new Set([...existingItems, ...newItems])];
        updateData.dislikes = allItems.join(', ');
      }

      if (Object.keys(updateData).length > 0) {
        await axios.put(`${API_URL}/api/restaurants/${selectedRestaurantForVisit.id}`, updateData);
      }
      
      // Close dialog and reset form IMMEDIATELY for snappy UX
      setIsVisitDialogOpen(false);
      setSelectedRestaurantForVisit(null);
      setVisitDate('');
      setVisitUserName('');
      setVisitFavorites('');
      setVisitDislikes('');
      setVisitNotes('');
      setVisitRating('');
      setVisitStaffNotes('');
      setVisitBathroomRating('');
      setVisitGuests('');
      setAddVisitPhotos([]);
      setIsSubmitting(false);
      
      // Refresh data in background - fire and forget
      console.log('Refreshing data after visit add...');
      fetchRestaurants();
      fetchAllUsersRestaurants();
      fetchRecentVisits();
      fetchAllRecentVisits();
    } catch (error) {
      console.error('Error adding visit:', error);
      
      // Provide detailed error message
      let errorMessage = 'Error occurred while adding visit\n\n';
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 404) {
          errorMessage += 'Restaurant not found.\n';
          errorMessage += 'Please refresh the page and try again.';
        } else if (status === 400) {
          errorMessage += 'Invalid data:\n';
          errorMessage += data.detail || 'Please check all fields are filled correctly.';
        } else if (status === 401) {
          errorMessage += 'Authentication failed. Your session has expired.\nPlease log out and log in again to continue.';
        } else if (status === 500) {
          errorMessage += 'Server error:\n';
          errorMessage += data.detail || 'An error occurred on the server.';
        } else {
          errorMessage += `Error ${status}: ${data.detail || data.message || 'Unknown error'}`;
        }
      } else if (error.request) {
        errorMessage += 'Cannot connect to server.\n';
        errorMessage += 'Please check your internet connection.';
      } else {
        errorMessage += error.message || 'An unexpected error occurred.';
      }
      
      alert(errorMessage);
    } finally {
      console.log('🔴 handleAddVisit: Completed, setting isSubmitting=false');
      setIsSubmitting(false);
    }
  };

  // Compress image before upload
  const compressImage = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Max dimensions - reduce large photos
          const MAX_WIDTH = 1920;
          const MAX_HEIGHT = 1920;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              height = height * (MAX_WIDTH / width);
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = width * (MAX_HEIGHT / height);
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
          }, 'image/jpeg', 0.85); // 85% quality
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Handle photo selection for Add Restaurant form
  const handleAddRestaurantPhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    console.log(`📷 Selected ${files.length} new photos, compressing...`);
    
    // Compress photos in parallel
    const compressedFiles = await Promise.all(files.map(f => compressImage(f)));
    console.log(`✅ Compressed ${compressedFiles.length} photos`);
    
    // Add to existing photos (not replace)
    setAddRestaurantPhotos(prev => [...prev, ...compressedFiles]);
    
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Handle photo selection for Add Visit form
  const handleAddVisitPhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    console.log(`📷 Selected ${files.length} new photos, compressing...`);
    
    // Compress photos in parallel
    const compressedFiles = await Promise.all(files.map(f => compressImage(f)));
    console.log(`✅ Compressed ${compressedFiles.length} photos`);
    
    // Add to existing photos (not replace)
    setAddVisitPhotos(prev => [...prev, ...compressedFiles]);
    
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Upload photos to a restaurant
  const uploadPhotosToRestaurant = async (restaurantId, photos) => {
    if (!photos || photos.length === 0) return;
    
    setUploadingPhoto(true);
    setUploadProgress({ current: 0, total: photos.length });
    
    try {
      console.log(`📤 Uploading ${photos.length} photos...`);
      
      // Upload photos with individual error handling
      const uploadResults = await Promise.allSettled(
        photos.map(async (photo, index) => {
          const formData = new FormData();
          formData.append('file', photo);
          
          try {
            const response = await axios.post(
              `${API_URL}/api/restaurants/${restaurantId}/photos`,
              formData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
            
            setUploadProgress(prev => ({ ...prev, current: prev.current + 1 }));
            console.log(`✅ Uploaded photo ${index + 1}/${photos.length}:`, response.data);
            return { success: true, index };
          } catch (err) {
            console.error(`❌ Failed to upload photo ${index + 1}:`, err.response?.data || err.message);
            setUploadProgress(prev => ({ ...prev, current: prev.current + 1 }));
            return { success: false, index, error: err.response?.data?.detail || err.message };
          }
        })
      );
      
      const failed = uploadResults.filter(r => r.status === 'rejected' || !r.value?.success);
      const succeeded = uploadResults.filter(r => r.status === 'fulfilled' && r.value?.success);
      
      console.log(`✅ ${succeeded.length}/${photos.length} photos uploaded successfully`);
      
      if (failed.length > 0) {
        console.error(`❌ ${failed.length} photos failed to upload`);
        // Don't throw error - just log it. The restaurant/visit was created successfully.
      }
    } catch (error) {
      console.error('Error uploading photos:', error);
      // Don't alert - the creation was successful
    } finally {
      setUploadingPhoto(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  // Geocode location to get coordinates
  const geocodeLocation = async (locationString) => {
    if (!locationString || locationString.trim() === '') {
      return null;
    }

    try {
      // Using OpenStreetMap Nominatim API with better accuracy parameters
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationString)}&limit=1&addressdetails=1&countrycodes=us`,
        {
          headers: {
            'User-Agent': 'Dine-A-Saurus-Restaurant-App'
          }
        }
      );
      
      if (!response.ok) {
        console.error('Geocoding service error:', response.status);
        return null;
      }
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        console.log('Geocoded:', locationString, '→', data[0].display_name);
        return {
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon)
        };
      }
      
      console.log('No geocoding results for:', locationString);
      return null;
    } catch (error) {
      console.error('Error geocoding location:', error);
      return null;
    }
  };

  // Auto-geocode when location field changes
  const handleLocationChange = async (e) => {
    const newLocation = e.target.value;
    setFormData({...formData, location: newLocation});
    
    // Debounce geocoding - only geocode if user stops typing for 1 second
    if (window.geocodeTimeout) {
      clearTimeout(window.geocodeTimeout);
    }
    
    window.geocodeTimeout = setTimeout(async () => {
      if (newLocation && newLocation.trim().length > 5) {
        // Use fullAddress if available, otherwise use location
        const addressToGeocode = formData.fullAddress || newLocation;
        const coords = await geocodeLocation(addressToGeocode);
        if (coords) {
          setFormData(prev => ({
            ...prev,
            latitude: coords.latitude.toFixed(6),
            longitude: coords.longitude.toFixed(6)
          }));
          console.log('Auto-geocoded:', addressToGeocode, '→', coords);
        }
      }
    }, 1500); // Wait 1.5 seconds after user stops typing
  };

  const handleFullAddressChange = async (e) => {
    const newFullAddress = e.target.value;
    setFormData({...formData, fullAddress: newFullAddress});
    
    // Debounce geocoding
    if (window.fullAddressGeocodeTimeout) {
      clearTimeout(window.fullAddressGeocodeTimeout);
    }
    
    window.fullAddressGeocodeTimeout = setTimeout(async () => {
      if (newFullAddress && newFullAddress.trim().length > 5) {
        const coords = await geocodeLocation(newFullAddress);
        if (coords) {
          // Also get reverse geocoding to extract city and state
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.latitude}&lon=${coords.longitude}`
            );
            const data = await response.json();
            
            if (data && data.address) {
              // State name to 2-letter code mapping
              const stateCodeMap = {
                'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
                'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
                'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
                'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
                'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
                'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
                'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
                'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
                'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
                'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
                'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
                'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
                'Wisconsin': 'WI', 'Wyoming': 'WY'
              };
              
              const address = data.address;
              const city = address.city || address.town || address.village || '';
              const stateName = address.state || '';
              
              // Try to match state name (case-insensitive)
              let stateCode = stateCodeMap[stateName];
              if (!stateCode) {
                // Try case-insensitive match
                const stateEntry = Object.entries(stateCodeMap).find(
                  ([key]) => key.toLowerCase() === stateName.toLowerCase()
                );
                stateCode = stateEntry ? stateEntry[1] : stateName;
              }
              const locationFormatted = [city, stateCode].filter(Boolean).join(', ');
              
              const newLat = parseFloat(coords.latitude.toFixed(6));
              const newLon = parseFloat(coords.longitude.toFixed(6));
              
              console.log('Extracted location info:', {
                city: city,
                stateName: stateName,
                stateCode: stateCode,
                locationFormatted: locationFormatted,
                addressObject: address
              });
              
              setFormData(prev => {
                const updated = {
                  ...prev,
                  latitude: newLat,
                  longitude: newLon,
                  location: locationFormatted
                };
                console.log('Updated formData:', updated);
                return updated;
              });
              
              console.log('Auto-geocoded from full address:', newFullAddress, '→', coords, '→ Location:', locationFormatted);
            }
          } catch (error) {
            console.error('Error getting location from coordinates:', error);
            // Still set coordinates even if reverse geocoding fails
            const newLat = parseFloat(coords.latitude.toFixed(6));
            const newLon = parseFloat(coords.longitude.toFixed(6));
            
            setFormData(prev => ({
              ...prev,
              latitude: newLat,
              longitude: newLon
            }));
            
            console.log('Updated coordinates (reverse geocoding failed):', { latitude: newLat, longitude: newLon });
          }
        }
      }
    }, 1500);
  };


  // Helper to save current tab before opening a dialog
  const saveCurrentTab = () => {
    setPreviousTab(activeTab);
  };

  // Helper to restore previous tab when closing a dialog
  const restorePreviousTab = () => {
    if (previousTab) {
      setActiveTab(previousTab);
      setPreviousTab(null);
    }
  };

  const showVisitHistory = async (restaurant) => {
    saveCurrentTab(); // Save current tab before opening dialog
    // Calculate distance if user location is available and restaurant has coordinates
    let restaurantWithDistance = { ...restaurant };
    
    if (userLocation && userLocation.latitude && userLocation.longitude &&
        restaurant.latitude && restaurant.longitude) {
      // Calculate distance using Haversine formula
      const R = 3959; // Earth's radius in miles
      const lat1 = userLocation.latitude * Math.PI / 180;
      const lat2 = restaurant.latitude * Math.PI / 180;
      const deltaLat = (restaurant.latitude - userLocation.latitude) * Math.PI / 180;
      const deltaLon = (restaurant.longitude - userLocation.longitude) * Math.PI / 180;
      
      const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
                Math.cos(lat1) * Math.cos(lat2) *
                Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;
      
      restaurantWithDistance.distance = distance;
    }
    
    setSelectedRestaurantHistory(restaurantWithDistance);
    try {
      const response = await axios.get(`${API_URL}/api/visits/${restaurant.id}`);
      const visits = response.data;
      setVisitHistory(visits);
      
      // Combine all photos from restaurant and all visits
      const allPhotos = [...(restaurant.photos || [])];
      
      // Note: Currently visits don't have photos stored with them in the backend
      // Photos are only at restaurant level. If this changes in the future,
      // we would add: visits.forEach(visit => allPhotos.push(...(visit.photos || [])));
      
      // Update restaurant with combined photos
      setSelectedRestaurantHistory({
        ...restaurantWithDistance,
        photos: allPhotos,
        visitCount: visits.length
      });
      
      setIsVisitHistoryOpen(true);
    } catch (error) {
      console.error('Error fetching visit history:', error);
      setVisitHistory([]);
      setIsVisitHistoryOpen(true);
    }
  };

  const navigateToPreviousRestaurant = async () => {
    if (!selectedRestaurantHistory) return;
    
    // Get the current list of restaurants being displayed based on active tab
    let currentList = [];
    if (activeTab === 'dashboard' || activeTab === 'allDashboard') {
      // On dashboard, combine restaurants from both Nearest and Recent Visits tables
      const nearbyList = nearbyRestaurants || [];
      const recentList = (recentVisits || []).map(visit => 
        allRestaurantsList.find(r => r.id === visit.restaurantId) || {
          id: visit.restaurantId,
          name: visit.restaurantName,
          location: visit.restaurantLocation
        }
      ).filter(r => r && r.id);
      
      // Combine and deduplicate by restaurant ID
      const combined = [...nearbyList, ...recentList];
      const uniqueIds = new Set();
      currentList = combined.filter(r => {
        if (uniqueIds.has(r.id)) return false;
        uniqueIds.add(r.id);
        return true;
      });
    } else {
      // On My Restaurants tab or All Restaurants tab
      currentList = filteredRestaurants;
    }
    
    const currentIndex = currentList.findIndex(r => r.id === selectedRestaurantHistory.id);
    
    if (currentIndex > 0) {
      const previousRestaurant = currentList[currentIndex - 1];
      await showVisitHistory(previousRestaurant);
    }
  };

  const navigateToNextRestaurant = async () => {
    if (!selectedRestaurantHistory) return;
    
    // Get the current list of restaurants being displayed based on active tab
    let currentList = [];
    if (activeTab === 'dashboard' || activeTab === 'allDashboard') {
      // On dashboard, combine restaurants from both Nearest and Recent Visits tables
      const nearbyList = nearbyRestaurants || [];
      const recentList = (recentVisits || []).map(visit => 
        allRestaurantsList.find(r => r.id === visit.restaurantId) || {
          id: visit.restaurantId,
          name: visit.restaurantName,
          location: visit.restaurantLocation
        }
      ).filter(r => r && r.id);
      
      // Combine and deduplicate by restaurant ID
      const combined = [...nearbyList, ...recentList];
      const uniqueIds = new Set();
      currentList = combined.filter(r => {
        if (uniqueIds.has(r.id)) return false;
        uniqueIds.add(r.id);
        return true;
      });
    } else {
      // On My Restaurants tab or All Restaurants tab
      currentList = filteredRestaurants;
    }
    
    const currentIndex = currentList.findIndex(r => r.id === selectedRestaurantHistory.id);
    
    if (currentIndex < currentList.length - 1) {
      const nextRestaurant = currentList[currentIndex + 1];
      await showVisitHistory(nextRestaurant);
    }
  };

  const handleDeleteVisit = async (visitId) => {
    if (window.confirm('Are you sure you want to delete this visit?')) {
      try {
        await axios.delete(`${API_URL}/api/visits/${visitId}`);
        
        // Refresh visit history immediately
        const response = await axios.get(`${API_URL}/api/visits/${selectedRestaurantHistory.id}`);
        setVisitHistory(response.data);
        
        // Light background refresh - don't block UI
        fetchRestaurants();
        fetchRecentVisits();
        fetchAllRecentVisits();
      } catch (error) {
        console.error('Error deleting visit:', error);
      }
    }
  };

  const openEditVisitDialog = (visit) => {
    setSelectedVisitForEdit(visit);
    
    // Convert "Oct 2025" to "2025-10-01" for date input
    // Parse the date string and create a valid date format
    let dateValue = visit.visitDate;
    if (visit.visitDate && !visit.visitDate.includes('-')) {
      // If date is in "Oct 2025" format, convert to YYYY-MM-DD
      try {
        const date = new Date(visit.visitDate);
        if (!isNaN(date.getTime())) {
          dateValue = date.toISOString().split('T')[0];
        }
      } catch (e) {
        // If conversion fails, use current date
        dateValue = getLocalDateString();
      }
    }
    
    setVisitDate(dateValue);
    setVisitUserName(visit.userName || '');
    setVisitFavorites(visit.favoriteItems || '');
    setVisitDislikes(visit.dislikes || '');
    setVisitNotes(visit.notes || '');
    setVisitRating(visit.rating || '');
    setVisitStaffNotes(visit.staffNotes || '');
    setVisitBathroomRating(visit.bathroomRating || '');
    setVisitGuests(visit.guests || '');
    saveCurrentTab();
    setIsEditVisitOpen(true);
  };

  const handleUpdateVisit = async (e) => {
    e.preventDefault();
    try {
      // Keep the date in YYYY-MM-DD format
      const visitData = {
        visitDate: visitDate,
        favoriteItems: visitFavorites,
        dislikes: visitDislikes,
        notes: visitNotes,
        rating: visitRating ? parseFloat(visitRating) : null,
        staffNotes: visitStaffNotes,
        bathroomRating: visitBathroomRating ? parseFloat(visitBathroomRating) : null,
        guests: visitGuests
      };

      console.log('Updating visit:', visitData);
      await axios.put(`${API_URL}/api/visits/${selectedVisitForEdit.id}`, visitData);
      
      setIsEditVisitOpen(false);
      setSelectedVisitForEdit(null);
      
      // Refresh visit history for this restaurant immediately
      const response = await axios.get(`${API_URL}/api/visits/${selectedRestaurantHistory.id}`);
      setVisitHistory(response.data);
      
      // Light background refresh - don't block UI
      fetchRestaurants();
      fetchRecentVisits();
      fetchAllRecentVisits();
    } catch (error) {
      console.error('Error updating visit:', error);
      alert('❌ Error updating visit. Please try again.');
    }
  };

  const showLatestVisits = async () => {
    console.log('showLatestVisits called');
    
    // Toggle off if already showing
    if (showLatestVisitsView) {
      setShowLatestVisitsView(false);
      setShowNearest(false);
      setSearchQuery('');
      console.log('Toggled off showLatestVisitsView');
      return;
    }
    
    try {
      // Get user's restaurants with their visit data
      // The restaurants already have lastVisit date from the API
      const currentRestaurants = [...restaurants];
      
      // Filter to only restaurants that have been visited (have lastVisit)
      const visitedRestaurants = currentRestaurants.filter(r => r.lastVisit);
      
      // Sort by most recent visit date
      const sortedByLastVisit = visitedRestaurants.sort((a, b) => {
        const dateA = new Date(a.lastVisit);
        const dateB = new Date(b.lastVisit);
        return dateB - dateA; // Descending order (newest first)
      });
      
      // Set filtered restaurants to show in the same format as Find Nearest
      setFilteredRestaurants(sortedByLastVisit);
      setShowLatestVisitsView(true);
      setShowNearest(false); // Clear nearest filter
      setSearchQuery(''); // Clear search
      
      console.log('Showing', sortedByLastVisit.length, 'restaurants sorted by latest visit');
    } catch (error) {
      console.error('Error showing latest visits:', error);
    }
  };

  const clearSearchHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('searchHistory');
  };

  const startVoiceSearch = () => {
    // Check if browser supports Web Speech API
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      alert('Voice search is not supported in your browser. Please try Chrome, Edge, or Safari.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    setIsListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setSearchQuery(transcript);
      setIsListening(false);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'no-speech') {
        alert('No speech detected. Please try again.');
      } else if (event.error === 'not-allowed') {
        alert('Microphone access denied. Please enable microphone permissions.');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  const startVoiceTryThis = () => {
    // Check if browser supports Web Speech API
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      alert('Voice input is not supported in your browser. Please try Chrome, Edge, or Safari.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    setIsListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setTryThisRestaurantName(transcript);
      setIsListening(false);
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'no-speech') {
        alert('No speech detected. Please try again.');
      } else if (event.error === 'not-allowed') {
        alert('Microphone access denied. Please enable microphone permissions.');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  const fetchOnDeckRestaurants = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/on-deck`);
      setOnDeckRestaurants(response.data);
    } catch (error) {
      console.error('Error fetching on-deck restaurants:', error);
    }
  };

  const handleAddToOnDeck = async () => {
    if (!tryThisRestaurantName.trim()) {
      alert('Please enter a restaurant name');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Use current user location (captured from geolocation)
      const latitude = userLocation?.latitude || null;
      const longitude = userLocation?.longitude || null;
      
      let fullAddress = null;
      let website = null;
      let phone = null;
      let rating = null;
      let priceLevel = null;
      let placeId = null;
      
      // Try to find restaurant details using Google Places API
      if (latitude && longitude) {
        try {
          // Call backend to search Google Places securely
          const searchResponse = await axios.post(`${API_URL}/api/on-deck/search-place`, null, {
            params: {
              name: tryThisRestaurantName.trim(),
              latitude,
              longitude
            }
          });
          
          if (searchResponse.data.found) {
            const placeData = searchResponse.data;
            website = placeData.website;
            phone = placeData.phone;
            fullAddress = placeData.address;
            rating = placeData.rating;
            priceLevel = placeData.priceLevel;
            placeId = placeData.placeId;
            
            console.log('✅ Found restaurant details:', { website, phone, rating, fullAddress });
          } else {
            console.log('⚠️ Restaurant not found in Google Places, using basic info');
            // Fallback to reverse geocoding for address
            try {
              const reverseGeocode = await axios.get(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
              );
              if (reverseGeocode.data && reverseGeocode.data.display_name) {
                fullAddress = reverseGeocode.data.display_name;
              }
            } catch (geocodeError) {
              console.warn('Reverse geocoding failed:', geocodeError);
            }
          }
        } catch (placesError) {
          console.warn('Google Places search failed:', placesError);
          // Fallback to reverse geocoding
          try {
            const reverseGeocode = await axios.get(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            if (reverseGeocode.data && reverseGeocode.data.display_name) {
              fullAddress = reverseGeocode.data.display_name;
            }
          } catch (geocodeError) {
            console.warn('Reverse geocoding also failed:', geocodeError);
          }
        }
      }
      
      // Add to on-deck list with all collected information
      await axios.post(`${API_URL}/api/on-deck`, {
        name: tryThisRestaurantName.trim(),
        latitude,
        longitude,
        fullAddress,
        website,
        phone,
        rating,
        priceLevel,
        placeId,
        user_email: userEmail
      });

      // Refresh the list
      await fetchOnDeckRestaurants();
      
      // Close dialog and reset
      setIsTryThisDialogOpen(false);
      setTryThisRestaurantName('');
      
      let infoMessage = '';
      if (website || phone || rating) {
        infoMessage = ' ✅ Details found!';
      } else if (latitude && longitude) {
        infoMessage = ' 📍 Location captured';
      } else {
        infoMessage = ' (Added without location)';
      }
      alert(`"${tryThisRestaurantName.trim()}" added to On Deck!${infoMessage}`);
    } catch (error) {
      console.error('Error adding to on-deck:', error);
      alert('Error adding restaurant to On Deck. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditOnDeck = (restaurant) => {
    setSelectedOnDeckRestaurant(restaurant);
    setEditOnDeckName(restaurant.name);
    setEditOnDeckAddress(restaurant.fullAddress || '');
    setIsEditOnDeckOpen(true);
  };

  const handleSaveOnDeckEdit = async () => {
    if (!editOnDeckName.trim()) {
      alert('Restaurant name is required');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Update the restaurant
      await axios.put(`${API_URL}/api/on-deck/${selectedOnDeckRestaurant.id}`, {
        name: editOnDeckName.trim(),
        fullAddress: editOnDeckAddress.trim() || null
      });

      // Refresh the list
      await fetchOnDeckRestaurants();
      
      // Close dialog
      setIsEditOnDeckOpen(false);
      setSelectedOnDeckRestaurant(null);
      setEditOnDeckName('');
      setEditOnDeckAddress('');
      
      alert('Restaurant updated successfully!');
    } catch (error) {
      console.error('Error updating on-deck restaurant:', error);
      alert('Error updating restaurant. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteOnDeck = async (id) => {
    if (!window.confirm('Remove this restaurant from On Deck?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/on-deck/${id}`);
      await fetchOnDeckRestaurants();
    } catch (error) {
      console.error('Error deleting on-deck restaurant:', error);
      alert('Error removing restaurant. Please try again.');
    }
  };

  const handleRandomPick = () => {
    if (restaurants.length === 0) {
      alert('No restaurants available in My Restaurants');
      return;
    }
    
    // Check if user location is available
    if (!userLocation || !userLocation.latitude || !userLocation.longitude) {
      alert('Unable to determine your location. Please enable location services and try "Find Nearest" first.');
      return;
    }
    
    // Filter restaurants within 30 miles and add distance property
    const nearbyRestaurants = restaurants
      .map(restaurant => {
        if (!restaurant.latitude || !restaurant.longitude) {
          return null; // Skip restaurants without coordinates
        }
        
        // Calculate distance using Haversine formula
        const R = 3959; // Earth's radius in miles
        const lat1 = userLocation.latitude * Math.PI / 180;
        const lat2 = restaurant.latitude * Math.PI / 180;
        const deltaLat = (restaurant.latitude - userLocation.latitude) * Math.PI / 180;
        const deltaLon = (restaurant.longitude - userLocation.longitude) * Math.PI / 180;
        
        const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
                  Math.cos(lat1) * Math.cos(lat2) *
                  Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;
        
        // Add distance to restaurant object
        return {
          ...restaurant,
          distance: distance
        };
      })
      .filter(restaurant => restaurant !== null && restaurant.distance <= 30);
    
    if (nearbyRestaurants.length === 0) {
      alert('No restaurants found within 30 miles of your current location. Try adding more restaurants or expanding your search area.');
      return;
    }
    
    // Pick a random restaurant from nearby restaurants
    const randomIndex = Math.floor(Math.random() * nearbyRestaurants.length);
    const randomRestaurant = nearbyRestaurants[randomIndex];
    
    // Show the visit history dialog for the random restaurant (now with distance property)
    setIsFromRandomPick(true);
    showVisitHistory(randomRestaurant);
  };

  const applyHistorySearch = (term) => {
    setSearchQuery(term);
  };

  const openPhotosDialog = async (restaurant) => {
    setSelectedRestaurantPhotos(restaurant);
    try {
      const response = await axios.get(`${API_URL}/api/restaurants/${restaurant.id}/photos`);
      setRestaurantPhotos(response.data.photos || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
      setRestaurantPhotos([]);
    }
    saveCurrentTab();
    setIsPhotosOpen(true);
  };

  const openRestaurantDetailsDialog = (restaurant) => {
    setSelectedRestaurantDetails(restaurant);
    setIsRestaurantDetailsOpen(true);
  };

  const handlePhotoUpload = async (event, restaurantId) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API_URL}/api/restaurants/${restaurantId}/photos`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Refresh photos
      const photosResponse = await axios.get(`${API_URL}/api/restaurants/${restaurantId}/photos`);
      setRestaurantPhotos(photosResponse.data.photos || []);
      
      // Refresh restaurant data if viewing history
      if (selectedRestaurantHistory && selectedRestaurantHistory.id === restaurantId) {
        const restaurantResponse = await axios.get(`${API_URL}/api/restaurants/${restaurantId}`);
        setSelectedRestaurantHistory({
          ...restaurantResponse.data,
          photos: photosResponse.data.photos || []
        });
      }
      
      // Refresh main restaurant list
      await fetchRestaurants();
      
      // Refresh photo montage
      await fetchPhotoMontage(activeTab === 'allDashboard');
      
      alert('Photo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Error uploading photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/api/restaurants/${selectedRestaurantPhotos.id}/photos`,
        {
          params: { photo_id: photoId }
        }
      );

      // Refresh photos
      const photosResponse = await axios.get(
        `${API_URL}/api/restaurants/${selectedRestaurantPhotos.id}/photos`
      );
      setRestaurantPhotos(photosResponse.data.photos || []);
      
      // Refresh restaurant data if viewing history
      if (selectedRestaurantHistory && selectedRestaurantHistory.id === selectedRestaurantPhotos.id) {
        const restaurantResponse = await axios.get(`${API_URL}/api/restaurants/${selectedRestaurantPhotos.id}`);
        setSelectedRestaurantHistory({
          ...restaurantResponse.data,
          photos: photosResponse.data.photos || []
        });
      }
      
      // Refresh main restaurant list
      await fetchRestaurants();
      
      // Refresh photo montage
      await fetchPhotoMontage(activeTab === 'allDashboard');
      
      alert('Photo deleted successfully!');
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Error deleting photo. Please try again.');
    }
  };


  const handleRotatePhoto = async (restaurantId, photoId, e) => {
    // Stop propagation to prevent clicking through to visit history
    if (e) {
      e.stopPropagation();
    }

    try {
      const response = await axios.patch(
        `${API_URL}/api/restaurants/${restaurantId}/photos/${photoId}/rotate`
      );

      // Refresh photo montage to show updated rotation
      await fetchPhotoMontage(activeTab === 'allDashboard');
      
      // If viewing restaurant photos, refresh those too
      if (selectedRestaurantPhotos && selectedRestaurantPhotos.id === restaurantId) {
        const photosResponse = await axios.get(
          `${API_URL}/api/restaurants/${restaurantId}/photos`
        );
        setRestaurantPhotos(photosResponse.data.photos || []);
      }
      
      console.log('Photo rotated successfully');
    } catch (error) {
      console.error('Error rotating photo:', error);
      alert('Error rotating photo. Please try again.');
    }
  };


  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  // Show User Profile page when active
  if (showProfilePage) {
    return (
      <UserProfile
        authToken={authToken}
        userEmail={userEmail}
        onBack={() => setShowProfilePage(false)}
        onProfileUpdate={(update) => {
          if (update?.display_name) setDisplayName(update.display_name);
          if (update?.email) setUserEmail(update.email);
        }}
      />
    );
  }

  return (
    <div className="app-container">
      {/* Preview Environment Banner */}
      {isPreviewEnvironment && (
        <div className="preview-banner">
          <span className="preview-badge">🧪 PREVIEW</span>
          <span className="preview-text">This is the preview environment - not production</span>
        </div>
      )}
      
      <header className="header">
        <div className="header-content">
          <div className="header-main">
            {showAdminPanel ? (
              <>
                <div className="logo-container-horizontal">
                  <div className="logo-container-vertical">
                    <h1 className="logo" data-testid="app-title">Dine-A-Saurus</h1>
                    <div className="logo-line-two">
                      <span className="logo-dino">🦖</span>
                      <p className="logo-subtitle">Admin Panel</p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="logo-container-horizontal">
                  <div className="logo-container-vertical">
                    <h1 className="logo" data-testid="app-title">Dine-A-Saurus</h1>
                    <div className="logo-line-two">
                      <span className="logo-dino">🦖</span>
                      <p className="logo-tagline">Tracking your dining adventures!</p>
                    </div>
                  </div>
                  <div className="logo-footprints">
                    <span className="dino-track">
                      <span className="toe toe-left"></span>
                      <span className="toe toe-center"></span>
                      <span className="toe toe-right"></span>
                    </span>
                    <span className="dino-track track-offset">
                      <span className="toe toe-left"></span>
                      <span className="toe toe-center"></span>
                      <span className="toe toe-right"></span>
                    </span>
                    <span className="dino-track">
                      <span className="toe toe-left"></span>
                      <span className="toe toe-center"></span>
                      <span className="toe toe-right"></span>
                    </span>
                    <span className="dino-track track-offset">
                      <span className="toe toe-left"></span>
                      <span className="toe toe-center"></span>
                      <span className="toe toe-right"></span>
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="header-user">
            {isAdmin && (
              <Button
                onClick={async () => {
                  setShowAdminPanel(!showAdminPanel);
                  // If toggling to Dashboard view and user is admin, set to All Dashboard and fetch data
                  if (showAdminPanel && isAdmin) {
                    setActiveTab('allDashboard');
                    // Fetch all dashboard data
                    await fetchAllRecentVisits();
                    await fetchPhotoMontage(true); // Fetch all users' photos
                    if (userLocation) {
                      await fetchAllNearbyRestaurants();
                    } else {
                      getUserLocationAndNearby();
                    }
                  }
                }}
                className="admin-btn"
                variant="outline"
                size="sm"
              >
                <Shield size={16} />
                {showAdminPanel ? 'Dashboard' : 'Admin Panel'}
              </Button>
            )}
            <Button
              onClick={() => setShowProfilePage(true)}
              className="profile-btn"
              variant="outline"
              size="sm"
              data-testid="header-profile-btn"
              title="View My Profile"
            >
              <User size={16} />
              My Profile
            </Button>
            <div 
              className="user-info-display clickable-user-info"
              onClick={() => setShowSettingsDialog(true)}
              title="Click to open account settings"
            >
              <User size={18} className="user-icon" />
              <div className="user-details">
                <span className="user-display-name">{displayName}</span>
              </div>
              <Settings size={16} className="settings-icon" />
            </div>
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Logout button clicked!');
                handleLogout();
              }}
              data-testid="logout-button"
              className="logout-btn"
              variant="outline"
              size="sm"
            >
              <LogOut size={16} />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {viewingAsUser && (
        <div className="viewing-as-banner">
          <div className="viewing-as-content">
            <span className="viewing-as-text">
              👁️ Viewing as: <strong>{viewingAsUser.display_name}</strong> ({viewingAsUser.email})
            </span>
            <Button
              onClick={handleReturnToAdmin}
              className="return-to-admin-btn"
              variant="outline"
              size="sm"
            >
              Return to Admin
            </Button>
          </div>
        </div>
      )}

      {!showAdminPanel && (
        <div className="tabs-container">
          <div className="tabs">
          {/* My Dashboard - visible to all users */}
            <button 
              className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={async () => {
                setActiveTab('dashboard');
                setAllRestaurantsLoaded(false);
                setShowNearest(false);
                setFilteredRestaurants([]);
                setSearchQuery('');
                clearAllFilters();
                
                // Refresh data for My Dashboard
                await fetchRestaurants();
                await fetchRecentVisits();
                await fetchPhotoMontage();
              }}
              data-testid="tab-my-dashboard"
            >
              My Dashboard
            </button>
            {/* All Dashboard - hide on mobile, always visible on desktop */}
            <button 
              className={`tab tab-all-dashboard ${activeTab === 'allDashboard' ? 'active' : ''}`}
              onClick={async () => {
                setActiveTab('allDashboard');
                setAllRestaurantsLoaded(false);
                setShowNearest(false);
                setFilteredRestaurants([]);
                setSearchQuery('');
                clearAllFilters();
                
                // Fetch all dashboard data
                await fetchAllRecentVisits();
                await fetchPhotoMontage(true); // Fetch all users' photos
                if (userLocation) {
                  await fetchAllNearbyRestaurants();
                } else {
                  // Try to get location
                  getUserLocationAndNearby();
                }
              }}
              data-testid="tab-all-dashboard"
            >
              Shared Dashboard <span className="tab-count">({allUsersRestaurants.length})</span>
            </button>
          <button
            className={`tab ${activeTab === 'activity' ? 'active' : ''}`}
            onClick={() => setActiveTab('activity')}
            data-testid="tab-activity"
          >
            Activity
          </button>
          <button
            className={`tab ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => setActiveTab('about')}
            data-testid="tab-about"
          >
            About Us
          </button>
          <button
            className={`tab ${activeTab === 'onDeck' ? 'active' : ''}`}
            onClick={() => setActiveTab('onDeck')}
            data-testid="tab-on-deck"
          >
            On Deck <span className="tab-count">({onDeckRestaurants.length})</span>
          </button>
        </div>
        
        {/* Quick Access Buttons - show on Dashboard tabs */}
        {(activeTab === 'dashboard' || activeTab === 'allDashboard') && (
          <div className="quick-access-buttons" style={{ 
            display: 'flex', 
            gap: '0.5rem',
            marginLeft: 'auto',
            alignItems: 'center'
          }}>
            <button
              onClick={() => setActiveTab('allRestaurants')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.5rem 0.8rem',
                backgroundColor: '#2c5f2d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              🍽️ My Restaurants ({allRestaurantsList.length})
            </button>
            <button
              onClick={() => {
                setActiveTab('allUsersRestaurants');
                fetchAllUsersRestaurants();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                padding: '0.5rem 0.8rem',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              🌐 Shared Restaurants ({allUsersRestaurants.length})
            </button>
          </div>
        )}
        
        <div className="header-action-buttons">
          <Button
            data-testid="smart-check-in-btn"
            onClick={handleSmartCheckIn}
            disabled={loading}
            className="header-action-btn check-in-btn"
          >
            <MapPin size={14} />
            <span className="btn-label-desktop">{loading ? 'Locating...' : 'Check In'}</span>
            <span className="btn-label-mobile">{loading ? 'Locating...' : 'Check In'}</span>
          </Button>

          <Button
            data-testid="eat-here-btn"
            onClick={handleRandomPick}
            disabled={restaurants.length === 0}
            className="header-action-btn eat-here-btn"
          >
            <Shuffle size={14} />
            <span className="btn-text-full">Random Pick</span>
            <span className="btn-text-short">Random</span>
          </Button>

          <Button
            data-testid="search-btn"
            onClick={() => setIsSearchDialogOpen(true)}
            className="header-action-btn search-btn"
          >
            <Search size={14} />
            Search
          </Button>

          <Button
            data-testid="mystery-btn"
            onClick={() => setIsTryThisDialogOpen(true)}
            className="header-action-btn mystery-btn"
          >
            <span className="btn-text-full">Try This!</span>
            <span className="btn-text-short">Try!</span>
          </Button>
        </div>
        </div>
      )}

      <div className={`main-content ${['activity', 'about', 'onDeck'].includes(activeTab) ? 'main-content--collapsed' : ''}`}>
        {/* Merge mode banner */}
        {mergePrimary && (
          <div
            data-testid="merge-mode-banner"
            style={{
              padding: '10px 16px',
              margin: '8px 0',
              background: '#fef9c3',
              border: '2px solid #ca8a04',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              fontSize: '0.9rem'
            }}
          >
            <span>
              🔀 <strong>Merge mode:</strong> primary is <strong>"{mergePrimary.name}"</strong>.
              Click another restaurant's <strong>🔀</strong> button to merge it INTO this one.
            </span>
            <button
              type="button"
              data-testid="merge-cancel-btn"
              onClick={() => setMergePrimary(null)}
              style={{
                padding: '4px 12px',
                background: '#64748b',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.85rem'
              }}
            >
              Cancel
            </button>
          </div>
        )}
        {/* Latest Visits View - Shows restaurants sorted by most recent visit */}
        {showLatestVisitsView && (searchQuery || showNearest || hasActiveFilters() || showLatestVisitsView) && (
          <>
            <div className="nearest-banner" data-testid="latest-visits-banner">
              <Clock size={18} />
              Showing restaurants sorted by most recent visit
              <button 
                onClick={() => {
                  setShowLatestVisitsView(false);
                  setSearchQuery('');
                }}
                className="close-banner-btn"
                title="Close"
              >
                <X size={16} />
              </button>
            </div>
            
            <div className="search-results">
              {filteredRestaurants.length === 0 ? (
                <p className="no-results">No restaurants with visits found.</p>
              ) : (
                <table className="restaurants-table" data-testid="restaurants-table">
                  <thead>
                    <tr>
                      <th className="restaurant-name-col">Restaurant</th>
                      <th>Last Visit</th>
                      <th>Cuisine</th>
                      <th>Price</th>
                      <th>Days Ago</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRestaurants.map((restaurant) => (
                      <tr 
                        key={restaurant.id} 
                        className="restaurant-row"
                        data-testid={`restaurant-row-${restaurant.id}`}
                      >
                        <td 
                          className="restaurant-name-cell clickable"
                          onClick={() => showVisitHistory(restaurant)}
                          data-testid={`name-${restaurant.id}`}
                        >
                          <strong>{restaurant.name}</strong>
                        </td>
                        <td>{restaurant.lastVisit ? formatDateDisplay(restaurant.lastVisit) : '–'}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', gap: '4px' }}>
                            {(() => {
                              const icon = getCuisineIcon(restaurant.ethnicity);
                              if (icon?.type === 'emoji') {
                                return <>{icon.value}</>;
                              } else if (icon?.type === 'flag') {
                                return (
                                  <img 
                                    src={`https://flagcdn.com/16x12/${icon.code}.png`}
                                    srcSet={`https://flagcdn.com/32x24/${icon.code}.png 2x, https://flagcdn.com/48x36/${icon.code}.png 3x`}
                                    width="16"
                                    height="12"
                                    alt={restaurant.ethnicity}
                                    style={{ flexShrink: 0 }}
                                  />
                                );
                              }
                              return restaurant.ethnicity || '–';
                            })()}
                          </div>
                        </td>
                        <td>{formatPriceToDollars(restaurant.price)}</td>
                        <td>{formatDaysBack(restaurant.lastVisit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
        
        {showAdminPanel ? (
          <div className="admin-panel-container">
            {/* Admin Tab Navigation */}
            <div className="admin-tabs">
              <button 
                className={`admin-tab ${adminTab === 'analytics' ? 'active' : ''}`}
                onClick={() => setAdminTab('analytics')}
              >
                📊 Analytics
              </button>
              <button 
                className={`admin-tab ${adminTab === 'users' ? 'active' : ''}`}
                onClick={() => setAdminTab('users')}
              >
                👥 Users
              </button>
              <button 
                className={`admin-tab ${adminTab === 'communications' ? 'active' : ''}`}
                onClick={() => setAdminTab('communications')}
              >
                📧 Communications
              </button>
              <button 
                className={`admin-tab ${adminTab === 'groups' ? 'active' : ''}`}
                onClick={() => setAdminTab('groups')}
                data-testid="admin-tab-groups"
              >
                👥 Groups
              </button>
              <button 
                className={`admin-tab ${adminTab === 'settings' ? 'active' : ''}`}
                onClick={() => setAdminTab('settings')}
              >
                ⚙️ Settings
              </button>
              <button 
                className={`admin-tab ${adminTab === 'backup' ? 'active' : ''}`}
                onClick={() => setAdminTab('backup')}
              >
                💾 Backup
              </button>
            </div>
            
            {/* Admin Tab Content */}
            <div className="admin-tab-content">
              {adminTab === 'analytics' && (
                <AdminPanel authToken={authToken} onViewAsUser={handleViewAsUser} initialTab="analytics" showOnly="analytics" />
              )}
              {adminTab === 'users' && (
                <AdminPanel authToken={authToken} onViewAsUser={handleViewAsUser} initialTab="users" showOnly="users" />
              )}
              {adminTab === 'communications' && (
                <AdminPanel authToken={authToken} onViewAsUser={handleViewAsUser} showOnly="communications" />
              )}
              {adminTab === 'groups' && (
                <GroupsAdmin authToken={authToken} />
              )}
              {adminTab === 'settings' && (
                <div className="admin-settings-panel">
                  <h2 style={{ marginBottom: '1.5rem', color: '#1e293b' }}>📧 SMTP Email Settings</h2>
                  
                  {/* Messages */}
                  {settingsError && (
                    <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#fef2f2', color: '#dc2626', borderRadius: '8px' }}>
                      {settingsError}
                    </div>
                  )}
                  {settingsSuccess && (
                    <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f0fdf4', color: '#16a34a', borderRadius: '8px' }}>
                      {settingsSuccess}
                    </div>
                  )}
                  
                  <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '1.5rem' }}>
                    Configure the SMTP server settings for sending automated emails and notifications.
                  </p>
                  
                  <div style={{ display: 'grid', gap: '1rem', maxWidth: '600px' }}>
                    {/* SMTP Host */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '600', fontSize: '0.9rem', color: '#374151' }}>
                        SMTP Host
                      </label>
                      <input
                        type="text"
                        value={smtpSettings.host}
                        onChange={(e) => setSmtpSettings({...smtpSettings, host: e.target.value})}
                        placeholder="smtp.example.com"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
                      />
                    </div>
                    
                    {/* SMTP Port */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '600', fontSize: '0.9rem', color: '#374151' }}>
                        SMTP Port
                      </label>
                      <input
                        type="text"
                        value={smtpSettings.port}
                        onChange={(e) => setSmtpSettings({...smtpSettings, port: e.target.value})}
                        placeholder="587"
                        style={{ width: '150px', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
                      />
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: '#64748b' }}>
                        (587 for TLS, 465 for SSL, 25 for unencrypted)
                      </span>
                    </div>
                    
                    {/* SMTP Username */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '600', fontSize: '0.9rem', color: '#374151' }}>
                        SMTP Username
                      </label>
                      <input
                        type="text"
                        value={smtpSettings.user}
                        onChange={(e) => setSmtpSettings({...smtpSettings, user: e.target.value})}
                        placeholder="your-email@example.com"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
                      />
                    </div>
                    
                    {/* SMTP Password */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '600', fontSize: '0.9rem', color: '#374151' }}>
                        SMTP Password
                      </label>
                      <input
                        type="password"
                        value={smtpSettings.password}
                        onChange={(e) => setSmtpSettings({...smtpSettings, password: e.target.value})}
                        placeholder="••••••••"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
                      />
                      <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                        Leave blank to keep existing password
                      </p>
                    </div>
                    
                    {/* From Email */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '600', fontSize: '0.9rem', color: '#374151' }}>
                        From Email Address
                      </label>
                      <input
                        type="email"
                        value={smtpSettings.fromEmail}
                        onChange={(e) => setSmtpSettings({...smtpSettings, fromEmail: e.target.value})}
                        placeholder="noreply@example.com"
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
                      />
                      <p style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                        The "From" address that will appear in sent emails
                      </p>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                    <button
                      onClick={async () => {
                        setSavingSmtp(true);
                        setSettingsError('');
                        setSettingsSuccess('');
                        try {
                          const response = await fetch(`${API_URL}/api/admin/smtp-settings`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${authToken}`,
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify(smtpSettings),
                          });
                          if (response.ok) {
                            setSettingsSuccess('✅ SMTP settings saved successfully!');
                          } else {
                            const data = await response.json();
                            setSettingsError(data.detail || 'Failed to save settings');
                          }
                        } catch (err) {
                          setSettingsError('Failed to save SMTP settings');
                        } finally {
                          setSavingSmtp(false);
                        }
                      }}
                      disabled={savingSmtp}
                      style={{
                        backgroundColor: '#10b981',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        border: 'none',
                        fontWeight: '600',
                        cursor: savingSmtp ? 'not-allowed' : 'pointer',
                        opacity: savingSmtp ? 0.7 : 1
                      }}
                    >
                      {savingSmtp ? '⏳ Saving...' : '💾 Save Settings'}
                    </button>
                    
                    <button
                      onClick={async () => {
                        setTestingSmtp(true);
                        setSettingsError('');
                        setSettingsSuccess('');
                        try {
                          const response = await fetch(`${API_URL}/api/admin/test-smtp`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${authToken}`,
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify(smtpSettings),
                          });
                          const data = await response.json();
                          if (response.ok) {
                            setSettingsSuccess(`✅ ${data.message}`);
                          } else {
                            setSettingsError(data.detail || 'SMTP test failed');
                          }
                        } catch (err) {
                          setSettingsError('Failed to test SMTP connection');
                        } finally {
                          setTestingSmtp(false);
                        }
                      }}
                      disabled={testingSmtp}
                      style={{
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        border: 'none',
                        fontWeight: '600',
                        cursor: testingSmtp ? 'not-allowed' : 'pointer',
                        opacity: testingSmtp ? 0.7 : 1
                      }}
                    >
                      {testingSmtp ? '⏳ Testing...' : '📤 Test Connection'}
                    </button>
                  </div>
                  
                  {/* Info Box */}
                  <div style={{ 
                    marginTop: '2rem', 
                    padding: '1rem', 
                    backgroundColor: '#f0f9ff', 
                    border: '1px solid #bae6fd', 
                    borderRadius: '8px',
                    fontSize: '0.85rem',
                    color: '#0369a1'
                  }}>
                    <strong>ℹ️ Note:</strong> These settings are stored securely and used for all email communications 
                    including automated notifications, mass emails, and password resets. The password is encrypted at rest.
                  </div>
                  
                  {/* Email Footer Settings */}
                  <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #e2e8f0' }}>
                    <h2 style={{ marginBottom: '1rem', color: '#1e293b' }}>📝 Email Footer Settings</h2>
                    <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '1.5rem' }}>
                      Customize the footer that appears at the bottom of all automated emails.
                    </p>
                    
                    {/* Footer Preview */}
                    <div style={{ 
                      marginBottom: '1.5rem',
                      padding: '12px',
                      backgroundColor: '#2c5f2d',
                      borderRadius: '6px',
                      textAlign: 'center'
                    }}>
                      <a href={footerSettings.siteUrl} style={{ color: '#ffffff', textDecoration: 'none', fontSize: '14px', fontWeight: 'bold' }}>
                        {footerSettings.siteName}
                      </a>
                      <span style={{ color: '#86efac', fontSize: '13px' }}> - Created by {footerSettings.createdBy}</span>
                    </div>
                    
                    <div style={{ display: 'grid', gap: '1rem', maxWidth: '600px' }}>
                      {/* Site Name */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '600', fontSize: '0.9rem', color: '#374151' }}>
                          Site Name
                        </label>
                        <input
                          type="text"
                          value={footerSettings.siteName}
                          onChange={(e) => setFooterSettings({...footerSettings, siteName: e.target.value})}
                          placeholder="DineASaurus.com"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
                        />
                      </div>
                      
                      {/* Site URL */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '600', fontSize: '0.9rem', color: '#374151' }}>
                          Site URL
                        </label>
                        <input
                          type="url"
                          value={footerSettings.siteUrl}
                          onChange={(e) => setFooterSettings({...footerSettings, siteUrl: e.target.value})}
                          placeholder="https://dineasaurus.com"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
                        />
                      </div>
                      
                      {/* Created By */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '600', fontSize: '0.9rem', color: '#374151' }}>
                          Created By
                        </label>
                        <input
                          type="text"
                          value={footerSettings.createdBy}
                          onChange={(e) => setFooterSettings({...footerSettings, createdBy: e.target.value})}
                          placeholder="Bill Casso"
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
                        />
                      </div>
                    </div>
                    
                    {/* Save Footer Button */}
                    <div style={{ marginTop: '1.5rem' }}>
                      <button
                        onClick={async () => {
                          setSavingFooter(true);
                          setSettingsError('');
                          setSettingsSuccess('');
                          try {
                            const response = await fetch(`${API_URL}/api/admin/footer-settings`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${authToken}`,
                                'Content-Type': 'application/json',
                              },
                              body: JSON.stringify(footerSettings),
                            });
                            const data = await response.json();
                            if (response.ok) {
                              setSettingsSuccess('✅ Footer settings saved successfully!');
                            } else {
                              setSettingsError(data.detail || 'Failed to save footer settings');
                            }
                          } catch (err) {
                            setSettingsError('Failed to save footer settings');
                          } finally {
                            setSavingFooter(false);
                          }
                        }}
                        disabled={savingFooter}
                        style={{
                          backgroundColor: '#2c5f2d',
                          color: 'white',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          border: 'none',
                          fontWeight: '600',
                          cursor: savingFooter ? 'not-allowed' : 'pointer',
                          opacity: savingFooter ? 0.7 : 1
                        }}
                      >
                        {savingFooter ? '⏳ Saving...' : '💾 Save Footer Settings'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {adminTab === 'backup' && (
                <div className="admin-backup-panel">
                  <h2 style={{ marginBottom: '1.5rem', color: '#1e293b' }}>Database Backup & Restore</h2>
                  
                  {/* Messages */}
                  {settingsError && (
                    <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#fef2f2', color: '#dc2626', borderRadius: '8px' }}>
                      {settingsError}
                    </div>
                  )}
                  {settingsSuccess && (
                    <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f0fdf4', color: '#16a34a', borderRadius: '8px' }}>
                      {settingsSuccess}
                    </div>
                  )}
                  
                  {/* Backup Section */}
                  <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>📤 Backup Database</h3>
                    
                    {/* Toggle for Scheduled Backups */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      padding: '0.75rem 1rem',
                      backgroundColor: backupEnabled ? '#ecfdf5' : 'white',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      border: backupEnabled ? '1px solid #10b981' : '1px solid #e2e8f0'
                    }}>
                      <div>
                        <span style={{ fontWeight: '600', color: backupEnabled ? '#059669' : '#64748b' }}>
                          Scheduled Backups
                        </span>
                        <p style={{ fontSize: '0.8rem', color: '#94a3b8', margin: 0 }}>
                          {backupEnabled
                            ? `Active: ${backupSchedule}${backupSchedule === 'weekly' ? ` (${['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][backupDayOfWeek]})` : backupSchedule === 'monthly' ? ` (day ${backupDayOfMonth})` : ''} at ${formatHHMM12(backupTime)} ${tzShortLabel(backupTz)} → ${backupEmail}`
                            : 'Currently disabled'}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setBackupEnabled(!backupEnabled);
                          setBackupSettingsSaved(false);
                        }}
                        style={{
                          width: '50px',
                          height: '28px',
                          borderRadius: '14px',
                          border: 'none',
                          backgroundColor: backupEnabled ? '#10b981' : '#cbd5e1',
                          cursor: 'pointer',
                          position: 'relative',
                          transition: 'background-color 0.2s'
                        }}
                      >
                        <span style={{
                          position: 'absolute',
                          top: '2px',
                          left: backupEnabled ? '24px' : '2px',
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          transition: 'left 0.2s'
                        }} />
                      </button>
                    </div>
                    
                    {/* Email Input */}
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '600', fontSize: '0.85rem' }}>
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={backupEmail}
                        onChange={(e) => {
                          setBackupEmail(e.target.value);
                          setBackupSettingsSaved(false);
                        }}
                        placeholder="Enter email for backup"
                        style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                      />
                    </div>
                    
                    {/* Schedule Select */}
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '600', fontSize: '0.85rem' }}>
                        Schedule
                      </label>
                      <select
                        value={backupSchedule}
                        onChange={(e) => {
                          setBackupSchedule(e.target.value);
                          setBackupSettingsSaved(false);
                        }}
                        style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: 'white' }}
                        data-testid="backup-schedule-select"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    
                    {/* Day of Week Picker (weekly only) */}
                    {backupSchedule === 'weekly' && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '600', fontSize: '0.85rem' }}>
                          Day of Week ({tzShortLabel(backupTz)})
                        </label>
                        <select
                          value={backupDayOfWeek}
                          onChange={(e) => {
                            setBackupDayOfWeek(parseInt(e.target.value, 10));
                            setBackupSettingsSaved(false);
                          }}
                          style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: 'white' }}
                          data-testid="backup-day-of-week-select"
                        >
                          <option value={0}>Monday</option>
                          <option value={1}>Tuesday</option>
                          <option value={2}>Wednesday</option>
                          <option value={3}>Thursday</option>
                          <option value={4}>Friday</option>
                          <option value={5}>Saturday</option>
                          <option value={6}>Sunday</option>
                        </select>
                      </div>
                    )}
                    
                    {/* Day of Month Picker (monthly only) */}
                    {backupSchedule === 'monthly' && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '600', fontSize: '0.85rem' }}>
                          Day of Month (1–28)
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={28}
                          value={backupDayOfMonth}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            setBackupDayOfMonth(Number.isNaN(v) ? 1 : Math.max(1, Math.min(28, v)));
                            setBackupSettingsSaved(false);
                          }}
                          style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                          data-testid="backup-day-of-month-input"
                        />
                      </div>
                    )}
                    
                    {/* Time Picker (wall-clock time in the schedule timezone; DST-proof) */}
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.4rem', fontWeight: '600', fontSize: '0.85rem' }}>
                        Backup Time ({tzShortLabel(backupTz)})
                      </label>
                      <input
                        type="time"
                        value={backupTime}
                        onChange={(e) => {
                          setBackupTime(e.target.value);
                          setBackupTz(getBrowserTimezone());
                          setBackupSettingsSaved(false);
                        }}
                        style={{ width: '100%', padding: '0.6rem', border: '1px solid #d1d5db', borderRadius: '6px' }}
                        data-testid="backup-time-picker"
                      />
                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.7rem', color: '#94a3b8' }} data-testid="backup-tz-note">
                        Runs at {formatHHMM12(backupTime)} <code>{backupTz}</code> year-round (adjusts for daylight saving automatically).
                        {backupTz !== getBrowserTimezone() && ` Your browser is in ${getBrowserTimezone()}; editing the time switches the schedule to your timezone.`}
                      </p>
                    </div>
                    
                    {/* Last Backup Notification */}
                    {lastBackupAt ? (
                      <div style={{
                        marginBottom: '1rem',
                        padding: '0.6rem 0.75rem',
                        backgroundColor: lastBackupStatus === 'success' ? '#f0fdf4' : '#fef2f2',
                        border: `1px solid ${lastBackupStatus === 'success' ? '#bbf7d0' : '#fecaca'}`,
                        borderRadius: '6px',
                        fontSize: '0.8rem'
                      }}>
                        <span style={{ fontWeight: '600', color: lastBackupStatus === 'success' ? '#16a34a' : '#dc2626' }}>
                          {lastBackupStatus === 'success' ? '✅' : '❌'} Last backup:
                        </span>{' '}
                        <span style={{ color: '#475569' }}>
                          {new Date(lastBackupAt).toLocaleString()}
                        </span>
                        {lastBackupStatus && lastBackupStatus !== 'success' && (
                          <div style={{ marginTop: '0.25rem', color: '#dc2626' }}>{lastBackupStatus}</div>
                        )}
                      </div>
                    ) : (
                      <div style={{
                        marginBottom: '1rem',
                        padding: '0.6rem 0.75rem',
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        color: '#64748b'
                      }} data-testid="last-backup-empty">
                        ℹ️ No backup has run yet. Click <strong>Send Now</strong> or <strong>Download</strong> to create one, or enable Scheduled Backups above.
                      </div>
                    )}
                    
                    {/* Save Settings Button — prominent warning when unsaved */}
                    {!backupSettingsSaved && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{
                          padding: '0.6rem 0.75rem',
                          marginBottom: '0.4rem',
                          backgroundColor: '#fef3c7',
                          border: '1px solid #fde68a',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          color: '#92400e',
                          fontWeight: '600'
                        }} data-testid="backup-unsaved-warning">
                          ⚠️ Unsaved changes — the automated scheduler uses the saved settings, not the current form values. Click Save Settings below to apply.
                        </div>
                        <Button 
                          onClick={saveBackupSettings}
                          style={{ backgroundColor: '#f59e0b', color: 'white', width: '100%', fontWeight: '700' }}
                          data-testid="backup-save-settings-btn"
                        >
                          💾 Save Settings
                        </Button>
                      </div>
                    )}
                    
                    {/* Scheduler Status (live diagnostic) */}
                    {schedulerStatus && !schedulerStatus.error && (
                      <div style={{
                        marginBottom: '0.75rem',
                        padding: '0.6rem 0.75rem',
                        backgroundColor: schedulerStatus.is_alive ? '#f0fdf4' : '#fef2f2',
                        border: `1px solid ${schedulerStatus.is_alive ? '#bbf7d0' : '#fecaca'}`,
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        color: '#475569'
                      }} data-testid="scheduler-status">
                        <div style={{ fontWeight: 600, color: schedulerStatus.is_alive ? '#16a34a' : '#dc2626', marginBottom: '0.25rem' }}>
                          Scheduler {schedulerStatus.is_alive ? '✅ alive' : '❌ not alive'}
                          {schedulerStatus.tick_count > 0 && ` • ${schedulerStatus.tick_count} ticks`}
                        </div>
                        {schedulerStatus.last_tick_at && (
                          <div>Last tick: {new Date(schedulerStatus.last_tick_at).toLocaleString()} ({Math.round(schedulerStatus.seconds_since_last_tick || 0)}s ago)</div>
                        )}
                        {schedulerStatus.last_decision && (
                          <div>Last decision: <strong>{schedulerStatus.last_decision}</strong>{schedulerStatus.last_reason ? ` — ${schedulerStatus.last_reason}` : ''}</div>
                        )}
                        {schedulerStatus.last_fired_at && (
                          <div>Last auto-fired: {new Date(schedulerStatus.last_fired_at).toLocaleString()}</div>
                        )}
                        {schedulerStatus.next_run_at && (
                          <div data-testid="scheduler-next-run">Next scheduled run: {new Date(schedulerStatus.next_run_at).toLocaleString()}</div>
                        )}
                        {!schedulerStatus.last_tick_at && (
                          <div style={{ color: '#dc2626' }}>⚠️ Scheduler hasn't ticked yet — backend may have just restarted, wait 60–90s and refresh.</div>
                        )}
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <Button 
                        onClick={handleUserBackup} 
                        style={{ backgroundColor: '#10b981', color: 'white', flex: '1 1 30%' }}
                        data-testid="backup-send-now-btn"
                      >
                        📧 Send Now
                      </Button>
                      <Button 
                        onClick={handleDownloadBackup} 
                        style={{ backgroundColor: '#3b82f6', color: 'white', flex: '1 1 30%' }}
                        data-testid="backup-download-btn"
                      >
                        💾 Download
                      </Button>
                      <Button 
                        onClick={handleRunBackupNow} 
                        style={{ backgroundColor: '#f59e0b', color: 'white', flex: '1 1 30%' }}
                        data-testid="backup-run-scheduled-now-btn"
                        title="Trigger the same logic the automated scheduler uses"
                      >
                        ⚡ Run Scheduled Now
                      </Button>
                    </div>
                  </div>
                  
                  {/* Restore Section */}
                  <div style={{ padding: '1.5rem', backgroundColor: '#fef2f2', borderRadius: '12px', border: '1px solid #fecaca' }}>
                    <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600', color: '#dc2626' }}>📥 Restore Database</h3>
                    <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1rem' }}>
                      ⚠️ Warning: Restoring will replace all existing data. Make sure to backup first!
                    </p>
                    <input
                      type="file"
                      accept=".json"
                      onChange={handleUserRestore}
                      style={{ marginBottom: '0.5rem' }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (activeTab === 'dashboard' || activeTab === 'allDashboard') && !showLatestVisitsView && (
          <>
          <div className="content-wrapper">
          
          {/* Mobile-only Photo Section - Above filters */}
          {!searchQuery && !showNearest && !hasActiveFilters() && activeTab === 'dashboard' && !isVisitHistoryOpen && !isLatestVisitsOpen && !isEditVisitOpen && !isPhotosOpen && !isEditOpen && !isAddOpen && !isVisitDialogOpen && !isQuickAddVisitOpen && !showLatestVisitsView && (
            <div className="dashboard-section photo-montage-section-top">
              <h2 className="section-title">
                📸 Recent Photos
                <div className="section-footprints">
                  <span className="dino-track">
                    <span className="toe toe-left"></span>
                    <span className="toe toe-center"></span>
                    <span className="toe toe-right"></span>
                  </span>
                  <span className="dino-track track-offset">
                    <span className="toe toe-left"></span>
                    <span className="toe toe-center"></span>
                    <span className="toe toe-right"></span>
                  </span>
                </div>
              </h2>
              {photoMontage.length === 0 ? (
                <p className="section-empty">No photos yet. Upload photos to see them here!</p>
              ) : (
                <div className="photo-montage dashboard-montage">
                  {photoMontage.map((photo, index) => (
                    <div 
                      key={index} 
                      className="montage-photo-item-small"
                      data-testid={`montage-photo-${index}`}
                      title={`${photo.restaurantName} - ${photo.restaurantLocation}`}
                    >
                      <img
                        src={getPhotoUrl(photo.thumbnailUrl)}
                        alt={photo.restaurantName}
                        className="montage-photo-small"
                        style={{
                          transform: `rotate(${photo.rotation || 0}deg)`,
                          cursor: 'pointer'
                        }}
                        onClick={() => showVisitHistory(photo.restaurant)}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <button
                        className="rotate-photo-btn"
                        onClick={(e) => handleRotatePhoto(photo.restaurant.id, photo.photoId, e)}
                        title="Rotate photo 90°"
                      >
                        ↻
                      </button>
                      <div className="montage-photo-overlay-small">
                        <span className="montage-restaurant-name-small">{photo.restaurantName}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!searchQuery && !showNearest && !hasActiveFilters() && activeTab === 'allDashboard' && !isVisitHistoryOpen && !isLatestVisitsOpen && !isEditVisitOpen && !isPhotosOpen && !isEditOpen && !isAddOpen && !isVisitDialogOpen && !isQuickAddVisitOpen && (
            <div className="dashboard-section photo-montage-section-top">
              <h2 className="section-title">
                📸 Recent Photos
                <div className="section-footprints">
                  <span className="dino-track">
                    <span className="toe toe-left"></span>
                    <span className="toe toe-center"></span>
                    <span className="toe toe-right"></span>
                  </span>
                  <span className="dino-track track-offset">
                    <span className="toe toe-left"></span>
                    <span className="toe toe-center"></span>
                    <span className="toe toe-right"></span>
                  </span>
                </div>
              </h2>
              {photoMontage.length === 0 ? (
                <p className="section-empty">No photos yet. Upload photos to see them here!</p>
              ) : (
                <div className="photo-montage dashboard-montage">
                  {photoMontage.map((photo, index) => (
                    <div 
                      key={index} 
                      className="montage-photo-item-small"
                      data-testid={`montage-photo-${index}`}
                      title={`${photo.restaurantName} - ${photo.restaurantLocation}`}
                    >
                      <img
                        src={getPhotoUrl(photo.thumbnailUrl)}
                        alt={photo.restaurantName}
                        className="montage-photo-small"
                        style={{
                          transform: `rotate(${photo.rotation || 0}deg)`,
                          cursor: 'pointer'
                        }}
                        onClick={() => showVisitHistory(photo.restaurant)}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <button
                        className="rotate-photo-btn"
                        onClick={(e) => handleRotatePhoto(photo.restaurant.id, photo.photoId, e)}
                        title="Rotate photo 90°"
                      >
                        ↻
                      </button>
                      <div className="montage-photo-overlay-small">
                        <span className="montage-restaurant-name-small">{photo.restaurantName}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Mobile Search Bar for Dashboard */}
          <div className="mobile-search-bar">
            <input
              type="text"
              placeholder="🔍 Search by restaurant name, location, or favorite"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mobile-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="mobile-search-clear"
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>

          {/* Active Filters Display for Mobile */}
          {hasActiveFilters() && (
            <div className="mobile-active-filters">
              {/* Cuisine/Ethnicity filters */}
              {filters.ethnicity.map((cuisine) => (
                <span key={cuisine} className="mobile-filter-chip">
                  <span className="filter-label">Cuisine:</span> {cuisine}{' '}
                  <button 
                    onClick={() => handleFilterChange('ethnicity', cuisine)}
                    className="mobile-filter-remove"
                    title="Remove filter"
                  >
                    ×
                  </button>
                </span>
              ))}
              
              {/* Location filters */}
              {filters.location.map((loc) => (
                <span key={loc} className="mobile-filter-chip">
                  <span className="filter-label">Location:</span> {loc.split(',')[0]}{' '}
                  <button 
                    onClick={() => handleFilterChange('location', loc)}
                    className="mobile-filter-remove"
                    title="Remove filter"
                  >
                    ×
                  </button>
                </span>
              ))}
              
              {/* Price filters */}
              {filters.price.map((price) => (
                <span key={price} className="mobile-filter-chip">
                  <span className="filter-label">Price:</span> {price}
                  <button 
                    onClick={() => handleFilterChange('price', price)}
                    className="mobile-filter-remove"
                    title="Remove filter"
                  >
                    ×
                  </button>
                </span>
              ))}
              
              {/* Ownership filters */}
              {filters.ownership.map((own) => (
                <span key={own} className="mobile-filter-chip">
                  <span className="filter-label">Type:</span> {own}
                  <button 
                    onClick={() => handleFilterChange('ownership', own)}
                    className="mobile-filter-remove"
                    title="Remove filter"
                  >
                    ×
                  </button>
                </span>
              ))}
              
              {/* Amenity filters */}
              {filters.patio && (
                <span className="mobile-filter-chip">
                  <span className="filter-label">Patio</span>
                  <button 
                    onClick={() => handleFilterChange('patio', null)}
                    className="mobile-filter-remove"
                    title="Remove filter"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.dogFriendly && (
                <span className="mobile-filter-chip">
                  <span className="filter-label">Dog Friendly</span>
                  <button 
                    onClick={() => handleFilterChange('dogFriendly', null)}
                    className="mobile-filter-remove"
                    title="Remove filter"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.heineken && (
                <span className="mobile-filter-chip">
                  <span className="filter-label">Heineken</span>
                  <button 
                    onClick={() => handleFilterChange('heineken', null)}
                    className="mobile-filter-remove"
                    title="Remove filter"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Mobile Filter Toggle Button */}
          <button 
            className="mobile-filter-toggle"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="4" y1="21" x2="4" y2="14"></line>
              <line x1="4" y1="10" x2="4" y2="3"></line>
              <line x1="12" y1="21" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12" y2="3"></line>
              <line x1="20" y1="21" x2="20" y2="16"></line>
              <line x1="20" y1="12" x2="20" y2="3"></line>
              <line x1="1" y1="14" x2="7" y2="14"></line>
              <line x1="9" y1="8" x2="15" y2="8"></line>
              <line x1="17" y1="16" x2="23" y2="16"></line>
            </svg>
            Filters {hasActiveFilters() && `(${Object.values(filters).flat().filter(Boolean).length})`}
            <span className={`filter-toggle-icon ${showMobileFilters ? 'open' : ''}`}>▼</span>
          </button>
          
          <aside className={`filter-panel ${showMobileFilters ? 'mobile-open' : 'mobile-closed'}`} data-testid="filter-panel">
            <div className="filter-header">
              <h3>Filters</h3>
              {hasActiveFilters() && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearAllFilters}
                  className="clear-filters-btn"
                  data-testid="clear-all-filters"
                >
                  Clear All
                </Button>
              )}
            </div>
            {renderShowClosedToggle()}

            <div className="filter-section">
              <h4>Location</h4>
              <select 
                value={filters.location[0] || ''} 
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    handleFilterChange('location', value);
                  }
                }}
                className="filter-select"
                data-testid="location-select"
              >
                <option value="">All Locations</option>
                {Object.keys(locationsByState).sort().map(state => (
                  <optgroup key={state} label={state}>
                    {locationsByState[state].map(({ fullLocation, city }) => (
                      <option key={fullLocation} value={fullLocation}>
                        {city}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {filters.location.length > 0 && (
                <div className="selected-locations">
                  {filters.location.map((loc) => (
                    <span key={loc} className="location-tag">
                      {loc.includes(',') ? loc.split(',')[0].trim() : loc}
                      <button 
                        onClick={() => handleFilterChange('location', loc)}
                        className="remove-tag"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-section">
              <h4>Cuisine</h4>
              <select
                value=""
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    handleFilterChange('ethnicity', value);
                  }
                }}
                className="filter-select"
                data-testid="cuisine-select"
              >
                <option value="">Select Cuisine...</option>
                {uniqueCuisines.length > 0 ? (
                  uniqueCuisines.map((cuisine) => (
                    <option key={cuisine} value={cuisine}>
                      {cuisine}
                    </option>
                  ))
                ) : (
                  <option disabled>No cuisines available</option>
                )}
              </select>
              {filters.ethnicity.length > 0 && (
                <div className="selected-locations">
                  {filters.ethnicity.map((cuisine) => (
                    <span key={cuisine} className="location-tag">
                      {cuisine}
                      <button 
                        onClick={() => handleFilterChange('ethnicity', cuisine)}
                        className="remove-tag"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-section">
              <h4>Ownership</h4>
              <select
                value=""
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    handleFilterChange('ownership', value);
                  }
                }}
                className="filter-select"
                data-testid="ownership-select"
              >
                <option value="">Select Ownership...</option>
                {uniqueOwnership.length > 0 ? (
                  uniqueOwnership.map((ownership) => (
                    <option key={ownership} value={ownership}>
                      {ownership}
                    </option>
                  ))
                ) : (
                  <option disabled>No ownership available</option>
                )}
              </select>
              {filters.ownership.length > 0 && (
                <div className="selected-locations">
                  {filters.ownership.map((ownership) => (
                    <span key={ownership} className="location-tag">
                      {ownership}
                      <button 
                        onClick={() => handleFilterChange('ownership', ownership)}
                        className="remove-tag"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="filter-section">
              <h4>Amenities</h4>
              <select
                value=""
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    handleFilterChange(value, true);
                  }
                }}
                className="filter-select"
                data-testid="amenities-select"
              >
                <option value="">Select Amenity...</option>
                <option value="patio">🏖️ Patio</option>
                <option value="dogFriendly">🐕 Dog Friendly</option>
                <option value="heineken">🍺 Heineken</option>
              </select>
              {(filters.patio || filters.dogFriendly || filters.heineken) && (
                <div className="selected-locations">
                  {filters.patio && (
                    <span className="location-tag">
                      🏖️ Patio
                      <button 
                        onClick={() => handleFilterChange('patio', true)}
                        className="remove-tag"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {filters.dogFriendly && (
                    <span className="location-tag">
                      🐕 Dog Friendly
                      <button 
                        onClick={() => handleFilterChange('dogFriendly', true)}
                        className="remove-tag"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {filters.heineken && (
                    <span className="location-tag">
                      🍺 Heineken
                      <button 
                        onClick={() => handleFilterChange('heineken', true)}
                        className="remove-tag"
                      >
                        ×
                      </button>
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="filter-section">
              <h4>Price</h4>
              <select
                value=""
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    handleFilterChange('price', value);
                  }
                }}
                className="filter-select"
                data-testid="price-select"
              >
                <option value="">Select Price...</option>
                {uniquePrices.length > 0 ? (
                  uniquePrices.map((price) => (
                    <option key={price} value={price}>
                      {price}
                    </option>
                  ))
                ) : (
                  <option disabled>No prices available</option>
                )}
              </select>
              {filters.price.length > 0 && (
                <div className="selected-locations">
                  {filters.price.map((price) => (
                    <span key={price} className="location-tag">
                      {price}
                      <button 
                        onClick={() => handleFilterChange('price', price)}
                        className="remove-tag"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            {/* Copyright at bottom of filter panel */}
            <div style={{ 
              marginTop: 'auto', 
              paddingTop: '2rem', 
              borderTop: '1px solid #e2e8f0',
              textAlign: 'center'
            }}>
              <a 
                href={footerSettings.siteUrl}
                style={{ 
                  color: '#2c5f2d', 
                  textDecoration: 'none', 
                  fontWeight: 'bold',
                  fontSize: '0.8rem',
                  display: 'block',
                  marginBottom: '0.25rem'
                }}
              >
                {footerSettings.siteName}
                <VersionBadge version={footerSettings.version} />
              </a>
              <p style={{ 
                fontSize: '0.7rem', 
                color: '#64748b',
                margin: 0
              }}>
                Created by {footerSettings.createdBy}
              </p>
            </div>
          </aside>

          <div className="main-section">
            {/* Show search and filters only for My Restaurants and All Restaurants tabs */}
            {(activeTab === 'allRestaurants' || activeTab === 'allUsersRestaurants') && (
              <>
                <div className="controls-section">
                  <div className="search-bar">
                    <Search className="search-icon" size={20} />
                    <Input
                      data-testid="search-input"
                      type="text"
                      placeholder="Search by restaurant name, location, or favorite items..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="search-input"
                    />
                    <button 
                      onClick={startVoiceSearch}
                      className={`voice-search-btn ${isListening ? 'listening' : ''}`}
                      title="Voice search"
                      disabled={isListening}
                    >
                      <Mic size={20} className={isListening ? 'mic-pulse' : ''} />
                    </button>
                  </div>

                  {searchHistory.length > 0 && (
                    <div className="search-history" data-testid="search-history">
                      <div className="search-history-header">
                        <span className="history-title">Recent Searches</span>
                        <button 
                          onClick={clearSearchHistory} 
                          className="clear-history-btn"
                          data-testid="clear-history-btn"
                        >
                          Clear
                        </button>
                      </div>
                      <div className="search-history-items">
                        {searchHistory.map((term, index) => (
                          <button
                            key={index}
                            onClick={() => applyHistorySearch(term)}
                            className="history-item"
                            data-testid={`history-item-${index}`}
                          >
                            <Search size={14} />
                            {term}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                </div>

                {showNearest && filteredRestaurants.length > 0 && !isVisitHistoryOpen && !isLatestVisitsOpen && !isEditVisitOpen && !isPhotosOpen && !isEditOpen && !isAddOpen && !isVisitDialogOpen && !isQuickAddVisitOpen && (
                  <div className="nearest-banner" data-testid="nearest-banner">
                    <MapPin size={18} />
                    Showing restaurants nearest to your location
                  </div>
                )}

                {(searchQuery || showNearest || hasActiveFilters()) && (
                  <div className="restaurant-count" data-testid="restaurant-count">
                    <span className="count-number">{filteredRestaurants.length}</span>
                    <span className="count-label">
                      Restaurant{filteredRestaurants.length !== 1 ? 's' : ''} 
                      {searchQuery ? ' found' : hasActiveFilters() ? ' match filters' : ' found'}
                    </span>
                    {filteredRestaurants.length > 0 && (
                      <span className="scroll-hint">↔ Scroll table horizontally to see all columns</span>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="restaurants-table-container">
              {!searchQuery && !showNearest && !hasActiveFilters() && activeTab === 'dashboard' && !isVisitHistoryOpen && !isLatestVisitsOpen && !isEditVisitOpen && !isPhotosOpen && !isEditOpen && !isAddOpen && !isVisitDialogOpen && !isQuickAddVisitOpen && !showLatestVisitsView ? (
                <>
                <div className="dashboard-view" data-testid="dashboard-view">
                  {/* Photo montage at top of My Dashboard */}
                  <div className="dashboard-section photo-section-desktop">
                    <h2 className="section-title">
                📸 Recent Photos
                <div className="section-footprints">
                  <span className="dino-track">
                    <span className="toe toe-left"></span>
                    <span className="toe toe-center"></span>
                    <span className="toe toe-right"></span>
                  </span>
                  <span className="dino-track track-offset">
                    <span className="toe toe-left"></span>
                    <span className="toe toe-center"></span>
                    <span className="toe toe-right"></span>
                  </span>
                </div>
              </h2>
                    {photoMontage.length === 0 ? (
                      <p className="section-empty">No photos yet. Upload photos to see them here!</p>
                    ) : (
                      <div className="photo-montage dashboard-montage">
                        {photoMontage.map((photo, index) => (
                          <div 
                            key={index} 
                            className="montage-photo-item-small"
                            data-testid={`montage-photo-${index}`}
                            title={`${photo.restaurantName} - ${photo.restaurantLocation}`}
                          >
                            <img
                              src={getPhotoUrl(photo.thumbnailUrl)}
                              alt={photo.restaurantName}
                              className="montage-photo-small"
                              style={{
                                transform: `rotate(${photo.rotation || 0}deg)`,
                                cursor: 'pointer'
                              }}
                              onClick={() => showVisitHistory(photo.restaurant)}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <button
                              className="rotate-photo-btn"
                              onClick={(e) => handleRotatePhoto(photo.restaurant.id, photo.photoId, e)}
                              title="Rotate photo 90°"
                            >
                              ↻
                            </button>
                            <div className="montage-photo-overlay-small">
                              <span className="montage-restaurant-name-small">{photo.restaurantName}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="dashboard-section dashboard-nearest">
                    <div className="section-header-collapsible">
                      <h2 className="section-title">
                        📍 Nearest Restaurants
                        <div className="section-footprints">
                          <span className="dino-track">
                            <span className="toe toe-left"></span>
                            <span className="toe toe-center"></span>
                            <span className="toe toe-right"></span>
                          </span>
                          <span className="dino-track track-offset">
                            <span className="toe toe-left"></span>
                            <span className="toe toe-center"></span>
                            <span className="toe toe-right"></span>
                          </span>
                        </div>
                      </h2>
                      <button 
                        className="collapse-toggle"
                        onClick={() => setIsNearestCollapsed(!isNearestCollapsed)}
                        aria-label={isNearestCollapsed ? "Expand section" : "Collapse section"}
                      >
                        {isNearestCollapsed ? '▼' : '▲'}
                      </button>
                    </div>
                    {!isNearestCollapsed && (
                      <>
                        {(() => {
                          // Use nearbyRestaurants if available, otherwise fall back to restaurants sorted by distance
                          const displayNearby = nearbyRestaurants.length > 0 
                            ? nearbyRestaurants 
                            : restaurants
                                .filter(r => r.distance !== undefined && r.distance !== null)
                                .sort((a, b) => (a.distance || 999) - (b.distance || 999))
                                .slice(0, 10);
                          
                          return displayNearby.length === 0 ? (
                            <div className="section-empty" data-testid="nearest-restaurants-empty">
                              <p>Loading nearest restaurants...</p>
                              <p style={{fontSize: '0.9em', color: '#666', marginTop: '8px'}}>
                                (Requires browser location permission)
                              </p>
                            </div>
                          ) : (
                      <div className="table-container">
                        <table className="restaurants-table nearest-table">
                          <thead>
                            <tr>
                              <th className="restaurant-name-col">Restaurant</th>
                              <th>
                                <span className="mobile-only">📍</span>
                                <span className="desktop-only">Distance</span>
                              </th>
                              <th>Cuisine</th>
                              <th>
                                <span className="mobile-only">⭐</span>
                                <span className="desktop-only">My Rating</span>
                              </th>
                              <th>Price</th>
                              <th>Last</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayNearby.map((restaurant) => (
                              <tr 
                                key={restaurant.id} 
                                className="restaurant-row"
                                data-testid={`nearby-${restaurant.id}`}
                              >
                                {/* Mobile cuisine column removed */}
                                <td 
                                  className="restaurant-name-cell clickable"
                                  onClick={() => showVisitHistory(restaurant)}
                                >
                                  <strong>{restaurant.name}</strong>
                                </td>
                                <td>{restaurant.distance ? `${restaurant.distance.toFixed(1)} mi` : '–'}</td>
                                <td className="cuisine-col">
                                  <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', gap: '4px' }}>
                                    {(() => {
                                      const icon = getCuisineIcon(restaurant.ethnicity);
                                      if (icon?.type === 'emoji') {
                                        return <>{icon.value} <span>{restaurant.ethnicity || '–'}</span></>;
                                      } else if (icon?.type === 'flag') {
                                        return (
                                          <>
                                            <img 
                                              src={`https://flagcdn.com/16x12/${icon.code}.png`}
                                              srcSet={`https://flagcdn.com/32x24/${icon.code}.png 2x, https://flagcdn.com/48x36/${icon.code}.png 3x`}
                                              width="16"
                                              height="12"
                                              alt=""
                                              style={{ flexShrink: 0 }}
                                            />
                                            <span>{restaurant.ethnicity}</span>
                                          </>
                                        );
                                      }
                                      return restaurant.ethnicity || '–';
                                    })()}
                                  </div>
                                </td>
                                <td>
                                  {restaurant.averageRating || restaurant.overallRating ? (
                                    <div className="rating-box">
                                      ⭐ {(restaurant.averageRating || restaurant.overallRating).toFixed(1)}/5
                                    </div>
                                  ) : '–'}
                                </td>
                                <td>{formatPriceToDollars(restaurant.price)}</td>
                                <td>{formatDaysBack(restaurant.lastVisit)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                        );
                        })()}
                      </>
                    )}
                  </div>

                  <div className="dashboard-section dashboard-recent">
                    <div className="section-header-collapsible">
                      <h2 className="section-title">
                        🕐 Most Recent Visits
                        <div className="section-footprints">
                          <span className="dino-track">
                            <span className="toe toe-left"></span>
                            <span className="toe toe-center"></span>
                            <span className="toe toe-right"></span>
                          </span>
                          <span className="dino-track track-offset">
                            <span className="toe toe-left"></span>
                            <span className="toe toe-center"></span>
                            <span className="toe toe-right"></span>
                          </span>
                        </div>
                      </h2>
                      <button 
                        className="collapse-toggle"
                        onClick={() => setIsRecentCollapsed(!isRecentCollapsed)}
                        aria-label={isRecentCollapsed ? "Expand section" : "Collapse section"}
                      >
                        {isRecentCollapsed ? '▼' : '▲'}
                      </button>
                    </div>
                    {!isRecentCollapsed && (
                      <>
                        {recentVisits.length === 0 ? (
                          <p className="section-empty">No visits recorded yet. Start tracking your dining experiences!</p>
                        ) : (
                      <div className="table-container" key={refreshTrigger}>
                        <table className="restaurants-table">
                          <thead>
                            <tr>
                              <th className="restaurant-name-col">Restaurant</th>
                              <th>
                                <span className="mobile-only">📍</span>
                                <span className="desktop-only">Distance</span>
                              </th>
                              <th>Cuisine</th>
                              <th>
                                <span className="mobile-only">⭐</span>
                                <span className="desktop-only">My Rating</span>
                              </th>
                              <th>Price</th>
                              <th>
                                <span className="mobile-only">Last</span>
                                <span className="desktop-only">Visit Date</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentVisits.map((visit) => {
                              // Find the full restaurant object from allRestaurantsList
                              const fullRestaurant = allRestaurantsList.find(r => r.id === visit.restaurantId) || {
                                id: visit.restaurantId,
                                name: visit.restaurantName,
                                location: visit.restaurantLocation,
                                latitude: visit.restaurantLatitude,
                                longitude: visit.restaurantLongitude,
                                user_email: visit.user_email
                              };
                              
                              // Calculate distance if not already available and userLocation exists
                              let distance = fullRestaurant.distance;
                              if (!distance && userLocation && fullRestaurant.latitude && fullRestaurant.longitude) {
                                distance = calculateDistance(
                                  userLocation.latitude,
                                  userLocation.longitude,
                                  fullRestaurant.latitude,
                                  fullRestaurant.longitude
                                );
                              }
                              
                              return (
                                <tr 
                                  key={`${visit.id}-${refreshTrigger}`} 
                                  className="restaurant-row"
                                  data-testid={`recent-${visit.id}`}
                                >
                                  <td 
                                    className="restaurant-name-cell clickable"
                                    onClick={() => showVisitHistory(fullRestaurant)}
                                  >
                                    <strong>{visit.restaurantName}</strong>
                                  </td>
                                  <td>{distance ? `${distance.toFixed(1)} mi` : '–'}</td>
                                  <td className="cuisine-col">
                                    <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', gap: '4px' }}>
                                      {(() => {
                                        const icon = getCuisineIcon(fullRestaurant.ethnicity);
                                        if (icon?.type === 'emoji') {
                                          return <>{icon.value} <span>{fullRestaurant.ethnicity || '–'}</span></>;
                                        } else if (icon?.type === 'flag') {
                                          return (
                                            <>
                                              <img 
                                                src={`https://flagcdn.com/16x12/${icon.code}.png`}
                                                srcSet={`https://flagcdn.com/32x24/${icon.code}.png 2x, https://flagcdn.com/48x36/${icon.code}.png 3x`}
                                                width="16"
                                                height="12"
                                                alt=""
                                                style={{ flexShrink: 0 }}
                                              />
                                              <span>{fullRestaurant.ethnicity}</span>
                                            </>
                                          );
                                        }
                                        return fullRestaurant.ethnicity || '–';
                                      })()}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="rating-box">
                                      {visit.rating ? `⭐ ${visit.rating.toFixed(1)}/5` : '–'}
                                    </div>
                                  </td>
                                  <td>{formatPriceToDollars(fullRestaurant.price)}</td>
                                  <td>
                                    <span className="mobile-only">{formatDateNoYear(visit.visitDate)}</span>
                                    <span className="desktop-only">{formatDateDisplay(visit.visitDate)}</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                </>
              ) : !searchQuery && !showNearest && !hasActiveFilters() && activeTab === 'allDashboard' ? (
                <>
                <div className="dashboard-view" data-testid="all-dashboard-view">
                  {/* Photo montage at top of All Dashboard */}
                  <div className="dashboard-section photo-section-desktop">
                    <h2 className="section-title">
                📸 Recent Photos
                <div className="section-footprints">
                  <span className="dino-track">
                    <span className="toe toe-left"></span>
                    <span className="toe toe-center"></span>
                    <span className="toe toe-right"></span>
                  </span>
                  <span className="dino-track track-offset">
                    <span className="toe toe-left"></span>
                    <span className="toe toe-center"></span>
                    <span className="toe toe-right"></span>
                  </span>
                </div>
              </h2>
                    {photoMontage.length === 0 ? (
                      <p className="section-empty">No photos yet. Upload photos to see them here!</p>
                    ) : (
                      <div className="photo-montage dashboard-montage">
                        {photoMontage.map((photo, index) => (
                          <div 
                            key={index} 
                            className="montage-photo-item-small"
                            data-testid={`montage-photo-${index}`}
                            title={`${photo.restaurantName} - ${photo.restaurantLocation}`}
                          >
                            <img
                              src={getPhotoUrl(photo.thumbnailUrl)}
                              alt={photo.restaurantName}
                              className="montage-photo-small"
                              style={{
                                transform: `rotate(${photo.rotation || 0}deg)`,
                                cursor: 'pointer'
                              }}
                              onClick={() => showVisitHistory(photo.restaurant)}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <button
                              className="rotate-photo-btn"
                              onClick={(e) => handleRotatePhoto(photo.restaurant.id, photo.photoId, e)}
                              title="Rotate photo 90°"
                            >
                              ↻
                            </button>
                            <div className="montage-photo-overlay-small">
                              <span className="montage-restaurant-name-small">{photo.restaurantName}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="dashboard-section dashboard-nearest">
                    <h2 className="section-title">
                      📍 Nearest Restaurants (All Users)
                      <div className="section-footprints">
                        <span className="dino-track">
                          <span className="toe toe-left"></span>
                          <span className="toe toe-center"></span>
                          <span className="toe toe-right"></span>
                        </span>
                        <span className="dino-track track-offset">
                          <span className="toe toe-left"></span>
                          <span className="toe toe-center"></span>
                          <span className="toe toe-right"></span>
                        </span>
                      </div>
                    </h2>
                    {(() => {
                      const displayAllNearby = allNearbyRestaurants.length > 0 
                        ? allNearbyRestaurants 
                        : allUsersRestaurants
                            .filter(r => r.distance !== undefined && r.distance !== null)
                            .sort((a, b) => (a.distance || 999) - (b.distance || 999))
                            .slice(0, 10);
                      
                      return displayAllNearby.length === 0 ? (
                        <div className="section-empty" data-testid="all-nearest-restaurants-empty">
                          <p>Loading nearest restaurants from all users...</p>
                          <p style={{fontSize: '0.9em', color: '#666', marginTop: '8px'}}>
                            (Requires browser location permission)
                          </p>
                        </div>
                      ) : (
                      <div className="table-container">
                        <table className="restaurants-table nearest-table">
                          <thead>
                            <tr>
                              <th className="restaurant-name-col">Restaurant</th>
                              <th>
                                <span className="mobile-only">📍</span>
                                <span className="desktop-only">Distance</span>
                              </th>
                              <th>Cuisine</th>
                              <th>
                                <span className="mobile-only">⭐</span>
                                <span className="desktop-only">My Rating</span>
                              </th>
                              <th>Price</th>
                              <th>Last</th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayAllNearby.map((restaurant) => (
                              <tr 
                                key={restaurant.id} 
                                className="restaurant-row"
                                data-testid={`all-nearby-${restaurant.id}`}
                              >
                                {/* Mobile cuisine column removed */}
                                <td 
                                  className="restaurant-name-cell clickable"
                                  onClick={() => showVisitHistory(restaurant)}
                                >
                                  <strong>{restaurant.name}</strong>
                                </td>
                                <td>{restaurant.distance ? `${restaurant.distance.toFixed(1)} mi` : '–'}</td>
                                <td className="cuisine-col">
                                  <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', gap: '4px' }}>
                                    {(() => {
                                      const icon = getCuisineIcon(restaurant.ethnicity);
                                      if (icon?.type === 'emoji') {
                                        return <>{icon.value} <span>{restaurant.ethnicity || '–'}</span></>;
                                      } else if (icon?.type === 'flag') {
                                        return (
                                          <>
                                            <img 
                                              src={`https://flagcdn.com/16x12/${icon.code}.png`}
                                              srcSet={`https://flagcdn.com/32x24/${icon.code}.png 2x, https://flagcdn.com/48x36/${icon.code}.png 3x`}
                                              width="16"
                                              height="12"
                                              alt=""
                                              style={{ flexShrink: 0 }}
                                            />
                                            <span>{restaurant.ethnicity}</span>
                                          </>
                                        );
                                      }
                                      return restaurant.ethnicity || '–';
                                    })()}
                                  </div>
                                </td>
                                <td>
                                  {restaurant.averageRating || restaurant.overallRating ? (
                                    <div className="rating-box">
                                      ⭐ {(restaurant.averageRating || restaurant.overallRating).toFixed(1)}/5
                                    </div>
                                  ) : '–'}
                                </td>
                                <td>{formatPriceToDollars(restaurant.price)}</td>
                                <td>{formatDaysBack(restaurant.lastVisit)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                    })()}
                  </div>

                  <div className="dashboard-section dashboard-recent">
                    <h2 className="section-title">
                      🕐 Most Recent Visits (All Users)
                      <div className="section-footprints">
                        <span className="dino-track">
                          <span className="toe toe-left"></span>
                          <span className="toe toe-center"></span>
                          <span className="toe toe-right"></span>
                        </span>
                        <span className="dino-track track-offset">
                          <span className="toe toe-left"></span>
                          <span className="toe toe-center"></span>
                          <span className="toe toe-right"></span>
                        </span>
                      </div>
                    </h2>
                    {allRecentVisits.length === 0 ? (
                      <p className="section-empty">No visits recorded yet across all users.</p>
                    ) : (
                      <div className="table-container" key={refreshTrigger}>
                        <table className="restaurants-table">
                          <thead>
                            <tr>
                              <th className="restaurant-name-col">Restaurant</th>
                              <th>
                                <span className="mobile-only">📍</span>
                                <span className="desktop-only">Distance</span>
                              </th>
                              <th>Cuisine</th>
                              <th>
                                <span className="mobile-only">⭐</span>
                                <span className="desktop-only">My Rating</span>
                              </th>
                              <th>Price</th>
                              <th>
                                <span className="mobile-only">Last</span>
                                <span className="desktop-only">Visit Date</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {allRecentVisits.map((visit) => {
                              // Find the full restaurant object from allUsersRestaurants
                              const fullRestaurant = allUsersRestaurants.find(r => r.id === visit.restaurantId) || {
                                id: visit.restaurantId,
                                name: visit.restaurantName,
                                location: visit.restaurantLocation,
                                latitude: visit.restaurantLatitude,
                                longitude: visit.restaurantLongitude,
                                user_email: visit.user_email
                              };
                              
                              return (
                                <tr 
                                  key={`${visit.id}-${refreshTrigger}`} 
                                  className="restaurant-row"
                                  data-testid={`all-recent-${visit.id}`}
                                >
                                  <td 
                                    className="restaurant-name-cell clickable"
                                    onClick={() => showVisitHistory(fullRestaurant)}
                                  >
                                    <strong>{visit.restaurantName}</strong>
                                  </td>
                                  <td>
                                    {fullRestaurant.distance ? `${fullRestaurant.distance.toFixed(1)} mi` : '–'}
                                  </td>
                                  <td className="cuisine-col">
                                    <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', gap: '4px' }}>
                                      {(() => {
                                        const icon = getCuisineIcon(fullRestaurant.ethnicity);
                                        if (icon?.type === 'emoji') {
                                          return <>{icon.value} <span>{fullRestaurant.ethnicity || '–'}</span></>;
                                        } else if (icon?.type === 'flag') {
                                          return (
                                            <>
                                              <img 
                                                src={`https://flagcdn.com/16x12/${icon.code}.png`}
                                                srcSet={`https://flagcdn.com/32x24/${icon.code}.png 2x, https://flagcdn.com/48x36/${icon.code}.png 3x`}
                                                width="16"
                                                height="12"
                                                alt={fullRestaurant.ethnicity}
                                                style={{ flexShrink: 0 }}
                                              />
                                              <span>{fullRestaurant.ethnicity}</span>
                                            </>
                                          );
                                        }
                                        return fullRestaurant.ethnicity || '–';
                                      })()}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="rating-box">
                                      {visit.rating ? `⭐ ${visit.rating.toFixed(1)}/5` : '–'}
                                    </div>
                                  </td>
                                  <td>{formatPriceToDollars(fullRestaurant.price)}</td>
                                  <td>
                                    <span className="mobile-only">{formatDateNoYear(visit.visitDate)}</span>
                                    <span className="desktop-only">{formatDateDisplay(visit.visitDate)}</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
                </>
              ) : filteredRestaurants.length === 0 ? (
                <div className="empty-state" data-testid="no-results">
                  <h2>No restaurants found</h2>
                  <p>Try a different search term</p>
                </div>
              ) : (
                <div>
                <table className="restaurants-table" data-testid="restaurants-table">
                  <thead>
                    <tr>
                      <th>Restaurant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRestaurants.map((restaurant) => (
                      <tr 
                        key={restaurant.id} 
                        className="restaurant-row"
                        data-testid={`restaurant-row-${restaurant.id}`}
                      >
                        <td 
                          className="restaurant-name-cell clickable"
                          onClick={() => showVisitHistory(restaurant)}
                          data-testid={`name-${restaurant.id}`}
                        >
                          <strong>{restaurant.name}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Restaurant Map - Outside content-wrapper, aligned with content area */}
        <div className={`map-section-wrapper ${activeTab === 'dashboard' ? 'my-dashboard-map' : 'all-dashboard-map'}`}>
          <div className="map-section">
            <h3>🗺️ Restaurant Locations Map</h3>
            <div className="map-container">
              <RestaurantMap 
                key={`map-${activeTab}-${restaurants.length}-${allUsersRestaurants.length}-${refreshTrigger}`}
                restaurants={activeTab === 'dashboard' ? restaurants : allUsersRestaurants} 
                userEmail={userEmail} 
              />
            </div>
          </div>
        </div>
          </>
        )}

        {activeTab === 'allRestaurants' && (
          <div className="content-wrapper">
            {/* Filter Panel - Same as dashboard */}
            <aside className={`filter-panel ${showMobileFilters ? 'mobile-open' : 'mobile-closed'}`} data-testid="filter-panel">
              <div className="filter-header">
                <h3>Filters</h3>
                {hasActiveFilters() && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearAllFilters}
                    className="clear-filters-btn"
                    data-testid="clear-all-filters"
                  >
                    Clear All
                  </Button>
                )}
              </div>
              {renderShowClosedToggle()}

              <div className="filter-section">
                <h4>Location</h4>
                <select 
                  value={filters.location[0] || ''} 
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      handleFilterChange('location', value);
                    }
                  }}
                  className="filter-select"
                  data-testid="location-select"
                >
                  <option value="">All Locations</option>
                  {Object.keys(locationsByState).sort().map(state => (
                    <optgroup key={state} label={state}>
                      {locationsByState[state].map(({ fullLocation, city }) => (
                        <option key={fullLocation} value={fullLocation}>
                          {city}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="filter-section">
                <h4>Cuisine</h4>
                <select
                  value=""
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      handleFilterChange('ethnicity', value);
                    }
                  }}
                  className="filter-select"
                  data-testid="cuisine-select"
                >
                  <option value="">Select Cuisine...</option>
                  {uniqueCuisines.length > 0 ? (
                    uniqueCuisines.map((cuisine) => (
                      <option key={cuisine} value={cuisine}>
                        {cuisine}
                      </option>
                    ))
                  ) : (
                    <option disabled>No cuisines available</option>
                  )}
                </select>
                {filters.ethnicity.length > 0 && (
                  <div className="selected-locations">
                    {filters.ethnicity.map((cuisine) => (
                      <span key={cuisine} className="location-tag">
                        {cuisine}
                        <button 
                          onClick={() => handleFilterChange('ethnicity', cuisine)}
                          className="remove-tag"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="filter-section">
                <h4>Ownership</h4>
                <select
                  value=""
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      handleFilterChange('ownership', value);
                    }
                  }}
                  className="filter-select"
                  data-testid="ownership-select"
                >
                  <option value="">Select Ownership...</option>
                  {uniqueOwnership.length > 0 ? (
                    uniqueOwnership.map((ownership) => (
                      <option key={ownership} value={ownership}>
                        {ownership}
                      </option>
                    ))
                  ) : (
                    <option disabled>No ownership available</option>
                  )}
                </select>
                {filters.ownership.length > 0 && (
                  <div className="selected-locations">
                    {filters.ownership.map((ownership) => (
                      <span key={ownership} className="location-tag">
                        {ownership}
                        <button 
                          onClick={() => handleFilterChange('ownership', ownership)}
                          className="remove-tag"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="filter-section">
                <h4>Amenities</h4>
                <select
                  value=""
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      handleFilterChange(value, true);
                    }
                  }}
                  className="filter-select"
                  data-testid="amenities-select"
                >
                  <option value="">Select Amenity...</option>
                  <option value="patio">🏖️ Patio</option>
                  <option value="dogFriendly">🐕 Dog Friendly</option>
                  <option value="heineken">🍺 Heineken</option>
                </select>
                {(filters.patio || filters.dogFriendly || filters.heineken) && (
                  <div className="selected-locations">
                    {filters.patio && (
                      <span className="location-tag">
                        🏖️ Patio
                        <button 
                          onClick={() => handleFilterChange('patio', true)}
                          className="remove-tag"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filters.dogFriendly && (
                      <span className="location-tag">
                        🐕 Dog Friendly
                        <button 
                          onClick={() => handleFilterChange('dogFriendly', true)}
                          className="remove-tag"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filters.heineken && (
                      <span className="location-tag">
                        🍺 Heineken
                        <button 
                          onClick={() => handleFilterChange('heineken', true)}
                          className="remove-tag"
                        >
                          ×
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="filter-section">
                <h4>Price</h4>
                <select
                  value=""
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      handleFilterChange('price', value);
                    }
                  }}
                  className="filter-select"
                  data-testid="price-select"
                >
                  <option value="">Select Price...</option>
                  {uniquePrices.length > 0 ? (
                    uniquePrices.map((price) => (
                      <option key={price} value={price}>
                        {price}
                      </option>
                    ))
                  ) : (
                    <option disabled>No prices available</option>
                  )}
                </select>
                {filters.price.length > 0 && (
                  <div className="selected-locations">
                    {filters.price.map((price) => (
                      <span key={price} className="location-tag">
                        {price}
                        <button 
                          onClick={() => handleFilterChange('price', price)}
                          className="remove-tag"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Copyright at bottom of filter panel */}
              <div style={{ 
                marginTop: 'auto', 
                paddingTop: '2rem', 
                borderTop: '1px solid #e2e8f0',
                textAlign: 'center'
              }}>
                <a 
                  href={footerSettings.siteUrl}
                  style={{ 
                    color: '#2c5f2d', 
                    textDecoration: 'none', 
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                    display: 'block',
                    marginBottom: '0.25rem'
                  }}
                >
                  {footerSettings.siteName}
                  <VersionBadge version={footerSettings.version} />
                </a>
                <p style={{ 
                  fontSize: '0.7rem', 
                  color: '#64748b',
                  margin: 0
                }}>
                  Created by {footerSettings.createdBy}
                </p>
              </div>
            </aside>

            <div className="all-restaurants-tab">
              <div className="all-restaurants-results" key={`my-restaurants-${refreshTrigger}`}>
              {filteredRestaurants.length === 0 ? (
                <div className="loading-message">
                  {searchQuery ? `No restaurants found matching "${searchQuery}"` : hasActiveFilters() ? 'No restaurants match your filters' : 'Loading all restaurants...'}
                </div>
              ) : (
                <>
                <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    onClick={fetchAllGoogleRatings}
                    className="fetch-all-ratings-btn"
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      backgroundColor: '#4285f4',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3367d6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4285f4'}
                    title="Fetch Google ratings for all restaurants with coordinates"
                  >
                    <RefreshCw size={14} />
                    Fetch All Google Ratings
                  </button>
                </div>
                <table className="restaurants-table" data-testid="all-restaurants-table" key={refreshTrigger}>
                  <thead>
                    <tr>
                      <th 
                        className="sortable-col"
                        onClick={() => handleSort('name')}
                        style={{ cursor: 'pointer' }}
                        title="Click to sort by restaurant name"
                      >
                        Restaurant <span className="sort-arrow">{sortColumn === 'name' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </th>
                      <th 
                        className="sortable-col"
                        onClick={() => handleSort('owner')}
                        style={{ cursor: 'pointer' }}
                        title="Click to sort by who added"
                      >
                        Added By <span className="sort-arrow">{sortColumn === 'owner' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </th>
                      <th 
                        className="sortable-col"
                        onClick={() => handleSort('location')}
                        style={{ cursor: 'pointer' }}
                        title="Click to sort by location"
                      >
                        Location <span className="sort-arrow">{sortColumn === 'location' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </th>
                      <th 
                        className="sortable-col"
                        onClick={() => handleSort('ethnicity')}
                        style={{ cursor: 'pointer' }}
                        title="Click to sort by cuisine"
                      >
                        Cuisine <span className="sort-arrow">{sortColumn === 'ethnicity' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </th>
                      <th>Favorite Items</th>
                      <th 
                        className="sortable-col"
                        onClick={() => handleSort('ownership')}
                        style={{ cursor: 'pointer' }}
                        title="Click to sort by ownership"
                      >
                        Ownership <span className="sort-arrow">{sortColumn === 'ownership' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </th>
                      <th 
                        className="sortable-col"
                        onClick={() => handleSort('price')}
                        style={{ cursor: 'pointer' }}
                        title="Click to sort by price"
                      >
                        Price <span className="sort-arrow">{sortColumn === 'price' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </th>
                      <th 
                        className="rating-col"
                        title="Ratings (★=Mine, G=Google)"
                      >
                        <div className="rating-header-container">
                          <span>★</span>
                          <span>G</span>
                        </div>
                      </th>
                      <th 
                        className="visits-col sortable-col"
                        onClick={() => handleSort('visitCount')}
                        style={{ cursor: 'pointer' }}
                        title="Click to sort by number of visits"
                      >
                        # <span className="sort-arrow">{sortColumn === 'visitCount' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </th>
                      <th 
                        className="sortable-col"
                        onClick={() => handleSort('lastVisit')}
                        style={{ cursor: 'pointer' }}
                        title="Click to sort by last visit (days back)"
                      >
                        Last <span className="sort-arrow">{sortColumn === 'lastVisit' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </th>
                      <th className="amenities-col">Amenities</th>
                      <th>Staff</th>
                      <th className="menu-col">Website</th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedRestaurants().map((restaurant) => (
                      <tr 
                        key={restaurant.id} 
                        className={`restaurant-row${mergePrimary?.id === restaurant.id ? ' merge-primary-row' : ''}`}
                        data-testid={`all-rest-${restaurant.id}`}
                        style={mergePrimary?.id === restaurant.id ? { background: '#fef08a', boxShadow: 'inset 4px 0 0 0 #ca8a04' } : undefined}
                      >
                        <td 
                          className="restaurant-name-cell clickable"
                          onClick={() => {
                            // Always show visit history dialog for My Restaurants table
                            showVisitHistory(restaurant);
                          }}
                          data-testid={`name-${restaurant.id}`}
                        >
                          <strong>{restaurant.name}</strong>
                          {restaurant.archived && <span className="closed-badge" data-testid={`closed-badge-${restaurant.id}`}>Closed</span>}
                        </td>
                        <td className="added-by-cell">
                          {restaurant.ownerDisplayName || restaurant.user_email || '–'}
                        </td>
                        <td>
                          {restaurant.latitude && restaurant.longitude ? (
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${restaurant.latitude},${restaurant.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="location-directions-link"
                              title="Click for directions"
                            >
                              {restaurant.location}
                              {restaurant.distance !== null && restaurant.distance !== undefined && (
                                <span className="distance-display-inline"> - {typeof restaurant.distance === 'number' ? restaurant.distance.toFixed(1) : restaurant.distance} miles</span>
                              )}
                            </a>
                          ) : (
                            <>
                              {restaurant.location}
                              {restaurant.distance !== null && restaurant.distance !== undefined && (
                                <span className="distance-display-inline"> - {typeof restaurant.distance === 'number' ? restaurant.distance.toFixed(1) : restaurant.distance} miles</span>
                              )}
                            </>
                          )}
                        </td>
                        <td>
                          <span className="cuisine-badge">
                            {(() => {
                              const icon = getCuisineIcon(restaurant.ethnicity);
                              if (icon?.type === 'flag') {
                                return <img src={`https://flagcdn.com/16x12/${icon.code}.png`} alt="" className="cuisine-flag" />;
                              } else if (icon?.type === 'emoji') {
                                return <span className="cuisine-emoji">{icon.value} </span>;
                              }
                              return null;
                            })()}
                            {restaurant.ethnicity || '–'}
                          </span>
                        </td>
                        <td className="favorites-cell">{restaurant.favoriteItems}</td>
                        <td>
                          <span className={`ownership-badge ${restaurant.ownership?.toLowerCase().replace(/\s+/g, '-')}`}>
                            {getOwnershipIcon(restaurant.ownership) && (
                              <span className="ownership-icon">{getOwnershipIcon(restaurant.ownership)} </span>
                            )}
                            {restaurant.ownership || '–'}
                          </span>
                        </td>
                        <td>
                          <span className={`price-badge ${restaurant.price?.toLowerCase()}`}>
                            {formatPriceToDollars(restaurant.price)}
                          </span>
                        </td>
                        <td className="rating-cell">
                          <div className="rating-display-container">
                            <span className="rating-column">
                              {restaurant.averageRating || restaurant.overallRating ? (
                                <span className="rating-number" title="Your rating">
                                  {(restaurant.averageRating || restaurant.overallRating).toFixed(1)}
                                </span>
                              ) : (
                                <span className="rating-number">–</span>
                              )}
                            </span>
                            <span className="rating-column">
                              {restaurant.googleRating ? (
                                <a 
                                  href={`https://www.google.com/search?q=${encodeURIComponent(restaurant.name + ' ' + restaurant.location)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="external-rating-link google-rating" 
                                  title="View on Google"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {restaurant.googleRating}
                                </a>
                              ) : (
                                <span className="rating-number">–</span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="visits-cell">
                          <span className="visit-count-badge">
                            {restaurant.visitCount || 0}
                          </span>
                        </td>
                        <td>{formatDaysBack(restaurant.lastVisit)}</td>
                        <td className="amenities-cell">
                          <div className="amenities-icons">
                            <span 
                              className={`amenity-icon ${!restaurant.patio ? 'amenity-unavailable' : ''}`}
                              title={restaurant.patio ? "Patio Available" : "No Patio"}
                            >
                              🏖️
                            </span>
                            <span 
                              className={`amenity-icon ${!restaurant.dogFriendly ? 'amenity-unavailable' : ''}`}
                              title={restaurant.dogFriendly ? "Dog Friendly" : "Not Dog Friendly"}
                            >
                              🐕
                            </span>
                            <span 
                              className={`amenity-icon ${!restaurant.heineken ? 'amenity-unavailable' : ''}`}
                              title={restaurant.heineken ? "Heineken Available" : "No Heineken"}
                            >
                              🍺
                            </span>
                          </div>
                        </td>
                        <td className="staff-cell">
                          <span className="staff-notes">
                            {restaurant.staff || '–'}
                          </span>
                        </td>
                        <td className="menu-cell">
                          {restaurant.menuLink ? (
                            <a
                              href={restaurant.menuLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="menu-link-btn"
                              data-testid={`menu-link-${restaurant.id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Website
                            </a>
                          ) : (
                            <span className="no-menu">–</span>
                          )}
                        </td>
                        <td className="actions-cell">
                          <div className="table-actions">
                            <Button
                              data-testid={`photos-btn-${restaurant.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => openPhotosDialog(restaurant)}
                              className={`table-action-btn ${restaurant.photos && restaurant.photos.length > 0 ? 'has-photos' : 'no-photos'}`}
                              title={restaurant.photos && restaurant.photos.length > 0 ? `View ${restaurant.photos.length} photo(s)` : "No photos yet - Click to upload"}
                            >
                              <Camera size={11} />
                            </Button>
                            <Button
                              data-testid={`add-visit-btn-${restaurant.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => openVisitDialog(restaurant)}
                              className="table-action-btn"
                              title="Add a new visit to this restaurant"
                            >
                              <Plus size={11} />
                            </Button>
                            <Button
                              data-testid={`edit-btn-${restaurant.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(restaurant)}
                              className="table-action-btn"
                              title="Edit restaurant details"
                            >
                              <Edit2 size={11} />
                            </Button>
                            <Button
                              data-testid={`merge-btn-${restaurant.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => handleMergeRestaurant(restaurant)}
                              className={`table-action-btn ${mergePrimary?.id === restaurant.id ? 'merge-primary-active' : ''}`}
                              style={mergePrimary?.id === restaurant.id ? { background: '#fef08a', border: '2px solid #ca8a04' } : (mergePrimary ? { background: '#dbeafe', border: '2px solid #2563eb' } : undefined)}
                              title={
                                mergePrimary
                                  ? (mergePrimary.id === restaurant.id
                                      ? `Primary selected — click any other restaurant's 🔀 to merge it here, or click this again to cancel`
                                      : `Merge "${restaurant.name}" INTO "${mergePrimary.name}"`)
                                  : 'Merge this restaurant with another (this becomes the primary - its name & address are kept)'
                              }
                            >
                              🔀
                            </Button>
                            {canArchive(restaurant) && (
                              <Button
                                data-testid={`archive-btn-${restaurant.id}`}
                                variant="outline"
                                size="sm"
                                onClick={() => handleArchiveRestaurant(restaurant)}
                                className={`table-action-btn ${restaurant.archived ? 'unarchive-btn' : 'archive-btn'}`}
                                title={restaurant.archived ? 'Reopen this restaurant' : 'Mark this restaurant as closed (keeps visits & photos)'}
                              >
                                {restaurant.archived ? <ArchiveRestore size={11} /> : <Archive size={11} />}
                              </Button>
                            )}
                            <Button
                              data-testid={`delete-btn-${restaurant.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteRestaurant(restaurant.id, restaurant.name)}
                              className="table-action-btn delete-btn"
                              title="Delete this restaurant"
                            >
                              <Trash2 size={11} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </>
              )}
            </div>
            </div>
          </div>
        )}

        {activeTab === 'allUsersRestaurants' && (
          <div className="content-wrapper">
            {/* Filter Panel - Same as dashboard */}
            <aside className={`filter-panel ${showMobileFilters ? 'mobile-open' : 'mobile-closed'}`} data-testid="filter-panel">
              <div className="filter-header">
                <h3>Filters</h3>
                {hasActiveFilters() && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearAllFilters}
                    className="clear-filters-btn"
                    data-testid="clear-all-filters"
                  >
                    Clear All
                  </Button>
                )}
              </div>
              {renderShowClosedToggle()}

              <div className="filter-section">
                <h4>Location</h4>
                <select 
                  value={filters.location[0] || ''} 
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      handleFilterChange('location', value);
                    }
                  }}
                  className="filter-select"
                  data-testid="location-select"
                >
                  <option value="">All Locations</option>
                  {Object.keys(locationsByState).sort().map(state => (
                    <optgroup key={state} label={state}>
                      {locationsByState[state].map(({ fullLocation, city }) => (
                        <option key={fullLocation} value={fullLocation}>
                          {city}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="filter-section">
                <h4>Cuisine</h4>
                <select
                  value=""
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      handleFilterChange('ethnicity', value);
                    }
                  }}
                  className="filter-select"
                  data-testid="cuisine-select"
                >
                  <option value="">Select Cuisine...</option>
                  {uniqueCuisines.length > 0 ? (
                    uniqueCuisines.map((cuisine) => (
                      <option key={cuisine} value={cuisine}>
                        {cuisine}
                      </option>
                    ))
                  ) : (
                    <option disabled>No cuisines available</option>
                  )}
                </select>
                {filters.ethnicity.length > 0 && (
                  <div className="selected-locations">
                    {filters.ethnicity.map((cuisine) => (
                      <span key={cuisine} className="location-tag">
                        {cuisine}
                        <button 
                          onClick={() => handleFilterChange('ethnicity', cuisine)}
                          className="remove-tag"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="filter-section">
                <h4>Ownership</h4>
                <select
                  value=""
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      handleFilterChange('ownership', value);
                    }
                  }}
                  className="filter-select"
                  data-testid="ownership-select"
                >
                  <option value="">Select Ownership...</option>
                  {uniqueOwnership.length > 0 ? (
                    uniqueOwnership.map((ownership) => (
                      <option key={ownership} value={ownership}>
                        {ownership}
                      </option>
                    ))
                  ) : (
                    <option disabled>No ownership available</option>
                  )}
                </select>
                {filters.ownership.length > 0 && (
                  <div className="selected-locations">
                    {filters.ownership.map((ownership) => (
                      <span key={ownership} className="location-tag">
                        {ownership}
                        <button 
                          onClick={() => handleFilterChange('ownership', ownership)}
                          className="remove-tag"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="filter-section">
                <h4>Amenities</h4>
                <select
                  value=""
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      handleFilterChange(value, true);
                    }
                  }}
                  className="filter-select"
                  data-testid="amenities-select"
                >
                  <option value="">Select Amenity...</option>
                  <option value="patio">🏖️ Patio</option>
                  <option value="dogFriendly">🐕 Dog Friendly</option>
                  <option value="heineken">🍺 Heineken</option>
                </select>
                {(filters.patio || filters.dogFriendly || filters.heineken) && (
                  <div className="selected-locations">
                    {filters.patio && (
                      <span className="location-tag">
                        🏖️ Patio
                        <button 
                          onClick={() => handleFilterChange('patio', true)}
                          className="remove-tag"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filters.dogFriendly && (
                      <span className="location-tag">
                        🐕 Dog Friendly
                        <button 
                          onClick={() => handleFilterChange('dogFriendly', true)}
                          className="remove-tag"
                        >
                          ×
                        </button>
                      </span>
                    )}
                    {filters.heineken && (
                      <span className="location-tag">
                        🍺 Heineken
                        <button 
                          onClick={() => handleFilterChange('heineken', true)}
                          className="remove-tag"
                        >
                          ×
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="filter-section">
                <h4>Price</h4>
                <select
                  value=""
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      handleFilterChange('price', value);
                    }
                  }}
                  className="filter-select"
                  data-testid="price-select"
                >
                  <option value="">Select Price...</option>
                  {uniquePrices.length > 0 ? (
                    uniquePrices.map((price) => (
                      <option key={price} value={price}>
                        {price}
                      </option>
                    ))
                  ) : (
                    <option disabled>No prices available</option>
                  )}
                </select>
                {filters.price.length > 0 && (
                  <div className="selected-locations">
                    {filters.price.map((price) => (
                      <span key={price} className="location-tag">
                        {price}
                        <button 
                          onClick={() => handleFilterChange('price', price)}
                          className="remove-tag"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Copyright at bottom of filter panel */}
              <div style={{ 
                marginTop: 'auto', 
                paddingTop: '2rem', 
                borderTop: '1px solid #e2e8f0',
                textAlign: 'center'
              }}>
                <a 
                  href={footerSettings.siteUrl}
                  style={{ 
                    color: '#2c5f2d', 
                    textDecoration: 'none', 
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                    display: 'block',
                    marginBottom: '0.25rem'
                  }}
                >
                  {footerSettings.siteName}
                  <VersionBadge version={footerSettings.version} />
                </a>
                <p style={{ 
                  fontSize: '0.7rem', 
                  color: '#64748b',
                  margin: 0
                }}>
                  Created by {footerSettings.createdBy}
                </p>
              </div>
            </aside>

            <div className="all-restaurants-tab">
              <div className="all-restaurants-results" key={`all-users-restaurants-${refreshTrigger}`}>>
              {filteredRestaurants.length === 0 ? (
                <div className="loading-message">
                  {searchQuery ? `No restaurants found matching "${searchQuery}"` : hasActiveFilters() ? 'No restaurants match your filters' : 'Loading all restaurants...'}
                </div>
              ) : (
                <>
                <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button
                    onClick={fetchAllGoogleRatings}
                    className="fetch-all-ratings-btn"
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      backgroundColor: '#4285f4',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3367d6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4285f4'}
                    title="Fetch Google ratings for all restaurants with coordinates"
                  >
                    <RefreshCw size={14} />
                    Fetch All Google Ratings
                  </button>
                </div>
                <table className="restaurants-table" data-testid="all-users-restaurants-table" key={refreshTrigger}>
                  <thead>
                    <tr>
                      <th 
                        className="sortable-col"
                        onClick={() => {
                          if (allUsersSortColumn === 'name') {
                            setAllUsersSortOrder(allUsersSortOrder === 'asc' ? 'desc' : 'asc');
                          } else {
                            setAllUsersSortColumn('name');
                            setAllUsersSortOrder('asc');
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                        title="Click to sort by restaurant name"
                      >
                        Restaurant {allUsersSortColumn === 'name' ? (allUsersSortOrder === 'asc' ? '↑' : '↓') : '⇅'}
                      </th>
                      <th>Added By</th>
                      <th 
                        className="sortable-col"
                        onClick={() => {
                          if (allUsersSortColumn === 'location') {
                            setAllUsersSortOrder(allUsersSortOrder === 'asc' ? 'desc' : 'asc');
                          } else {
                            setAllUsersSortColumn('location');
                            setAllUsersSortOrder('asc');
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                        title="Click to sort by location"
                      >
                        Location {allUsersSortColumn === 'location' ? (allUsersSortOrder === 'asc' ? '↑' : '↓') : '⇅'}
                      </th>
                      <th 
                        className="sortable-col"
                        onClick={() => {
                          if (allUsersSortColumn === 'ethnicity') {
                            setAllUsersSortOrder(allUsersSortOrder === 'asc' ? 'desc' : 'asc');
                          } else {
                            setAllUsersSortColumn('ethnicity');
                            setAllUsersSortOrder('asc');
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                        title="Click to sort by cuisine"
                      >
                        Cuisine {allUsersSortColumn === 'ethnicity' ? (allUsersSortOrder === 'asc' ? '↑' : '↓') : '⇅'}
                      </th>
                      <th>Favorite Items</th>
                      <th 
                        className="sortable-col"
                        onClick={() => {
                          if (allUsersSortColumn === 'ownership') {
                            setAllUsersSortOrder(allUsersSortOrder === 'asc' ? 'desc' : 'asc');
                          } else {
                            setAllUsersSortColumn('ownership');
                            setAllUsersSortOrder('asc');
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                        title="Click to sort by ownership"
                      >
                        Ownership {allUsersSortColumn === 'ownership' ? (allUsersSortOrder === 'asc' ? '↑' : '↓') : '⇅'}
                      </th>
                      <th 
                        className="sortable-col"
                        onClick={() => {
                          if (allUsersSortColumn === 'price') {
                            setAllUsersSortOrder(allUsersSortOrder === 'asc' ? 'desc' : 'asc');
                          } else {
                            setAllUsersSortColumn('price');
                            setAllUsersSortOrder('asc');
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                        title="Click to sort by price"
                      >
                        Price {allUsersSortColumn === 'price' ? (allUsersSortOrder === 'asc' ? '↑' : '↓') : '⇅'}
                      </th>
                      <th 
                        className="rating-col sortable-col"
                        onClick={() => {
                          if (allUsersSortColumn === 'rating') {
                            setAllUsersSortOrder(allUsersSortOrder === 'asc' ? 'desc' : 'asc');
                          } else {
                            setAllUsersSortColumn('rating');
                            setAllUsersSortOrder('asc');
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                        title="Click to sort by rating (★=Mine, G=Google)"
                      >
                        <div className="rating-header-container">
                          <span>★</span>
                          <span>G</span>
                          <span className="sort-arrow">{allUsersSortColumn === 'rating' ? (allUsersSortOrder === 'asc' ? '↑' : '↓') : '⇅'}</span>
                        </div>
                      </th>
                      <th className="visits-col" title="Number of visits">#</th>
                      <th title="Days since last visit">Last</th>
                      <th className="amenities-col">Amenities</th>
                      <th>Staff</th>
                      <th className="menu-col">Website</th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...filteredRestaurants]
                      .sort((a, b) => {
                      let aVal, bVal;
                      
                      switch(allUsersSortColumn) {
                        case 'name':
                          aVal = a.name?.toLowerCase() || '';
                          bVal = b.name?.toLowerCase() || '';
                          break;
                        case 'location':
                          aVal = a.location?.toLowerCase() || '';
                          bVal = b.location?.toLowerCase() || '';
                          break;
                        case 'owner':
                          aVal = a.ownerDisplayName?.toLowerCase() || '';
                          bVal = b.ownerDisplayName?.toLowerCase() || '';
                          break;
                        case 'distance':
                          aVal = a.distance ?? Infinity;
                          bVal = b.distance ?? Infinity;
                          break;
                        case 'ethnicity':
                          aVal = a.ethnicity?.toLowerCase() || '';
                          bVal = b.ethnicity?.toLowerCase() || '';
                          break;
                        case 'ownership':
                          aVal = a.ownership?.toLowerCase() || '';
                          bVal = b.ownership?.toLowerCase() || '';
                          break;
                        case 'price':
                          const priceOrder = { 'Cheap': 1, 'Reasonable': 2, 'Expensive': 3 };
                          aVal = priceOrder[a.price] || 0;
                          bVal = priceOrder[b.price] || 0;
                          break;
                        case 'rating':
                          aVal = a.averageRating || a.overallRating || 0;
                          bVal = b.averageRating || b.overallRating || 0;
                          break;
                        case 'visits':
                          aVal = a.visitCount || 0;
                          bVal = b.visitCount || 0;
                          break;
                        default:
                          return 0;
                      }
                      
                      if (aVal < bVal) return allUsersSortOrder === 'asc' ? -1 : 1;
                      if (aVal > bVal) return allUsersSortOrder === 'asc' ? 1 : -1;
                      return 0;
                    }).map((restaurant) => (
                      <tr 
                        key={restaurant.id} 
                        className={`restaurant-row${mergePrimary?.id === restaurant.id ? ' merge-primary-row' : ''}`}
                        data-testid={`all-users-rest-${restaurant.id}`}
                        style={mergePrimary?.id === restaurant.id ? { background: '#fef08a', boxShadow: 'inset 4px 0 0 0 #ca8a04' } : undefined}
                      >
                        <td 
                          className="restaurant-name-cell clickable"
                          onClick={() => {
                            if (window.innerWidth <= 768) {
                              openRestaurantDetailsDialog(restaurant);
                            } else {
                              showVisitHistory(restaurant);
                            }
                          }}
                          data-testid={`name-${restaurant.id}`}
                        >
                          <strong>{restaurant.name}</strong>
                          {restaurant.archived && <span className="closed-badge" data-testid={`closed-badge-${restaurant.id}`}>Closed</span>}
                        </td>
                        <td className="added-by-cell">
                          {restaurant.ownerDisplayName || restaurant.user_email || '–'}
                        </td>
                        <td>
                          {restaurant.latitude && restaurant.longitude ? (
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${restaurant.latitude},${restaurant.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="location-directions-link"
                              title="Click for directions"
                            >
                              {restaurant.location}
                              {restaurant.distance !== null && restaurant.distance !== undefined && (
                                <span className="distance-display-inline"> - {typeof restaurant.distance === 'number' ? restaurant.distance.toFixed(1) : restaurant.distance} miles</span>
                              )}
                            </a>
                          ) : (
                            <>
                              {restaurant.location}
                              {restaurant.distance !== null && restaurant.distance !== undefined && (
                                <span className="distance-display-inline"> - {typeof restaurant.distance === 'number' ? restaurant.distance.toFixed(1) : restaurant.distance} miles</span>
                              )}
                            </>
                          )}
                        </td>
                        <td>
                          <span className="cuisine-badge">
                            {(() => {
                              const icon = getCuisineIcon(restaurant.ethnicity);
                              if (icon?.type === 'flag') {
                                return <img src={`https://flagcdn.com/16x12/${icon.code}.png`} alt="" className="cuisine-flag" />;
                              } else if (icon?.type === 'emoji') {
                                return <span className="cuisine-emoji">{icon.value} </span>;
                              }
                              return null;
                            })()}
                            {restaurant.ethnicity || '–'}
                          </span>
                        </td>
                        <td className="favorites-cell">{restaurant.favoriteItems}</td>
                        <td>
                          <span className={`ownership-badge ${restaurant.ownership?.toLowerCase().replace(/\s+/g, '-')}`}>
                            {getOwnershipIcon(restaurant.ownership) && (
                              <span className="ownership-icon">{getOwnershipIcon(restaurant.ownership)} </span>
                            )}
                            {restaurant.ownership || '–'}
                          </span>
                        </td>
                        <td>
                          <span className={`price-badge ${restaurant.price?.toLowerCase()}`}>
                            {formatPriceToDollars(restaurant.price)}
                          </span>
                        </td>
                        <td className="rating-cell">
                          <div className="rating-display-container">
                            <span className="rating-column">
                              {restaurant.averageRating || restaurant.overallRating ? (
                                <span className="rating-number" title="Your rating">
                                  {(restaurant.averageRating || restaurant.overallRating).toFixed(1)}
                                </span>
                              ) : (
                                <span className="rating-number">–</span>
                              )}
                            </span>
                            <span className="rating-column">
                              {restaurant.googleRating ? (
                                <a 
                                  href={`https://www.google.com/search?q=${encodeURIComponent(restaurant.name + ' ' + restaurant.location)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="external-rating-link google-rating" 
                                  title="View on Google"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {restaurant.googleRating}
                                </a>
                              ) : (
                                <span className="rating-number">–</span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="visits-cell">
                          <span className="visit-count-badge">
                            {restaurant.visitCount || 0}
                          </span>
                        </td>
                        <td>{formatDaysBack(restaurant.lastVisit)}</td>
                        <td className="amenities-cell">
                          <div className="amenities-icons">
                            <span 
                              className={`amenity-icon ${!restaurant.patio ? 'amenity-unavailable' : ''}`}
                              title={restaurant.patio ? "Patio Available" : "No Patio"}
                            >
                              🏖️
                            </span>
                            <span 
                              className={`amenity-icon ${!restaurant.dogFriendly ? 'amenity-unavailable' : ''}`}
                              title={restaurant.dogFriendly ? "Dog Friendly" : "Not Dog Friendly"}
                            >
                              🐕
                            </span>
                            <span 
                              className={`amenity-icon ${!restaurant.heineken ? 'amenity-unavailable' : ''}`}
                              title={restaurant.heineken ? "Heineken Available" : "No Heineken"}
                            >
                              🍺
                            </span>
                          </div>
                        </td>
                        <td className="staff-cell">
                          <span className="staff-notes">
                            {restaurant.staff || '–'}
                          </span>
                        </td>
                        <td className="menu-cell">
                          {restaurant.menuLink ? (
                            <a
                              href={restaurant.menuLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="menu-link-btn"
                              data-testid={`menu-link-${restaurant.id}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Website
                            </a>
                          ) : (
                            <span className="no-menu">–</span>
                          )}
                        </td>
                        <td className="actions-cell">
                          <div className="table-actions">
                            <Button
                              data-testid={`photos-btn-${restaurant.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => openPhotosDialog(restaurant)}
                              className={`table-action-btn ${restaurant.photos && restaurant.photos.length > 0 ? 'has-photos' : 'no-photos'}`}
                              title={restaurant.photos && restaurant.photos.length > 0 ? `View ${restaurant.photos.length} photo(s)` : "No photos yet - Click to upload"}
                            >
                              <Camera size={11} />
                            </Button>
                            <Button
                              data-testid={`add-visit-btn-${restaurant.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => openVisitDialog(restaurant)}
                              className="table-action-btn"
                              title="Add a new visit to this restaurant"
                            >
                              <Plus size={11} />
                            </Button>
                            <Button
                              data-testid={`edit-btn-${restaurant.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(restaurant)}
                              className="table-action-btn"
                              title="Edit restaurant details"
                            >
                              <Edit2 size={11} />
                            </Button>
                            <Button
                              data-testid={`merge-btn-${restaurant.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => handleMergeRestaurant(restaurant)}
                              className={`table-action-btn ${mergePrimary?.id === restaurant.id ? 'merge-primary-active' : ''}`}
                              style={mergePrimary?.id === restaurant.id ? { background: '#fef08a', border: '2px solid #ca8a04' } : (mergePrimary ? { background: '#dbeafe', border: '2px solid #2563eb' } : undefined)}
                              title={
                                mergePrimary
                                  ? (mergePrimary.id === restaurant.id
                                      ? `Primary selected — click any other restaurant's 🔀 to merge it here, or click this again to cancel`
                                      : `Merge "${restaurant.name}" INTO "${mergePrimary.name}"`)
                                  : 'Merge this restaurant with another (this becomes the primary - its name & address are kept)'
                              }
                            >
                              🔀
                            </Button>
                            {canArchive(restaurant) && (
                              <Button
                                data-testid={`archive-btn-${restaurant.id}`}
                                variant="outline"
                                size="sm"
                                onClick={() => handleArchiveRestaurant(restaurant)}
                                className={`table-action-btn ${restaurant.archived ? 'unarchive-btn' : 'archive-btn'}`}
                                title={restaurant.archived ? 'Reopen this restaurant' : 'Mark this restaurant as closed (keeps visits & photos)'}
                              >
                                {restaurant.archived ? <ArchiveRestore size={11} /> : <Archive size={11} />}
                              </Button>
                            )}
                            <Button
                              data-testid={`delete-btn-${restaurant.id}`}
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteRestaurant(restaurant.id, restaurant.name)}
                              className="table-action-btn delete-btn"
                              title="Delete this restaurant"
                            >
                              <Trash2 size={11} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </>
              )}
            </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={isAddOpen} onOpenChange={(open) => {
        setIsAddOpen(open);
        if (!open) restorePreviousTab();
      }}>
        <DialogContent className="dialog-content">
          <DialogHeader>
            <DialogTitle>Add New Restaurant</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddRestaurant} className="restaurant-form">
            {duplicateWarning && (
              <div className="duplicate-warning-banner" data-testid="duplicate-warning">
                <div className="warning-header">
                  <span className="warning-icon">⚠️</span>
                  <strong>Restaurant Already Exists</strong>
                </div>
                <div className="warning-details">
                  <p>
                    <strong>"{duplicateWarning.name}"</strong> already exists
                    {!duplicateWarning.isOwnRestaurant && (
                      <span className="owner-info"> (Added by {duplicateWarning.ownerDisplayName})</span>
                    )}
                  </p>
                  <p className="warning-info">
                    📍 {duplicateWarning.location}
                    {duplicateWarning.ethnicity && ` • 🍽️ ${duplicateWarning.ethnicity}`}
                    {duplicateWarning.price && ` • 💵 ${duplicateWarning.price}`}
                  </p>
                  <p className="warning-hint">
                    {duplicateWarning.isOwnRestaurant 
                      ? "You'll be asked if this is the same restaurant when you submit."
                      : "This restaurant was added by another user. You can still add it if it's a different location."}
                  </p>
                  <Button 
                    onClick={handleAddVisitFromWarning}
                    className="add-visit-from-warning-btn"
                    style={{
                      marginTop: '10px',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      width: '100%'
                    }}
                  >
                    📝 Add Visit to This Restaurant Instead
                  </Button>
                </div>
              </div>
            )}
            <div className="form-grid">
              <div className="form-field">
                <Label>Name *</Label>
                <Input
                  data-testid="form-name"
                  required
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({...formData, name: e.target.value});
                  }}
                  onBlur={(e) => {
                    const trimmedName = e.target.value.trim();
                    if (trimmedName) {
                      checkForDuplicate(trimmedName);
                    }
                  }}
                />
              </div>
              <div className="form-field">
                <Label>Full Address *</Label>
                <div className="address-field-container">
                  <textarea
                    data-testid="form-full-address"
                    name="fullAddress"
                    required
                    value={formData.fullAddress || ''}
                    onChange={handleFullAddressChange}
                    onInput={handleFullAddressChange}
                    placeholder="e.g., 123 Main St, Dallas, TX 75201"
                    rows={3}
                    className="full-address-textarea"
                  />
                  <button
                    type="button"
                    className="location-icon-btn"
                    disabled={uploadingPhoto}
                    onClick={async (e) => {
                      const btn = e.currentTarget;
                      if (!navigator.geolocation) {
                        alert('Geolocation is not supported by your browser');
                        return;
                      }
                      
                      // Show loading state
                      btn.innerHTML = '⏳';
                      btn.disabled = true;
                      
                      navigator.geolocation.getCurrentPosition(
                        async (position) => {
                          const { latitude, longitude } = position.coords;
                          
                          try {
                            // Use reverse geocoding with better accuracy parameters
                            const response = await fetch(
                              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
                              {
                                headers: {
                                  'User-Agent': 'Dine-A-Saurus-Restaurant-App'
                                }
                              }
                            );
                            
                            if (!response.ok) {
                              throw new Error('Geocoding service unavailable');
                            }
                            
                            const data = await response.json();
                            
                            if (data && data.display_name) {
                              const address = data.address;
                              
                              // State name to 2-letter code mapping
                              const stateCodeMap = {
                                'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
                                'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
                                'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
                                'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
                                'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
                                'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
                                'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
                                'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
                                'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
                                'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
                                'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
                                'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
                                'Wisconsin': 'WI', 'Wyoming': 'WY'
                              };
                              
                              // Convert state name to 2-letter code
                              const stateName = address.state || '';
                              
                              // Try to match state name (case-insensitive)
                              let stateCode = stateCodeMap[stateName];
                              if (!stateCode) {
                                // Try case-insensitive match
                                const stateEntry = Object.entries(stateCodeMap).find(
                                  ([key]) => key.toLowerCase() === stateName.toLowerCase()
                                );
                                stateCode = stateEntry ? stateEntry[1] : stateName;
                              }
                              
                              // Build Full Address (same as original, just replace state with stateCode)
                              const formattedAddress = [
                                address.house_number,
                                address.road,
                                address.city || address.town || address.village,
                                stateCode,  // Using state code instead of address.state
                                address.postcode
                              ].filter(Boolean).join(', ');
                              
                              // Format location as "City, State Code"
                              const city = address.city || address.town || address.village || '';
                              const locationFormatted = [city, stateCode].filter(Boolean).join(', ');
                              
                              setFormData({
                                ...formData,
                                fullAddress: formattedAddress,
                                location: locationFormatted,
                                latitude: latitude,
                                longitude: longitude
                              });
                              
                              btn.innerHTML = '✓';
                              setTimeout(() => {
                                btn.innerHTML = '📍';
                                btn.disabled = false;
                              }, 1500);
                            } else {
                              throw new Error('No address found');
                            }
                          } catch (error) {
                            console.error('Error getting address:', error);
                            btn.innerHTML = '📍';
                            btn.disabled = false;
                            alert('Unable to get address. Please try again or enter manually.');
                          }
                        },
                        (error) => {
                          btn.innerHTML = '📍';
                          btn.disabled = false;
                          let errorMsg = 'Unable to retrieve your location. ';
                          if (error.code === error.PERMISSION_DENIED) {
                            errorMsg += 'Please enable location permissions in your browser settings.';
                          } else if (error.code === error.TIMEOUT) {
                            errorMsg += 'Location request timed out. Please try again.';
                          } else {
                            errorMsg += 'Please check your location settings.';
                          }
                          alert(errorMsg);
                        },
                        {
                          enableHighAccuracy: true,
                          timeout: 10000,
                          maximumAge: 0
                        }
                      );
                    }}
                    title="Use my current location to auto-fill address"
                  >
                    📍
                  </button>
                </div>
                <p className="field-hint">Click 📍 to auto-fill from your current location</p>
              </div>
              <div className="form-field">
                <Label>Location (City, State) *</Label>
                <Input
                  data-testid="form-location"
                  required
                  value={formData.location}
                  onChange={handleLocationChange}
                  placeholder="e.g., Dallas, TX"
                />
                <p className="field-hint">Format: City, State (used for filtering)</p>
              </div>
              <div className="form-field">
                <Label>First Visit Date *</Label>
                <Input
                  type="date"
                  data-testid="form-first-visit-date"
                  required
                  value={formData.firstVisitDate}
                  onChange={(e) => setFormData({...formData, firstVisitDate: e.target.value})}
                />
                <p className="field-hint">Date of your first visit to this restaurant</p>
              </div>
              <div className="form-field full-width">
                <Label>Favorite Items *</Label>
                <Textarea
                  data-testid="form-favorite-items"
                  required
                  value={formData.favoriteItems}
                  onChange={(e) => setFormData({...formData, favoriteItems: e.target.value})}
                />
              </div>
              <div className="form-field full-width">
                <Label>Dislikes</Label>
                <Input
                  data-testid="form-dislikes"
                  value={formData.dislikes}
                  onChange={(e) => setFormData({...formData, dislikes: e.target.value})}
                />
              </div>
              <div className="form-field">
                <Label>Price</Label>
                <select
                  data-testid="form-price"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  className="form-select"
                >
                  <option value="">Select Price</option>
                  <option value="Cheap">Cheap ($)</option>
                  <option value="Bargain">Bargain ($$)</option>
                  <option value="Reasonable">Reasonable ($$$)</option>
                  <option value="Expensive">Expensive ($$$$)</option>
                  <option value="Stupid">Stupid ($$$$$)</option>
                </select>
              </div>
              <div className="form-field">
                <Label>Ownership</Label>
                <select
                  data-testid="form-ownership"
                  value={formData.ownership}
                  onChange={(e) => setFormData({...formData, ownership: e.target.value})}
                  className="form-select"
                >
                  <option value="">Select Ownership</option>
                  <option value="Local">Local</option>
                  <option value="Franchise">Franchise</option>
                  <option value="Chain">Chain</option>
                  <option value="Family Owned">Family Owned</option>
                  <option value="Franchise/Local">Franchise/Local</option>
                </select>
              </div>
              <div className="form-field">
                <Label>Ethnicity/Cuisine</Label>
                <select
                  data-testid="form-ethnicity"
                  value={formData.ethnicity}
                  onChange={(e) => {
                    setFormData({...formData, ethnicity: e.target.value});
                    if (e.target.value !== 'Other') {
                      setEthnicityOther('');
                    }
                  }}
                  className="form-select"
                >
                  <option value="">Select Cuisine</option>
                  {(() => {
                    // Comprehensive cuisine emoji mapping function
                    const getCuisineEmoji = (cuisine) => {
                      const lowerCuisine = cuisine.toLowerCase();
                      
                      // Country flags
                      const countryFlags = {
                        'american': '🇺🇸',
                        'chinese': '🇨🇳',
                        'french': '🇫🇷',
                        'german': '🇩🇪',
                        'greek': '🇬🇷',
                        'indian': '🇮🇳',
                        'irish': '🇮🇪',
                        'italian': '🇮🇹',
                        'japanese': '🇯🇵',
                        'korean': '🇰🇷',
                        'mexican': '🇲🇽',
                        'spanish': '🇪🇸',
                        'thai': '🇹🇭',
                        'turkish': '🇹🇷',
                        'vietnamese': '🇻🇳',
                        'brazilian': '🇧🇷',
                        'cuban': '🇨🇺',
                        'ethiopian': '🇪🇹',
                        'lebanese': '🇱🇧',
                        'moroccan': '🇲🇦',
                        'peruvian': '🇵🇪',
                        'polish': '🇵🇱',
                        'portuguese': '🇵🇹',
                        'russian': '🇷🇺'
                      };
                      
                      // Food type emojis
                      const foodEmojis = {
                        'bbq': '🥩',
                        'barbecue': '🥩',
                        'burger': '🍔',
                        'burgers': '🍔',
                        'breakfast': '🍳',
                        'brunch': '🥞',
                        'cafe': '☕',
                        'coffee': '☕',
                        'deli': '🥪',
                        'dessert': '🍰',
                        'pizza': '🍕',
                        'ramen': '🍜',
                        'seafood': '🐟',
                        'sushi': '🍣',
                        'steak': '🥩',
                        'steakhouse': '🥩',
                        'sandwich': '🥪',
                        'sandwiches': '🥪',
                        'taco': '🌮',
                        'tacos': '🌮',
                        'vegan': '🥗',
                        'vegetarian': '🥗',
                        'wings': '🍗',
                        'bakery': '🥖',
                        'bar': '🍺',
                        'pub': '🍺',
                        'grill': '🍖'
                      };
                      
                      // Check for country flag first
                      if (countryFlags[lowerCuisine]) {
                        return countryFlags[lowerCuisine];
                      }
                      
                      // Check for food emoji
                      if (foodEmojis[lowerCuisine]) {
                        return foodEmojis[lowerCuisine];
                      }
                      
                      // Default emoji for cuisines
                      return '🍽️';
                    };
                    
                    // Get all unique cuisines from database and merge with defaults
                    const allCuisines = [...new Set([
                      'American', 'BBQ', 'Chinese', 'Indian', 'Italian', 'Japanese', 
                      'Mediterranean', 'Mexican', 'Pizza', 'Seafood', 'Thai',
                      ...uniqueCuisines.filter(c => c && c !== 'Other')
                    ])].sort();
                    
                    return allCuisines.map(cuisine => {
                      const emoji = getCuisineEmoji(cuisine);
                      return (
                        <option key={cuisine} value={cuisine}>
                          {emoji} {cuisine}
                        </option>
                      );
                    });
                  })()}
                  <option value="Other">Other (Specify)</option>
                </select>
              </div>
              {formData.ethnicity === 'Other' && (
                <div className="form-field">
                  <Label>Specify Cuisine *</Label>
                  <Input
                    data-testid="form-ethnicity-other"
                    required
                    value={ethnicityOther}
                    onChange={(e) => setEthnicityOther(e.target.value)}
                    placeholder="Enter cuisine type"
                  />
                </div>
              )}
              <div className="form-field">
                <Label>Restroom Rating (0-5)</Label>
                <Input
                  data-testid="form-restroom-rating"
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={formData.restroomRating}
                  onChange={(e) => setFormData({...formData, restroomRating: e.target.value})}
                  placeholder="Rate the restrooms"
                />
                <p className="field-hint">4+ = Golden Commode 🏆</p>
              </div>
              <div className="form-field">
                <Label>Initial Rating (0-5)</Label>
                <Input
                  data-testid="form-initial-rating"
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={formData.initialRating}
                  onChange={(e) => setFormData({...formData, initialRating: e.target.value})}
                  placeholder="Your initial rating"
                />
                <p className="field-hint">Will auto-record first visit</p>
              </div>
              <div className="form-field">
                <Label>Guests (Optional)</Label>
                <Input
                  data-testid="form-initial-guests"
                  type="text"
                  value={formData.initialGuests}
                  onChange={(e) => setFormData({...formData, initialGuests: e.target.value})}
                  placeholder="Who joined you? (e.g., Sarah, Mike)"
                />
                <p className="field-hint">Track who was with you on first visit</p>
              </div>
              <div className="form-field">
                <Label>Cost (Optional)</Label>
                <Input
                  data-testid="form-initial-cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.initialCost}
                  onChange={(e) => setFormData({...formData, initialCost: e.target.value})}
                  placeholder="How much did you spend? (e.g., 45.50)"
                />
                <p className="field-hint">Track your spending on first visit</p>
              </div>
              {/* Google ratings will be fetched automatically via API - removed manual input */}
              <div className="form-field full-width">
                <Label>Staff Notes</Label>
                <Textarea
                  data-testid="form-staff"
                  value={formData.staff}
                  onChange={(e) => setFormData({...formData, staff: e.target.value})}
                  placeholder="Notes about staff service..."
                />
              </div>
              <div className="form-field">
                <Label>Website (auto-filled)</Label>
                <Input
                  data-testid="form-menu-link"
                  type="url"
                  value={formData.menuLink}
                  onChange={(e) => setFormData({...formData, menuLink: e.target.value})}
                  placeholder="https://... (will be searched automatically)"
                />
              </div>
              <div className="form-field" style={{ display: 'none' }}>
                <Label>Image URL</Label>
                <Input
                  data-testid="form-image-url"
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                  placeholder="https://..."
                />
              </div>
              <div className="form-field" style={{ display: 'none' }}>
                <Label>Latitude</Label>
                <Input
                  data-testid="form-latitude"
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                  placeholder="33.0456"
                />
              </div>
              <div className="form-field" style={{ display: 'none' }}>
                <Label>Longitude</Label>
                <Input
                  data-testid="form-longitude"
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                  placeholder="-96.9876"
                />
              </div>
              <div className="form-field full-width">
                <div className="amenities-checkboxes">
                  <label className="amenity-checkbox">
                    <Checkbox
                      data-testid="form-patio"
                      checked={formData.patio}
                      onCheckedChange={(checked) => setFormData({...formData, patio: checked})}
                    />
                    <span>🏖️ Patio</span>
                  </label>
                  <label className="amenity-checkbox">
                    <Checkbox
                      data-testid="form-dog-friendly"
                      checked={formData.dogFriendly}
                      onCheckedChange={(checked) => setFormData({...formData, dogFriendly: checked})}
                    />
                    <span>🐕 Dog Friendly</span>
                  </label>
                  <label className="amenity-checkbox">
                    <Checkbox
                      data-testid="form-heineken"
                      checked={formData.heineken}
                      onCheckedChange={(checked) => setFormData({...formData, heineken: checked})}
                    />
                    <span>🍺 Heineken</span>
                  </label>
                </div>
              </div>
              <div className="form-field full-width">
                <Label>📸 Add Photos (Optional)</Label>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleAddRestaurantPhotoSelect}
                    className="file-input"
                    id="add-restaurant-file-input"
                    style={{ display: 'none' }}
                  />
                  <Input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleAddRestaurantPhotoSelect}
                    className="file-input"
                    id="add-restaurant-camera-input"
                    style={{ display: 'none' }}
                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById('add-restaurant-file-input').click()}
                    style={{ flex: '1', minWidth: '140px' }}
                  >
                    <Camera size={16} />
                    {addRestaurantPhotos.length > 0 ? 'Add More Photos' : 'Choose Photos'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => document.getElementById('add-restaurant-camera-input').click()}
                    style={{ flex: '1', minWidth: '140px', background: '#059669' }}
                  >
                    <Camera size={16} />
                    Take Photo
                  </Button>
                </div>
                {addRestaurantPhotos.length > 0 && (
                  <div>
                    <p style={{ fontSize: '0.9em', color: '#059669', marginBottom: '10px' }}>
                      ✓ {addRestaurantPhotos.length} photo(s) selected
                    </p>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {Array.from(addRestaurantPhotos).map((photo, index) => (
                        <div key={index} style={{ 
                          position: 'relative', 
                          width: '80px', 
                          height: '80px',
                          border: '2px solid #059669',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}>
                          <img 
                            src={URL.createObjectURL(photo)} 
                            alt={`Preview ${index + 1}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newPhotos = Array.from(addRestaurantPhotos).filter((_, i) => i !== index);
                              setAddRestaurantPhotos(newPhotos);
                            }}
                            style={{
                              position: 'absolute',
                              top: '2px',
                              right: '2px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: '12px',
                              padding: 0
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div style={{ position: 'relative' }}>
              <Button data-testid="submit-add-btn" type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? '⏳ Adding Restaurant...' : 'Add Restaurant'}
              </Button>
              {isSubmitting && (
                <div style={{
                  marginTop: '10px',
                  padding: '10px',
                  background: '#f0f9ff',
                  border: '1px solid #3b82f6',
                  borderRadius: '6px',
                  color: '#1e40af',
                  fontSize: '0.9em',
                  textAlign: 'center'
                }}>
                  {uploadProgress.total > 0 ? (
                    <>📸 Uploading photos: {uploadProgress.current}/{uploadProgress.total}</>
                  ) : (
                    <>📤 Submitting... Please wait, do not close this window.</>
                  )}
                </div>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Check-In Dialog */}
      <Dialog open={isCheckInDialogOpen} onOpenChange={setIsCheckInDialogOpen}>
        <DialogContent className="dialog-content" style={{ maxWidth: '500px' }}>
          <DialogHeader>
            <DialogTitle>📍 Check In</DialogTitle>
          </DialogHeader>
          {detectedRestaurant && (
            <div style={{ padding: '1rem 0' }}>
              {detectedRestaurant.isNew ? (
                /* New Restaurant Detected */
                <>
                  <div style={{ 
                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', 
                    padding: '1.5rem', 
                    borderRadius: '12px',
                    marginBottom: '1.5rem',
                    border: '2px solid #22c55e'
                  }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#166534', marginBottom: '0.5rem' }}>
                      📍 New Location Detected!
                    </div>
                    <div style={{ fontSize: '0.95rem', color: '#166534', marginBottom: '0.5rem' }}>
                      {detectedRestaurant.detectedAddress || 'Address not available'}
                    </div>
                    {detectedRestaurant.location && (
                      <div style={{ fontSize: '0.9rem', color: '#15803d' }}>
                        {detectedRestaurant.location}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ marginBottom: '1.5rem' }}>
                    <p style={{ fontSize: '1rem', color: '#475569' }}>
                      No restaurants found near this location. Would you like to add a new restaurant?
                    </p>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
                    <Button 
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          location: detectedRestaurant.location || '',
                          fullAddress: detectedRestaurant.detectedAddress || '',
                          latitude: detectedRestaurant.latitude,
                          longitude: detectedRestaurant.longitude
                        }));
                        setIsCheckInDialogOpen(false);
                        fetchAllUsersRestaurants();
                        saveCurrentTab();
                        setIsAddOpen(true);
                      }}
                      style={{ 
                        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                        color: 'white',
                        padding: '0.75rem 1.5rem',
                        fontSize: '1rem',
                        fontWeight: '600'
                      }}
                    >
                      ➕ Add New Restaurant
                    </Button>
                    
                    <Button 
                      onClick={() => setIsCheckInDialogOpen(false)}
                      variant="outline"
                      style={{ 
                        padding: '0.75rem 1.5rem',
                        fontSize: '0.95rem',
                        color: '#64748b'
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                /* Existing Restaurant Found */
                <>
                  <div style={{ 
                    background: detectedRestaurant.isFromGroup 
                      ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                      : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', 
                    padding: '1.5rem', 
                    borderRadius: '12px',
                    marginBottom: '1.5rem',
                    border: detectedRestaurant.isFromGroup ? '2px solid #f59e0b' : '2px solid #0ea5e9'
                  }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: '600', color: detectedRestaurant.isFromGroup ? '#92400e' : '#0c4a6e', marginBottom: '0.5rem' }}>
                      📍 You're near:
                    </div>
                    <div style={{ fontSize: '1.3rem', fontWeight: '700', color: detectedRestaurant.isFromGroup ? '#b45309' : '#0369a1', marginBottom: '0.75rem' }}>
                      {detectedRestaurant.name}
                    </div>
                    <div style={{ fontSize: '0.95rem', color: detectedRestaurant.isFromGroup ? '#92400e' : '#0c4a6e', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <MapPin size={16} />
                      Distance: {detectedRestaurant.distance?.toFixed(2)} miles away
                    </div>
                    {detectedRestaurant.isFromGroup && (
                      <div style={{ 
                        marginTop: '0.75rem', 
                        padding: '0.5rem', 
                        background: 'rgba(245, 158, 11, 0.2)', 
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        color: '#92400e'
                      }}>
                        👥 From group member: {detectedRestaurant.ownerDisplayName || detectedRestaurant.user_email}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ marginBottom: '1.5rem' }}>
                    <p style={{ fontSize: '1rem', color: '#475569', marginBottom: '1rem' }}>
                      {detectedRestaurant.isFromGroup 
                        ? 'This restaurant was added by a group member. Adding a visit will also add it to your restaurants.'
                        : 'Is this the correct restaurant, or would you like to choose a different one?'
                      }
                    </p>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
                    <Button 
                      onClick={handleAcceptDetectedRestaurant}
                      style={{ 
                        background: detectedRestaurant.isFromGroup 
                          ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                          : 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                        color: 'white',
                        padding: '0.75rem 1.5rem',
                        fontSize: '1rem',
                        fontWeight: '600'
                      }}
                    >
                      ✅ Add Visit to {detectedRestaurant.name}
                    </Button>
                    
                    <Button 
                      onClick={handleChooseDifferentRestaurant}
                      variant="outline"
                      style={{ 
                        padding: '0.75rem 1.5rem',
                        fontSize: '1rem',
                        fontWeight: '600',
                        borderWidth: '2px'
                      }}
                    >
                      🔄 Choose Different Restaurant
                    </Button>
                    
                    <Button 
                      onClick={handleAddNewRestaurantFromCheckIn}
                      variant="outline"
                      style={{ 
                        padding: '0.75rem 1.5rem',
                        fontSize: '1rem',
                        fontWeight: '600',
                        borderWidth: '2px',
                        borderColor: '#10b981',
                        color: '#10b981'
                      }}
                    >
                      ➕ Add New Restaurant Here
                    </Button>
                    
                    <Button 
                      onClick={() => setIsCheckInDialogOpen(false)}
                      variant="outline"
                      style={{ 
                        padding: '0.75rem 1.5rem',
                        fontSize: '0.95rem',
                        color: '#64748b'
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isQuickAddVisitOpen} onOpenChange={(open) => {
        setIsQuickAddVisitOpen(open);
        if (!open) restorePreviousTab();
      }}>
        <DialogContent className="dialog-content">
          <DialogHeader>
            <DialogTitle>Add New Visit</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleQuickAddVisit} className="visit-form">
            <div className="form-field">
              <Label>Select Restaurant *</Label>
              <select
                data-testid="quick-visit-restaurant-select"
                value={selectedRestaurantId}
                onChange={(e) => handleRestaurantSelect(e.target.value)}
                className="form-select"
                required
              >
                <option value="">-- Choose a Restaurant --</option>
                {/* If coming from duplicate warning, include that restaurant even if not in user's list */}
                {selectedRestaurantForVisit && !restaurants.find(r => r.id === selectedRestaurantForVisit.id) && (
                  <option key={selectedRestaurantForVisit.id} value={selectedRestaurantForVisit.id}>
                    {selectedRestaurantForVisit.name} {selectedRestaurantForVisit.ownerDisplayName && `(Added by ${selectedRestaurantForVisit.ownerDisplayName})`}
                  </option>
                )}
                {restaurants
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((restaurant) => (
                    <option key={restaurant.id} value={restaurant.id}>
                      {restaurant.name}
                    </option>
                  ))}
              </select>
              {restaurants.length === 0 && (
                <p className="no-restaurants-message">No restaurants available.</p>
              )}
              <div className="add-restaurant-link">
                <span>Restaurant not in list? </span>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setIsQuickAddVisitOpen(false);
                    setIsAddOpen(true);
                  }}
                  data-testid="link-add-restaurant"
                >
                  Add New Restaurant
                </button>
              </div>
            </div>

            {selectedRestaurantId && (
              <>
                <div className="form-field">
                  <Label>Visit Date *</Label>
                  <Input
                    type="date"
                    value={visitDate}
                    onChange={(e) => setVisitDate(e.target.value)}
                    required
                    data-testid="quick-visit-date"
                  />
                </div>

                <div className="form-field">
                  <Label>Favorite Items</Label>
                  <Textarea
                    value={visitFavorites}
                    onChange={(e) => setVisitFavorites(e.target.value)}
                    placeholder="What did you love?"
                    data-testid="quick-visit-favorites"
                  />
                </div>

                <div className="form-field">
                  <Label>Dislikes</Label>
                  <Input
                    value={visitDislikes}
                    onChange={(e) => setVisitDislikes(e.target.value)}
                    placeholder="What didn't you like?"
                    data-testid="quick-visit-dislikes"
                  />
                </div>

                <div className="form-field">
                  <Label>Notes</Label>
                  <Textarea
                    value={visitNotes}
                    onChange={(e) => setVisitNotes(e.target.value)}
                    placeholder="Additional notes..."
                    data-testid="quick-visit-notes"
                  />
                </div>

                <div className="form-field">
                  <Label>Rating (0-5)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={visitRating}
                    onChange={(e) => setVisitRating(e.target.value)}
                    placeholder="Rate your experience (e.g., 4.6)"
                    data-testid="quick-visit-rating"
                  />
                  <p className="field-hint">Rate your experience: 0 (worst) to 5 (best)</p>
                </div>

                <div className="form-field">
                  <Label>Staff Notes</Label>
                  <Textarea
                    value={visitStaffNotes}
                    onChange={(e) => setVisitStaffNotes(e.target.value)}
                    placeholder="Notes about staff service..."
                    data-testid="quick-visit-staff"
                  />
                </div>

                <div className="form-field">
                  <Label>Bathroom Rating (0-5)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={visitBathroomRating}
                    onChange={(e) => setVisitBathroomRating(e.target.value)}
                    placeholder="4+ = Golden Commode 🏆"
                    data-testid="quick-visit-bathroom"
                  />
                </div>

                <div className="form-field">
                  <Label>Cost (Optional)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={visitCost}
                    onChange={(e) => setVisitCost(e.target.value)}
                    placeholder="How much did you spend? (e.g., 45.50)"
                    data-testid="quick-visit-cost"
                  />
                  <p className="field-hint">Track your spending - will be summed up on the restaurant history</p>
                </div>

                <div className="form-field">
                  <Label>Guests (Optional)</Label>
                  <Input
                    value={visitGuests}
                    onChange={(e) => setVisitGuests(e.target.value)}
                    placeholder="Who joined you? (e.g., Sarah, Mike)"
                    data-testid="quick-visit-guests"
                  />
                  <p className="field-hint">Names of people who joined you for this visit</p>
                </div>

                <div className="form-field">
                  <Label>📸 Add Photos (Optional)</Label>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    <Input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleAddVisitPhotoSelect}
                      className="file-input"
                      id="quick-visit-file-input"
                      style={{ display: 'none' }}
                    />
                    <Input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleAddVisitPhotoSelect}
                      className="file-input"
                      id="quick-visit-camera-input"
                      style={{ display: 'none' }}
                    />
                    <Button
                      type="button"
                      onClick={() => document.getElementById('quick-visit-file-input').click()}
                      style={{ flex: '1', minWidth: '140px' }}
                    >
                      <Camera size={16} />
                      {addVisitPhotos.length > 0 ? 'Add More' : 'Choose Photos'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => document.getElementById('quick-visit-camera-input').click()}
                      style={{ flex: '1', minWidth: '140px', background: '#059669' }}
                    >
                      <Camera size={16} />
                      Take Photo
                    </Button>
                  </div>
                  {addVisitPhotos.length > 0 && (
                    <div>
                      <p style={{ fontSize: '0.9em', color: '#059669', marginBottom: '10px' }}>
                        ✓ {addVisitPhotos.length} photo(s) selected
                      </p>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {Array.from(addVisitPhotos).map((photo, index) => (
                          <div key={index} style={{ 
                            position: 'relative', 
                            width: '80px', 
                            height: '80px',
                            border: '2px solid #059669',
                            borderRadius: '8px',
                            overflow: 'hidden'
                          }}>
                            <img 
                              src={URL.createObjectURL(photo)} 
                              alt={`Preview ${index + 1}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newPhotos = Array.from(addVisitPhotos).filter((_, i) => i !== index);
                                setAddVisitPhotos(newPhotos);
                              }}
                              style={{
                                position: 'absolute',
                                top: '2px',
                                right: '2px',
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '12px',
                                padding: 0
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ position: 'relative' }}>
                  <Button type="submit" className="submit-btn" data-testid="quick-visit-submit" disabled={isSubmitting}>
                    {isSubmitting ? '⏳ Adding Visit...' : 'Add Visit'}
                  </Button>
                  {isSubmitting && (
                    <div style={{
                      marginTop: '10px',
                      padding: '10px',
                      background: '#f0f9ff',
                      border: '1px solid #3b82f6',
                      borderRadius: '6px',
                      color: '#1e40af',
                      fontSize: '0.9em',
                      textAlign: 'center'
                    }}>
                      {uploadProgress.total > 0 ? (
                        <>📸 Uploading photos: {uploadProgress.current}/{uploadProgress.total}</>
                      ) : (
                        <>📤 Submitting... Please wait.</>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isLatestVisitsOpen} onOpenChange={(open) => {
        setIsLatestVisitsOpen(open);
        if (!open) restorePreviousTab();
      }}>
        <DialogContent className="dialog-content-large">
          <DialogHeader>
            <DialogTitle>Latest Visits Across All Restaurants</DialogTitle>
          </DialogHeader>
          <div className="latest-visits-content">
            {latestVisits.length === 0 ? (
              <p className="no-visits">No visits recorded yet.</p>
            ) : (
              <div className="visits-table-wrapper">
                <table className="visits-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Restaurant</th>
                      <th>Location</th>
                      <th className="directions-col">Directions</th>
                      <th>Diner</th>
                      <th>Rating</th>
                      <th>Favorites</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {latestVisits.map((visit) => (
                      <tr key={visit.id} data-testid={`latest-visit-${visit.id}`}>
                        <td className="visit-date-cell">{formatDateDisplay(visit.visitDate)}</td>
                        <td className="visit-restaurant-cell">
                          <strong 
                            className="clickable-restaurant-name"
                            onClick={async () => {
                              // Find the restaurant by ID and show visit history
                              try {
                                const response = await axios.get(`${API_URL}/api/restaurants/${visit.restaurantId}`);
                                if (response.data) {
                                  showVisitHistory(response.data);
                                }
                              } catch (error) {
                                console.error('Error fetching restaurant:', error);
                              }
                            }}
                            style={{ cursor: 'pointer', color: '#3a7050' }}
                            title="Click to view visit history"
                          >
                            {visit.restaurantName}
                          </strong>
                        </td>
                        <td className="visit-location-cell">{visit.restaurantLocation}</td>
                        <td className="directions-cell">
                          {visit.restaurantLatitude && visit.restaurantLongitude ? (
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${visit.restaurantLatitude},${visit.restaurantLongitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="directions-link"
                              title="Get directions to this restaurant"
                            >
                              🧭
                            </a>
                          ) : (
                            <span className="no-directions">–</span>
                          )}
                        </td>
                        <td className="visit-user-cell">
                          {visit.userName ? (
                            <span className="user-email">{visit.userName}</span>
                          ) : (
                            <span className="no-data">–</span>
                          )}
                        </td>
                        <td className="visit-rating-cell">
                          {visit.rating ? (
                            <span className="visit-rating-display">⭐ {visit.rating.toFixed(1)}/5</span>
                          ) : (
                            <span className="no-data">–</span>
                          )}
                        </td>
                        <td className="visit-favorites-cell">
                          {visit.favoriteItems || <span className="no-data">–</span>}
                        </td>
                        <td className="visit-notes-cell">
                          {visit.notes || <span className="no-data">–</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isVisitHistoryOpen} onOpenChange={(open) => {
        setIsVisitHistoryOpen(open);
        if (!open) {
          setIsFromRandomPick(false);
          restorePreviousTab();
        }
      }}>
        <DialogContent className="dialog-content-xlarge visit-history-dialog">
          <DialogHeader style={{ position: 'relative' }}>
            <DialogTitle className="visit-history-title">
              {/* Restaurant name - top line by itself */}
              <div className="history-dialog-name">
                {selectedRestaurantHistory?.name}
              </div>
              {/* All buttons on second line: < 🔊 > */}
              <div className="history-dialog-buttons">
                {!isFromRandomPick ? (
                  <button
                    onClick={navigateToPreviousRestaurant}
                    disabled={!selectedRestaurantHistory}
                    className="history-nav-btn history-nav-prev"
                    title="Previous Restaurant"
                  >
                    <ChevronLeft size={20} color="#3a7050" strokeWidth={2.5} />
                  </button>
                ) : <div className="history-nav-spacer" />}
                
                <button
                  onClick={handleSpeakRestaurantInfo}
                  className="history-nav-btn history-speak-btn"
                  title="Read restaurant name, favorites, and dislikes"
                >
                  <Volume2 size={18} color="#3a7050" strokeWidth={2} />
                </button>
                
                {!isFromRandomPick ? (
                  <button
                    onClick={navigateToNextRestaurant}
                    disabled={!selectedRestaurantHistory}
                    className="history-nav-btn history-nav-next"
                    title="Next Restaurant"
                  >
                    <ChevronRight size={20} color="#3a7050" strokeWidth={2.5} />
                  </button>
                ) : <div className="history-nav-spacer" />}
              </div>
            </DialogTitle>
          </DialogHeader>
          {selectedRestaurantHistory && (
            <div className="visit-history-content">
              {/* Two-Column Information Grid */}
              <div className="restaurant-info-card">
                <div className="restaurant-info-grid-two-col">
                  {/* Row 1 */}
                  <div className="info-item">
                    <span className="info-label">Location:</span>
                    <span className="info-value">{selectedRestaurantHistory.location}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Cuisine:</span>
                    <span className="info-value">
                      {(() => {
                        const icon = getCuisineIcon(selectedRestaurantHistory.ethnicity);
                        if (icon?.type === 'emoji') {
                          return <>{icon.value} {selectedRestaurantHistory.ethnicity || '–'}</>;
                        } else if (icon?.type === 'flag') {
                          return (
                            <>
                              <img 
                                src={`https://flagcdn.com/16x12/${icon.code}.png`}
                                srcSet={`https://flagcdn.com/32x24/${icon.code}.png 2x, https://flagcdn.com/48x36/${icon.code}.png 3x`}
                                width="16"
                                height="12"
                                alt=""
                                style={{ marginRight: '4px', verticalAlign: 'middle' }}
                              />
                              {selectedRestaurantHistory.ethnicity}
                            </>
                          );
                        }
                        return selectedRestaurantHistory.ethnicity || '–';
                      })()}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Full Address:</span>
                    <span className="info-value" style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start', flex: 1 }}>
                      {editingAddressId === selectedRestaurantHistory.id ? (
                        <>
                          <textarea
                            value={editingAddressValue}
                            onChange={(e) => setEditingAddressValue(e.target.value)}
                            rows={2}
                            style={{ flex: 1, padding: '0.4rem 0.5rem', fontSize: '0.875rem', border: '1px solid #cbd5e1', borderRadius: '4px', resize: 'vertical' }}
                            data-testid="address-edit-input"
                            placeholder="e.g., 2224 Hollyhill Lane, Denton, TX 76205"
                          />
                          <button
                            type="button"
                            onClick={async () => {
                              const newAddr = editingAddressValue.trim();
                              if (!newAddr) return;
                              try {
                                const res = await axios.post(
                                  `${API_URL}/api/restaurants/${selectedRestaurantHistory.id}/recalculate-geo`,
                                  { address: newAddr },
                                  { headers: { Authorization: `Bearer ${authToken}` } }
                                );
                                if (res.data.success) {
                                  const { new_coordinates: n } = res.data;
                                  setSelectedRestaurantHistory((prev) => prev && prev.id === selectedRestaurantHistory.id
                                    ? { ...prev, latitude: n.lat, longitude: n.lng, fullAddress: res.data.address_saved || newAddr, distance: 0 }
                                    : prev
                                  );
                                  setEditingAddressId(null);
                                  setEditingAddressValue('');
                                  fetchRestaurants();
                                } else {
                                  alert(`⚠️ ${res.data.message || 'Could not geocode this address.'}`);
                                }
                              } catch (err) {
                                alert(`❌ Failed: ${err.response?.data?.detail || err.message}`);
                              }
                            }}
                            data-testid="address-save-btn"
                            style={{ padding: '0.3rem 0.6rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
                            title="Save and re-geocode"
                          >
                            ✓
                          </button>
                          <button
                            type="button"
                            onClick={() => { setEditingAddressId(null); setEditingAddressValue(''); }}
                            data-testid="address-cancel-btn"
                            style={{ padding: '0.3rem 0.6rem', background: '#e5e7eb', color: '#475569', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <>
                          <span style={{ flex: 1 }}>{selectedRestaurantHistory.fullAddress || '–'}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingAddressId(selectedRestaurantHistory.id);
                              setEditingAddressValue(selectedRestaurantHistory.fullAddress || '');
                            }}
                            data-testid="address-edit-btn"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 0.25rem', color: '#3a7050' }}
                            title="Edit address (will re-geocode)"
                          >
                            ✏️
                          </button>
                        </>
                      )}
                    </span>
                  </div>
                  
                  {/* Row 2 */}
                  <div className="info-item">
                    <span className="info-label">Favorites:</span>
                    <span className="info-value">
                      {(() => {
                        // Consolidate and deduplicate favorites from all visits
                        const allFavorites = visitHistory
                          .map(v => v.favoriteItems)
                          .filter(f => f && f.trim())
                          .join(', ');
                        
                        if (!allFavorites) return '–';
                        
                        // Split by comma, trim, remove duplicates (case-insensitive)
                        const items = allFavorites.split(',').map(item => item.trim()).filter(item => item);
                        const uniqueItems = [...new Set(items.map(item => item.toLowerCase()))];
                        const displayItems = uniqueItems.map(lower => 
                          items.find(item => item.toLowerCase() === lower)
                        );
                        
                        return displayItems.join(', ');
                      })()}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Website:</span>
                    <span className="info-value">
                      {selectedRestaurantHistory.menuLink ? (
                        <a href={selectedRestaurantHistory.menuLink} target="_blank" rel="noopener noreferrer" className="menu-link-inline">
                          🔗 Visit Website
                        </a>
                      ) : '–'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Distance & Directions:</span>
                    <span className="info-value">
                      {selectedRestaurantHistory.distance !== undefined ? (
                        <>
                          {selectedRestaurantHistory.distance.toFixed(1)} miles
                          {selectedRestaurantHistory.latitude && selectedRestaurantHistory.longitude && (
                            <>
                              {' • '}
                              <a
                                href={`https://www.google.com/maps/dir/?api=1&destination=${selectedRestaurantHistory.latitude},${selectedRestaurantHistory.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="directions-link-inline"
                                title="Get directions"
                              >
                                Get Directions ↗
                              </a>
                            </>
                          )}
                          {' • '}
                          <button
                            type="button"
                            onClick={async () => {
                              await fixRestaurantLocation(selectedRestaurantHistory);
                            }}
                            data-testid="recalc-geo-btn"
                            className="directions-link-inline"
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
                            title="Use your current GPS or paste a corrected address"
                          >
                            📍 Fix Location
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={async () => {
                            await fixRestaurantLocation(selectedRestaurantHistory);
                          }}
                          data-testid="recalc-geo-btn"
                          className="directions-link-inline"
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
                          title="Use your current GPS or paste an address"
                        >
                          📍 Fix Location
                        </button>
                      )}
                    </span>
                  </div>
                  
                  {/* Row 3 */}
                  <div className="info-item">
                    <span className="info-label">Dislikes:</span>
                    <span className="info-value">
                      {(() => {
                        // Consolidate and deduplicate dislikes from all visits
                        const allDislikes = visitHistory
                          .map(v => v.dislikes)
                          .filter(d => d && d.trim())
                          .join(', ');
                        
                        if (!allDislikes) return '–';
                        
                        // Split by comma, trim, remove duplicates (case-insensitive)
                        const items = allDislikes.split(',').map(item => item.trim()).filter(item => item);
                        const uniqueItems = [...new Set(items.map(item => item.toLowerCase()))];
                        const displayItems = uniqueItems.map(lower => 
                          items.find(item => item.toLowerCase() === lower)
                        );
                        
                        return displayItems.join(', ');
                      })()}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Price:</span>
                    <span className="info-value">
                      {formatPriceToDollars(selectedRestaurantHistory.price)}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Staff Notes:</span>
                    <span className="info-value">{selectedRestaurantHistory.staff || '–'}</span>
                  </div>
                  
                  {/* Row 4 */}
                  <div className="info-item">
                    <span className="info-label">Last Visit:</span>
                    <span className="info-value">
                      {selectedRestaurantHistory.lastVisit ? (
                        <>
                          {selectedRestaurantHistory.lastVisit}
                          {' '}
                          <span style={{ color: '#64748b', fontSize: '0.9em' }}>
                            ({formatDaysBack(selectedRestaurantHistory.lastVisit)} days)
                          </span>
                        </>
                      ) : 'Never'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Total Visits:</span>
                    <span className="info-value">{selectedRestaurantHistory.visitCount || 0}</span>
                  </div>
                  <div className="info-item info-item-ratings">
                    <span className="info-label">Ratings:</span>
                    <span className="info-value" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <span>
                          Mine: ⭐ {(() => {
                            // Calculate average rating from visit history
                            const ratingsFromVisits = visitHistory.filter(v => v.rating && v.rating > 0);
                            if (ratingsFromVisits.length > 0) {
                              const avgRating = ratingsFromVisits.reduce((sum, v) => sum + v.rating, 0) / ratingsFromVisits.length;
                              return avgRating.toFixed(1);
                            }
                            return '–';
                          })()}/5
                        </span>
                        <span>
                          Bathroom: 🚻 {(() => {
                            // Show restaurant's restroom rating if available, otherwise calculate from visits
                            if (selectedRestaurantHistory.restroomRating !== null && selectedRestaurantHistory.restroomRating !== undefined) {
                              return selectedRestaurantHistory.restroomRating.toFixed(1);
                            }
                            // Fallback: Calculate average bathroom rating from visit history
                            const bathroomRatings = visitHistory.filter(v => v.bathroomRating !== null && v.bathroomRating !== undefined && v.bathroomRating >= 0);
                            if (bathroomRatings.length > 0) {
                              const avgBathroom = bathroomRatings.reduce((sum, v) => sum + v.bathroomRating, 0) / bathroomRatings.length;
                              return avgBathroom.toFixed(1);
                            }
                            return '–';
                          })()}/5
                        </span>
                      </span>
                      {selectedRestaurantHistory.googleRating && (
                        <a 
                          href={`https://www.google.com/search?q=${encodeURIComponent(selectedRestaurantHistory.name + ' ' + selectedRestaurantHistory.location)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rating-link-inline"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Google: ⭐ {selectedRestaurantHistory.googleRating}/5 ↗
                        </a>
                      )}
                    </span>
                  </div>
                  
                  <div className="info-item">
                    <span className="info-label">Total Spent:</span>
                    <span className="info-value">
                      💰 ${(() => {
                        // Calculate total cost from all visits
                        const visitsWithCost = visitHistory.filter(v => v.cost && v.cost > 0);
                        if (visitsWithCost.length > 0) {
                          const totalCost = visitsWithCost.reduce((sum, v) => sum + v.cost, 0);
                          return totalCost.toFixed(2);
                        }
                        return '0.00';
                      })()}
                      {(() => {
                        const visitsWithCost = visitHistory.filter(v => v.cost && v.cost > 0);
                        if (visitsWithCost.length > 0) {
                          const totalCost = visitsWithCost.reduce((sum, v) => sum + v.cost, 0);
                          const avgCost = totalCost / visitsWithCost.length;
                          return ` (avg $${avgCost.toFixed(2)}/visit)`;
                        }
                        return ' (no costs tracked)';
                      })()}
                    </span>
                  </div>
                  
                  <div className="info-item">
                    <span className="info-label">Guests:</span>
                    <span className="info-value">
                      👥 {(() => {
                        // Collect all unique guests from visits
                        const allGuests = visitHistory
                          .map(v => v.guests)
                          .filter(g => g && g.trim())
                          .flatMap(g => g.split(',').map(name => name.trim()))
                          .filter(name => name.length > 0);
                        
                        // Get unique guests
                        const uniqueGuests = [...new Set(allGuests)];
                        
                        if (uniqueGuests.length > 0) {
                          return uniqueGuests.join(', ');
                        }
                        return '–';
                      })()}
                    </span>
                  </div>
                  
                  {/* Row 5 */}
                  <div className="info-item">
                    <span className="info-label">Ownership:</span>
                    <span className="info-value">
                      {(() => {
                        const icon = getOwnershipIcon(selectedRestaurantHistory.ownership);
                        if (icon) {
                          return <>{icon} {selectedRestaurantHistory.ownership}</>;
                        }
                        return selectedRestaurantHistory.ownership || '–';
                      })()}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Amenities:</span>
                    <span className="info-value">
                      {(selectedRestaurantHistory.patio || selectedRestaurantHistory.dogFriendly || selectedRestaurantHistory.heineken) ? (
                        <>
                          {selectedRestaurantHistory.patio && '🏖️ Patio '}
                          {selectedRestaurantHistory.dogFriendly && '🐕 Dog Friendly '}
                          {selectedRestaurantHistory.heineken && '🍺 Heineken'}
                        </>
                      ) : '–'}
                    </span>
                  </div>
                  <div className="info-item">
                    {/* Empty cell */}
                  </div>
                </div>
              </div>
              
              {/* Record a Visit CTA */}
              <div className="record-visit-cta" style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                <Button
                  type="button"
                  data-testid="record-visit-from-detail-btn"
                  className="submit-btn"
                  onClick={() => {
                    if (!selectedRestaurantHistory) return;
                    // Pre-fill form for the current restaurant
                    const today = getLocalDateString();
                    setVisitDate(today);
                    setVisitUserName('');
                    setVisitFavorites('');
                    setVisitDislikes('');
                    setVisitNotes('');
                    setVisitRating('');
                    setVisitStaffNotes('');
                    setVisitBathroomRating('');
                    setVisitCost('');
                    setVisitGuests('');
                    setSelectedRestaurantForVisit(selectedRestaurantHistory);
                    setSelectedRestaurantId(selectedRestaurantHistory.id);
                    saveCurrentTab();
                    setIsVisitHistoryOpen(false);
                    setIsQuickAddVisitOpen(true);
                  }}
                >
                  ➕ Record a Visit
                </Button>
              </div>

              {/* All Visits Table */}
              <div className="visits-list-section">
                <h3 className="section-heading">All Visits ({visitHistory.length})</h3>
                {visitHistory.length === 0 ? (
                  <p className="no-visits">No visits recorded yet. Click "Add Visit" to record your first experience!</p>
                ) : (
                  <>
                    {/* Mobile View - Simple List */}
                    <div className="visits-mobile-list">
                      {visitHistory.map((visit) => (
                        <div 
                          key={visit.id} 
                          className="visit-mobile-item"
                          onClick={() => setSelectedVisitDetail(selectedVisitDetail?.id === visit.id ? null : visit)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="visit-mobile-header">
                            <span className="visit-mobile-date">📅 {formatDateDisplay(visit.visitDate)}</span>
                            <span className="visit-mobile-diner">👤 {visit.userName || '–'}</span>
                            <span className="visit-mobile-arrow">{selectedVisitDetail?.id === visit.id ? '▼' : '▶'}</span>
                          </div>
                          
                          {selectedVisitDetail?.id === visit.id && (
                            <div className="visit-mobile-details">
                              <div className="visit-detail-row">
                                <strong>Rating:</strong> {visit.rating ? `⭐ ${visit.rating.toFixed(1)}/5` : '–'}
                              </div>
                              <div className="visit-detail-row">
                                <strong>Favorites:</strong> {visit.favoriteItems || '–'}
                              </div>
                              <div className="visit-detail-row">
                                <strong>Dislikes:</strong> {visit.dislikes || '–'}
                              </div>
                              <div className="visit-detail-row">
                                <strong>Notes:</strong> {visit.notes || '–'}
                              </div>
                              <div className="visit-detail-row">
                                <strong>Cost:</strong> {visit.cost ? `💰 $${visit.cost.toFixed(2)}` : '–'}
                              </div>
                              <div className="visit-detail-row">
                                <strong>Guests:</strong> {visit.guests || '–'}
                              </div>
                              <div className="visit-detail-actions">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditVisitDialog(visit);
                                  }}
                                  style={{ marginRight: '8px' }}
                                >
                                  ✏️ Edit
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteVisit(visit.id);
                                  }}
                                  style={{ background: '#fee', color: '#c00' }}
                                >
                                  🗑️ Delete
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Desktop View - Full Table */}
                    <div className="visits-table-wrapper">
                      <table className="visits-table-enhanced">
                        <thead>
                          <tr>
                            <th>DATE</th>
                            <th>DINER</th>
                            <th>RATING</th>
                            <th>FAVORITES</th>
                            <th>DISLIKES</th>
                            <th>NOTES</th>
                            <th>COST</th>
                            <th>GUESTS</th>
                            <th>ACTIONS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visitHistory.map((visit) => (
                            <tr key={visit.id} data-testid={`visit-${visit.id}`}>
                              <td className="visit-date-cell">{formatDateDisplay(visit.visitDate)}</td>
                              <td className="visit-user-cell">
                                {visit.userName || <span className="no-data">–</span>}
                              </td>
                              <td className="visit-rating-cell">
                                {visit.rating ? `⭐ ${visit.rating.toFixed(1)}/5` : <span className="no-data">–</span>}
                              </td>
                              <td className="visit-favorites-cell">
                                {visit.favoriteItems || <span className="no-data">–</span>}
                              </td>
                              <td className="visit-dislikes-cell">
                                {visit.dislikes || <span className="no-data">–</span>}
                              </td>
                              <td className="visit-notes-cell">
                                {visit.notes || <span className="no-data">–</span>}
                              </td>
                              <td className="visit-cost-cell">
                                {visit.cost ? `💰 $${visit.cost.toFixed(2)}` : <span className="no-data">–</span>}
                              </td>
                              <td className="visit-guests-cell">
                                {visit.guests || <span className="no-data">–</span>}
                              </td>
                              <td className="visit-actions-cell">
                                <div className="action-buttons">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditVisitDialog(visit)}
                                    className="action-btn edit-btn"
                                    data-testid={`edit-visit-${visit.id}`}
                                    title="Edit visit"
                                  >
                                    ✏️
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteVisit(visit.id)}
                                    className="action-btn delete-btn"
                                    data-testid={`delete-visit-${visit.id}`}
                                    title="Delete visit"
                                  >
                                    🗑️
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              {/* Photos Section */}
              <div className="visit-history-photos-section">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <h3 className="section-heading" style={{ margin: 0 }}>Photos ({selectedRestaurantHistory.photos?.length || 0})</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedRestaurantPhotos(selectedRestaurantHistory);
                      setIsPhotosOpen(true);
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Camera size={16} />
                    Add Photos
                  </Button>
                </div>
                {!selectedRestaurantHistory.photos || selectedRestaurantHistory.photos.length === 0 ? (
                  <p className="no-photos">No photos yet. Click "Add Photos" to upload images!</p>
                ) : (
                  <div className="photos-inline-grid">
                    {selectedRestaurantHistory.photos.map((photo, index) => {
                      const thumbnailUrl = typeof photo === 'string' ? photo : photo.thumbnail;
                      const originalUrl = typeof photo === 'string' ? photo : photo.original;
                      
                      return (
                        <div key={index} className="photo-inline-item" data-testid={`inline-photo-${index}`}>
                          <img
                            src={getPhotoUrl(thumbnailUrl)}
                            alt={`${selectedRestaurantHistory.name} photo ${index + 1}`}
                            className="photo-inline-thumbnail"
                            onClick={() => {
                              setPhotoViewerPhotos(selectedRestaurantHistory.photos);
                              setCurrentPhotoIndex(index);
                              setIsPhotoViewerOpen(true);
                            }}
                            onError={(e) => {
                              console.error('Image load error:', thumbnailUrl);
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Viewer Modal */}
      <Dialog open={isPhotoViewerOpen} onOpenChange={setIsPhotoViewerOpen}>
        <DialogContent className="photo-viewer-dialog" style={{ maxWidth: '95vw', maxHeight: '95vh', padding: '0', background: '#000' }}>
          <div style={{ position: 'relative', width: '100%', height: '90vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* Close Button */}
            <button
              onClick={() => setIsPhotoViewerOpen(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '24px',
                cursor: 'pointer',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Close"
            >
              ×
            </button>

            {/* Previous Button */}
            {currentPhotoIndex > 0 && (
              <button
                onClick={() => setCurrentPhotoIndex(currentPhotoIndex - 1)}
                style={{
                  position: 'absolute',
                  left: '16px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '48px',
                  height: '48px',
                  fontSize: '28px',
                  cursor: 'pointer',
                  zIndex: 1000,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Previous Photo"
              >
                ‹
              </button>
            )}

            {/* Photo */}
            {photoViewerPhotos[currentPhotoIndex] && (
              <img
                src={getPhotoUrl(typeof photoViewerPhotos[currentPhotoIndex] === 'string' 
                  ? photoViewerPhotos[currentPhotoIndex] 
                  : photoViewerPhotos[currentPhotoIndex].original)}
                alt={`Photo ${currentPhotoIndex + 1}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            )}

            {/* Next Button */}
            {currentPhotoIndex < photoViewerPhotos.length - 1 && (
              <button
                onClick={() => setCurrentPhotoIndex(currentPhotoIndex + 1)}
                style={{
                  position: 'absolute',
                  right: '16px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '48px',
                  height: '48px',
                  fontSize: '28px',
                  cursor: 'pointer',
                  zIndex: 1000,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Next Photo"
              >
                ›
              </button>
            )}

            {/* Photo Counter */}
            <div
              style={{
                position: 'absolute',
                bottom: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '14px'
              }}
            >
              {currentPhotoIndex + 1} / {photoViewerPhotos.length}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isVisitDialogOpen} onOpenChange={(open) => {
        setIsVisitDialogOpen(open);
        if (!open) restorePreviousTab();
      }}>
        <DialogContent className="dialog-content">
          <DialogHeader>
            <DialogTitle>Add New Visit</DialogTitle>
          </DialogHeader>
          {selectedRestaurantForVisit && (
            <div>
              <p className="visit-restaurant-name">{selectedRestaurantForVisit.name}</p>
              <form onSubmit={handleAddVisit} className="visit-form">
                <div className="form-field">
                  <Label>Visit Date *</Label>
                  <Input
                    data-testid="visit-date"
                    type="date"
                    required
                    value={visitDate}
                    onChange={(e) => setVisitDate(e.target.value)}
                  />
                </div>

                <div className="form-field">
                  <Label>User Name (Email)</Label>
                  <Input
                    data-testid="visit-username"
                    type="email"
                    placeholder="user@example.com"
                    value={visitUserName}
                    onChange={(e) => setVisitUserName(e.target.value)}
                  />
                  <p className="field-hint">Who visited? Enter email address</p>
                </div>

                <div className="form-field">
                  <Label>Favorite Items from This Visit (Optional)</Label>
                  <Textarea
                    data-testid="visit-favorites"
                    placeholder="e.g., Grilled Salmon, Garlic Bread, Caesar Salad"
                    value={visitFavorites}
                    onChange={(e) => setVisitFavorites(e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="form-field">
                  <Label>Dislikes from This Visit (Optional)</Label>
                  <Textarea
                    data-testid="visit-dislikes"
                    placeholder="e.g., Overcooked steak, Too salty soup"
                    value={visitDislikes}
                    onChange={(e) => setVisitDislikes(e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="form-field">
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    data-testid="visit-notes"
                    placeholder="Any additional notes about this visit..."
                    value={visitNotes}
                    onChange={(e) => setVisitNotes(e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="form-field">
                  <Label>Rating (0-5)</Label>
                  <Input
                    data-testid="visit-rating"
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    placeholder="Rate this visit (e.g., 4.6)"
                    value={visitRating}
                    onChange={(e) => setVisitRating(e.target.value)}
                  />
                  <p className="field-hint">Rate your experience: 0 (worst) to 5 (best)</p>
                </div>

                <div className="form-field">
                  <Label>Staff Notes (Optional)</Label>
                  <Textarea
                    data-testid="visit-staff"
                    placeholder="Notes about staff service..."
                    value={visitStaffNotes}
                    onChange={(e) => setVisitStaffNotes(e.target.value)}
                    rows={2}
                  />
                  <p className="field-hint">How was the service?</p>
                </div>

                <div className="form-field">
                  <Label>Bathroom Rating (0-5)</Label>
                  <Input
                    data-testid="visit-bathroom"
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    placeholder="Rate the restrooms"
                    value={visitBathroomRating}
                    onChange={(e) => setVisitBathroomRating(e.target.value)}
                  />
                  <p className="field-hint">4+ = Golden Commode 🏆</p>
                </div>

                <div className="form-field">
                  <Label>Cost (Optional)</Label>
                  <Input
                    data-testid="visit-cost"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="How much did you spend? (e.g., 45.50)"
                    value={visitCost}
                    onChange={(e) => setVisitCost(e.target.value)}
                  />
                  <p className="field-hint">Track your spending - will be summed up on the restaurant history</p>
                </div>

                <div className="form-field">
                  <Label>Guests (Optional)</Label>
                  <Input
                    data-testid="visit-guests"
                    placeholder="Who joined you? (e.g., Sarah, Mike, John)"
                    value={visitGuests}
                    onChange={(e) => setVisitGuests(e.target.value)}
                  />
                  <p className="field-hint">Names of people who joined you for this visit</p>
                </div>

                <div className="form-field">
                  <Label>📸 Add Photos (Optional)</Label>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    <Input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleAddVisitPhotoSelect}
                      className="file-input"
                      id="add-visit-file-input"
                      style={{ display: 'none' }}
                    />
                    <Input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleAddVisitPhotoSelect}
                      className="file-input"
                      id="add-visit-camera-input"
                      style={{ display: 'none' }}
                    />
                    <Button
                      type="button"
                      onClick={() => document.getElementById('add-visit-file-input').click()}
                      style={{ flex: '1', minWidth: '140px' }}
                    >
                      <Camera size={16} />
                      {addVisitPhotos.length > 0 ? 'Add More' : 'Choose Photos'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => document.getElementById('add-visit-camera-input').click()}
                      style={{ flex: '1', minWidth: '140px', background: '#059669' }}
                    >
                      <Camera size={16} />
                      Take Photo
                    </Button>
                  </div>
                  {addVisitPhotos.length > 0 && (
                    <div>
                      <p style={{ fontSize: '0.9em', color: '#059669', marginBottom: '10px' }}>
                        ✓ {addVisitPhotos.length} photo(s) selected
                      </p>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {Array.from(addVisitPhotos).map((photo, index) => (
                          <div key={index} style={{ 
                            position: 'relative', 
                            width: '80px', 
                            height: '80px',
                            border: '2px solid #059669',
                            borderRadius: '8px',
                            overflow: 'hidden'
                          }}>
                            <img 
                              src={URL.createObjectURL(photo)} 
                              alt={`Preview ${index + 1}`}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newPhotos = Array.from(addVisitPhotos).filter((_, i) => i !== index);
                                setAddVisitPhotos(newPhotos);
                              }}
                              style={{
                                position: 'absolute',
                                top: '2px',
                                right: '2px',
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '12px',
                                padding: 0
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="visit-form-actions">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setIsVisitDialogOpen(false)}
                    data-testid="cancel-visit-btn"
                  >
                    Cancel
                  </Button>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Button 
                      type="submit" 
                      className="submit-btn"
                      data-testid="submit-visit-btn"
                      disabled={isSubmitting}
                      style={{ width: '100%' }}
                    >
                      {isSubmitting ? '⏳ Saving Visit...' : 'Save Visit'}
                    </Button>
                    {isSubmitting && (
                      <div style={{
                        marginTop: '10px',
                        padding: '8px',
                        background: '#f0f9ff',
                        border: '1px solid #3b82f6',
                        borderRadius: '6px',
                        color: '#1e40af',
                        fontSize: '0.85em',
                        textAlign: 'center',
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 10
                      }}>
                        {uploadProgress.total > 0 ? (
                          <>📸 {uploadProgress.current}/{uploadProgress.total}</>
                        ) : (
                          <>📤 Submitting...</>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Visit Dialog */}
      <Dialog open={isEditVisitOpen} onOpenChange={(open) => {
        setIsEditVisitOpen(open);
        if (!open) restorePreviousTab();
      }}>
        <DialogContent className="dialog-content">
          <DialogHeader>
            <DialogTitle>Edit Visit</DialogTitle>
          </DialogHeader>
          {selectedVisitForEdit && (
            <div>
              <p className="visit-restaurant-name">{selectedRestaurantHistory?.name}</p>
              <form onSubmit={handleUpdateVisit} className="visit-form">
                <div className="form-field">
                  <Label>Visit Date *</Label>
                  <Input
                    data-testid="edit-visit-date"
                    type="date"
                    required
                    value={visitDate}
                    onChange={(e) => setVisitDate(e.target.value)}
                  />
                </div>

                <div className="form-field">
                  <Label>Favorite Items from This Visit (Optional)</Label>
                  <Textarea
                    data-testid="edit-visit-favorites"
                    placeholder="e.g., Grilled Salmon, Garlic Bread, Caesar Salad"
                    value={visitFavorites}
                    onChange={(e) => setVisitFavorites(e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="form-field">
                  <Label>Dislikes from This Visit (Optional)</Label>
                  <Textarea
                    data-testid="edit-visit-dislikes"
                    placeholder="e.g., Overcooked steak, Too salty soup"
                    value={visitDislikes}
                    onChange={(e) => setVisitDislikes(e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="form-field">
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    data-testid="edit-visit-notes"
                    placeholder="Any additional notes about this visit..."
                    value={visitNotes}
                    onChange={(e) => setVisitNotes(e.target.value)}
                    rows={2}
                  />
                </div>

                <div className="form-field">
                  <Label>Rating (0-5)</Label>
                  <Input
                    data-testid="edit-visit-rating"
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    placeholder="Rate this visit (e.g., 4.6)"
                    value={visitRating}
                    onChange={(e) => setVisitRating(e.target.value)}
                  />
                  <p className="field-hint">Rate your experience: 0 (worst) to 5 (best)</p>
                </div>

                <div className="form-field">
                  <Label>Staff Notes (Optional)</Label>
                  <Textarea
                    data-testid="edit-visit-staff"
                    placeholder="Notes about staff service..."
                    value={visitStaffNotes}
                    onChange={(e) => setVisitStaffNotes(e.target.value)}
                    rows={2}
                  />
                  <p className="field-hint">How was the service?</p>
                </div>

                <div className="form-field">
                  <Label>Bathroom Rating (0-5)</Label>
                  <Input
                    data-testid="edit-visit-bathroom"
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    placeholder="Rate the restrooms"
                    value={visitBathroomRating}
                    onChange={(e) => setVisitBathroomRating(e.target.value)}
                  />
                  <p className="field-hint">4+ = Golden Commode 🏆</p>
                </div>

                <div className="form-field">
                  <Label>Guests (Optional)</Label>
                  <Input
                    data-testid="edit-visit-guests"
                    placeholder="Who joined you? (e.g., Sarah, Mike, John)"
                    value={visitGuests}
                    onChange={(e) => setVisitGuests(e.target.value)}
                  />
                  <p className="field-hint">Names of people who joined you for this visit</p>
                </div>

                <div className="visit-form-actions">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setIsEditVisitOpen(false)}
                    data-testid="cancel-edit-visit-btn"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="submit-btn"
                    data-testid="submit-edit-visit-btn"
                  >
                    Update Visit
                  </Button>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Restaurant Details Dialog - Mobile Only */}
      <Dialog open={isRestaurantDetailsOpen} onOpenChange={setIsRestaurantDetailsOpen}>
        <DialogContent className="dialog-content restaurant-details-dialog">
          <DialogHeader>
            <DialogTitle>{selectedRestaurantDetails?.name}</DialogTitle>
          </DialogHeader>
          {selectedRestaurantDetails && (
            <div className="restaurant-details-content" style={{ position: 'relative', paddingTop: '50px' }}>
              {/* Navigation Buttons - Mobile */}
              <div style={{ 
                position: 'absolute',
                top: '0px',
                left: '0',
                right: '0',
                height: '50px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingLeft: '8px',
                paddingRight: '8px',
                zIndex: 10
              }}>
                <button
                  onClick={navigateToPreviousRestaurant}
                  disabled={!selectedRestaurantDetails}
                  className="history-nav-btn history-nav-prev"
                  title="Previous Restaurant"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    border: '2px solid #3a7050',
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    flexShrink: 0
                  }}
                >
                  <ChevronLeft size={24} color="#3a7050" strokeWidth={2.5} />
                </button>
                <button
                  onClick={navigateToNextRestaurant}
                  disabled={!selectedRestaurantDetails}
                  className="history-nav-btn history-nav-next"
                  title="Next Restaurant"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    border: '2px solid #3a7050',
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    flexShrink: 0
                  }}
                >
                  <ChevronRight size={24} color="#3a7050" strokeWidth={2.5} />
                </button>
              </div>
              
              <div className="detail-section">
                <h4>Location</h4>
                <p>{selectedRestaurantDetails.location}</p>
              </div>

              <div className="detail-section">
                <h4>Cuisine</h4>
                <p><span className="cuisine-badge">{selectedRestaurantDetails.ethnicity || '–'}</span></p>
              </div>

              <div className="detail-section">
                <h4>Favorite Items</h4>
                <p>{selectedRestaurantDetails.favoriteItems || '–'}</p>
              </div>

              <div className="detail-section">
                <h4>Ownership</h4>
                <p><span className="ownership-badge">
                  {getOwnershipIcon(selectedRestaurantDetails.ownership) && (
                    <span className="ownership-icon">{getOwnershipIcon(selectedRestaurantDetails.ownership)} </span>
                  )}
                  {selectedRestaurantDetails.ownership || '–'}
                </span></p>
              </div>

              <div className="detail-section">
                <h4>Price</h4>
                <p><span className={`price-badge ${selectedRestaurantDetails.price?.toLowerCase()}`}>{selectedRestaurantDetails.price || '–'}</span></p>
              </div>

              <div className="detail-section">
                <h4>Ratings</h4>
                <div className="detail-ratings-container">
                  <div className="detail-rating-item">
                    <span className="detail-rating-label">Mine:</span>
                    {selectedRestaurantDetails.averageRating || selectedRestaurantDetails.overallRating ? (
                      <span className="rating-display">⭐ {(selectedRestaurantDetails.averageRating || selectedRestaurantDetails.overallRating)}/5</span>
                    ) : <span>–</span>}
                  </div>
                  {selectedRestaurantDetails.googleRating && (
                    <div className="detail-rating-item">
                      <span className="detail-rating-label">Google:</span>
                      <a 
                        href={`https://www.google.com/search?q=${encodeURIComponent(selectedRestaurantDetails.name + ' ' + selectedRestaurantDetails.location)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="detail-rating-link google-link"
                      >
                        {selectedRestaurantDetails.googleRating}/5 ↗
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h4>Visits</h4>
                <p><span className="visit-count-badge">{selectedRestaurantDetails.visitCount || 0}</span></p>
              </div>

              <div className="detail-section">
                <h4>Last Visit</h4>
                <p>{selectedRestaurantDetails.lastVisit || '–'}</p>
              </div>

              <div className="detail-section">
                <h4>Amenities</h4>
                <div className="amenities-icons">
                  <span className={`amenity-icon ${!selectedRestaurantDetails.patio ? 'amenity-unavailable' : ''}`} title={selectedRestaurantDetails.patio ? "Patio Available" : "No Patio"}>🏖️</span>
                  <span className={`amenity-icon ${!selectedRestaurantDetails.dogFriendly ? 'amenity-unavailable' : ''}`} title={selectedRestaurantDetails.dogFriendly ? "Dog Friendly" : "Not Dog Friendly"}>🐕</span>
                  <span className={`amenity-icon ${!selectedRestaurantDetails.heineken ? 'amenity-unavailable' : ''}`} title={selectedRestaurantDetails.heineken ? "Heineken Available" : "No Heineken"}>🍺</span>
                </div>
              </div>

              <div className="detail-section">
                <h4>Staff Notes</h4>
                <p>{selectedRestaurantDetails.staff || '–'}</p>
              </div>

              {selectedRestaurantDetails.menuLink && (
                <div className="detail-section">
                  <h4>Menu</h4>
                  <a href={selectedRestaurantDetails.menuLink} target="_blank" rel="noopener noreferrer" className="menu-link-btn">🔗 View Menu</a>
                </div>
              )}

              <div className="detail-actions">
                <Button onClick={() => {
                  setIsRestaurantDetailsOpen(false);
                  openPhotosDialog(selectedRestaurantDetails);
                }} className="detail-action-btn">
                  <Camera size={16} /> Photos
                </Button>
                <Button onClick={() => {
                  setIsRestaurantDetailsOpen(false);
                  openVisitDialog(selectedRestaurantDetails);
                }} className="detail-action-btn">
                  <Plus size={16} /> Add Visit
                </Button>
                <Button onClick={() => {
                  setIsRestaurantDetailsOpen(false);
                  openEditDialog(selectedRestaurantDetails);
                }} className="detail-action-btn">
                  <Edit2 size={16} /> Edit
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={isPhotosOpen} onOpenChange={(open) => {
        setIsPhotosOpen(open);
        if (!open) restorePreviousTab();
      }}>
        <DialogContent className="dialog-content-large">
          <DialogHeader>
            <DialogTitle>
              {selectedRestaurantPhotos?.name} - Photos
            </DialogTitle>
          </DialogHeader>
          <div className="photos-dialog-content">
            <div className="upload-section">
              <input
                type="file"
                accept="image/*"
                id={`photo-upload-${selectedRestaurantPhotos?.id}`}
                style={{ display: 'none' }}
                onChange={(e) => handlePhotoUpload(e, selectedRestaurantPhotos?.id)}
                data-testid="photo-upload-input"
              />
              <Button
                onClick={() => document.getElementById(`photo-upload-${selectedRestaurantPhotos?.id}`).click()}
                disabled={uploadingPhoto}
                data-testid="upload-photo-btn"
                className="upload-photo-btn"
              >
                <Camera size={18} />
                {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
              </Button>
              <p className="upload-hint">Max 5MB, JPG/PNG/GIF • Auto-resized for display • Click to view full size</p>
            </div>

            <div className="photos-gallery">
              {restaurantPhotos.length === 0 ? (
                <p className="no-photos">No photos yet. Upload your first photo!</p>
              ) : (
                <div className="photos-grid">
                  {restaurantPhotos.map((photo, index) => {
                    // Handle both old format (string) and new format (object with thumbnail/original)
                    const thumbnailUrl = typeof photo === 'string' ? photo : photo.thumbnail;
                    const originalUrl = typeof photo === 'string' ? photo : photo.original;
                    // Use the photo object's id, or the URL itself as the id for old format
                    const photoId = typeof photo === 'string' ? photo : (photo.id || photo.thumbnail || photo.original);
                    
                    return (
                      <div key={index} className="photo-item" data-testid={`photo-${index}`} style={{ position: 'relative' }}>
                        {/* Always visible delete X button */}
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm('Delete this photo?')) {
                              try {
                                await axios.delete(
                                  `${API_URL}/api/restaurants/${selectedRestaurantPhotos.id}/photos`,
                                  { params: { photo_id: photoId } }
                                );
                                
                                // Update local state immediately
                                const updatedPhotos = restaurantPhotos.filter((_, i) => i !== index);
                                setRestaurantPhotos(updatedPhotos);
                                
                                // Refresh other data
                                await fetchRestaurants();
                                await fetchPhotoMontage(activeTab === 'allDashboard');
                              } catch (error) {
                                console.error('Error deleting photo:', error);
                                alert('Error deleting photo. Please try again.');
                              }
                            }
                          }}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(220, 38, 38, 0.9)',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            zIndex: 10,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                          }}
                          title="Delete photo"
                        >
                          ×
                        </button>
                        <img
                          src={getPhotoUrl(thumbnailUrl)}
                          alt={`${selectedRestaurantPhotos?.name} photo ${index + 1}`}
                          className="photo-thumbnail"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setPhotoViewerPhotos(restaurantPhotos);
                            setCurrentPhotoIndex(index);
                            setIsPhotoViewerOpen(true);
                          }}
                          onError={(e) => {
                            console.error('Image load error:', thumbnailUrl);
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23ddd" width="200" height="200"/><text x="50%" y="50%" text-anchor="middle" fill="%23999">Image Error</text></svg>';
                          }}
                        />
                        <div className="photo-overlay">
                          <button
                            className="photo-view-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPhotoViewerPhotos(restaurantPhotos);
                              setCurrentPhotoIndex(index);
                              setIsPhotoViewerOpen(true);
                            }}
                            title="View full size"
                          >
                            👁️
                          </button>
                          <button
                            className="photo-delete-btn"
                            onClick={() => handleDeletePhoto(photoId)}
                            title="Delete photo"
                            data-testid={`delete-photo-${index}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditOpen} onOpenChange={(open) => {
        setIsEditOpen(open);
        if (!open) restorePreviousTab();
      }}>
        <DialogContent className="dialog-content">
          <DialogHeader>
            <DialogTitle>Edit Restaurant</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditRestaurant} className="restaurant-form">
            <div className="form-grid">
              <div className="form-field">
                <Label>Name *</Label>
                <Input
                  data-testid="edit-name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div className="form-field">
                <Label>Location *</Label>
                <Input
                  data-testid="edit-location"
                  required
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                />
              </div>
              <div className="form-field full-width">
                <Label>Full Address</Label>
                <Input
                  data-testid="edit-full-address"
                  value={formData.fullAddress}
                  onChange={handleFullAddressChange}
                  placeholder="Complete street address for mapping"
                />
              </div>
              <div className="form-field full-width">
                <Label>Favorite Items *</Label>
                <Textarea
                  data-testid="edit-favorite-items"
                  required
                  value={formData.favoriteItems}
                  onChange={(e) => setFormData({...formData, favoriteItems: e.target.value})}
                />
              </div>
              <div className="form-field full-width">
                <Label>Dislikes</Label>
                <Input
                  data-testid="edit-dislikes"
                  value={formData.dislikes}
                  onChange={(e) => setFormData({...formData, dislikes: e.target.value})}
                />
              </div>
              <div className="form-field">
                <Label>Price</Label>
                <select
                  data-testid="edit-price"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: e.target.value})}
                  className="form-select"
                >
                  <option value="">Select Price</option>
                  <option value="Cheap">Cheap ($)</option>
                  <option value="Bargain">Bargain ($$)</option>
                  <option value="Reasonable">Reasonable ($$$)</option>
                  <option value="Expensive">Expensive ($$$$)</option>
                  <option value="Stupid">Stupid ($$$$$)</option>
                </select>
              </div>
              <div className="form-field">
                <Label>Ownership</Label>
                <select
                  data-testid="edit-ownership"
                  value={formData.ownership}
                  onChange={(e) => setFormData({...formData, ownership: e.target.value})}
                  className="form-select"
                >
                  <option value="">Select Ownership</option>
                  <option value="Local">Local</option>
                  <option value="Franchise">Franchise</option>
                  <option value="Chain">Chain</option>
                  <option value="Family Owned">Family Owned</option>
                  <option value="Franchise/Local">Franchise/Local</option>
                </select>
              </div>
              <div className="form-field">
                <Label>Ethnicity/Cuisine</Label>
                <select
                  data-testid="edit-ethnicity"
                  value={formData.ethnicity}
                  onChange={(e) => {
                    setFormData({...formData, ethnicity: e.target.value});
                    if (e.target.value !== 'Other') {
                      setEthnicityOther('');
                    }
                  }}
                  className="form-select"
                >
                  <option value="">Select Cuisine</option>
                  {(() => {
                    // Comprehensive cuisine emoji mapping function
                    const getCuisineEmoji = (cuisine) => {
                      const lowerCuisine = cuisine.toLowerCase();
                      
                      // Country flags
                      const countryFlags = {
                        'american': '🇺🇸',
                        'chinese': '🇨🇳',
                        'french': '🇫🇷',
                        'german': '🇩🇪',
                        'greek': '🇬🇷',
                        'indian': '🇮🇳',
                        'irish': '🇮🇪',
                        'italian': '🇮🇹',
                        'japanese': '🇯🇵',
                        'korean': '🇰🇷',
                        'mexican': '🇲🇽',
                        'spanish': '🇪🇸',
                        'thai': '🇹🇭',
                        'turkish': '🇹🇷',
                        'vietnamese': '🇻🇳',
                        'brazilian': '🇧🇷',
                        'cuban': '🇨🇺',
                        'ethiopian': '🇪🇹',
                        'lebanese': '🇱🇧',
                        'moroccan': '🇲🇦',
                        'peruvian': '🇵🇪',
                        'polish': '🇵🇱',
                        'portuguese': '🇵🇹',
                        'russian': '🇷🇺'
                      };
                      
                      // Food type emojis
                      const foodEmojis = {
                        'bbq': '🥩',
                        'barbecue': '🥩',
                        'burger': '🍔',
                        'burgers': '🍔',
                        'breakfast': '🍳',
                        'brunch': '🥞',
                        'cafe': '☕',
                        'coffee': '☕',
                        'deli': '🥪',
                        'dessert': '🍰',
                        'pizza': '🍕',
                        'ramen': '🍜',
                        'seafood': '🐟',
                        'sushi': '🍣',
                        'steak': '🥩',
                        'steakhouse': '🥩',
                        'sandwich': '🥪',
                        'sandwiches': '🥪',
                        'taco': '🌮',
                        'tacos': '🌮',
                        'vegan': '🥗',
                        'vegetarian': '🥗',
                        'wings': '🍗',
                        'bakery': '🥖',
                        'bar': '🍺',
                        'pub': '🍺',
                        'grill': '🍖'
                      };
                      
                      // Check for country flag first
                      if (countryFlags[lowerCuisine]) {
                        return countryFlags[lowerCuisine];
                      }
                      
                      // Check for food emoji
                      if (foodEmojis[lowerCuisine]) {
                        return foodEmojis[lowerCuisine];
                      }
                      
                      // Default emoji for cuisines
                      return '🍽️';
                    };
                    
                    // Get all unique cuisines from database and merge with defaults
                    const allCuisines = [...new Set([
                      'American', 'BBQ', 'Chinese', 'Deli', 'Indian', 'Italian', 'Japanese', 
                      'Mediterranean', 'Mexican', 'Pizza', 'Seafood', 'Steakhouse', 'Thai',
                      ...uniqueCuisines.filter(c => c && c !== 'Other')
                    ])].sort();
                    
                    return allCuisines.map(cuisine => {
                      const emoji = getCuisineEmoji(cuisine);
                      return (
                        <option key={cuisine} value={cuisine}>
                          {emoji} {cuisine}
                        </option>
                      );
                    });
                  })()}
                  <option value="Other">Other (specify)</option>
                </select>
              </div>
              {formData.ethnicity === 'Other' && (
                <div className="form-field">
                  <Label>Specify Cuisine</Label>
                  <Input
                    data-testid="edit-ethnicity-other"
                    value={ethnicityOther}
                    onChange={(e) => setEthnicityOther(e.target.value)}
                    placeholder="Enter cuisine type"
                  />
                </div>
              )}
              <div className="form-field">
                <Label>Restroom Rating (0-5)</Label>
                <Input
                  data-testid="edit-restroom-rating"
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={formData.restroomRating}
                  onChange={(e) => setFormData({...formData, restroomRating: e.target.value})}
                  placeholder="4+ = Golden Commode 🏆"
                />
              </div>
              <div className="form-field">
                <Label>Overall Rating (0-5)</Label>
                <Input
                  data-testid="edit-overall-rating"
                  type="number"
                  min="0"
                  max="5"
                  step="0.1"
                  value={formData.overallRating}
                  onChange={(e) => setFormData({...formData, overallRating: e.target.value})}
                  placeholder="Your overall rating"
                />
              </div>
              <div className="form-field">
                <Label>Last Visit</Label>
                <Input
                  data-testid="edit-last-visit"
                  value={formData.lastVisit}
                  onChange={(e) => setFormData({...formData, lastVisit: e.target.value})}
                />
              </div>
              
              {/* Location Coordinates - visible for fixing wrong distances */}
              <div className="form-field">
                <Label>Latitude</Label>
                <Input
                  data-testid="edit-latitude"
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData({...formData, latitude: e.target.value})}
                  placeholder="e.g., 32.9858"
                />
              </div>
              <div className="form-field">
                <Label>Longitude</Label>
                <Input
                  data-testid="edit-longitude"
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData({...formData, longitude: e.target.value})}
                  placeholder="e.g., -96.7501"
                />
              </div>
              <div className="form-field full-width">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (navigator.geolocation) {
                      navigator.geolocation.getCurrentPosition(
                        (position) => {
                          setFormData({
                            ...formData,
                            latitude: position.coords.latitude.toFixed(6),
                            longitude: position.coords.longitude.toFixed(6)
                          });
                          alert('📍 Location updated to your current position!');
                        },
                        (error) => {
                          alert('Could not get your location: ' + error.message);
                        }
                      );
                    } else {
                      alert('Geolocation not supported by your browser');
                    }
                  }}
                  style={{ width: '100%', marginBottom: '0.5rem' }}
                >
                  📍 Use My Current Location
                </Button>
                <p style={{ fontSize: '0.75rem', color: '#666', margin: 0 }}>
                  Tip: Go to the restaurant and click this button, or look up coordinates on Google Maps
                </p>
              </div>
              
              <div className="form-field full-width">
                <Label>Staff Notes</Label>
                <Textarea
                  data-testid="edit-staff"
                  value={formData.staff}
                  onChange={(e) => setFormData({...formData, staff: e.target.value})}
                  placeholder="Notes about staff service..."
                />
              </div>
              <div className="form-field full-width">
                <Label>Menu Link</Label>
                <Input
                  data-testid="edit-menu-link"
                  value={formData.menuLink}
                  onChange={(e) => setFormData({...formData, menuLink: e.target.value})}
                />
              </div>
              <div className="checkbox-group">
                <div className="checkbox-item">
                  <Checkbox
                    data-testid="edit-patio"
                    checked={formData.patio}
                    onCheckedChange={(checked) => setFormData({...formData, patio: checked})}
                  />
                  <Label>Patio</Label>
                </div>
                <div className="checkbox-item">
                  <Checkbox
                    data-testid="edit-dog-friendly"
                    checked={formData.dogFriendly}
                    onCheckedChange={(checked) => setFormData({...formData, dogFriendly: checked})}
                  />
                  <Label>Dog Friendly</Label>
                </div>
                <div className="checkbox-item">
                  <Checkbox
                    data-testid="edit-heineken"
                    checked={formData.heineken}
                    onCheckedChange={(checked) => setFormData({...formData, heineken: checked})}
                  />
                  <Label>Heineken</Label>
                </div>
              </div>
            </div>
            
            {/* Refresh from Google button */}
            <div style={{ marginBottom: '1rem' }}>
              <Button 
                type="button"
                variant="outline"
                onClick={async () => {
                  if (!selectedRestaurant?.id) return;
                  try {
                    const response = await axios.post(`${API_URL}/api/restaurants/${selectedRestaurant.id}/refresh-google-data`);
                    if (response.data.success) {
                      // Update form with refreshed data
                      const updated = response.data.updated_fields || {};
                      setFormData(prev => ({
                        ...prev,
                        website: updated.website || prev.website,
                        phone: updated.phone || prev.phone,
                        fullAddress: updated.fullAddress || prev.fullAddress,
                        latitude: updated.latitude || prev.latitude,
                        longitude: updated.longitude || prev.longitude,
                        googleRating: updated.googleRating || prev.googleRating
                      }));
                      alert('✅ Refreshed from Google! Check updated fields above.');
                    } else {
                      alert('Could not find restaurant in Google Places: ' + (response.data.message || 'Unknown error'));
                    }
                  } catch (error) {
                    console.error('Error refreshing from Google:', error);
                    alert('Error refreshing from Google: ' + (error.response?.data?.detail || error.message));
                  }
                }}
                style={{ 
                  width: '100%', 
                  backgroundColor: '#f0f9ff', 
                  borderColor: '#0ea5e9',
                  color: '#0369a1'
                }}
              >
                🔄 Refresh from Google (fix location/phone/website)
              </Button>
            </div>
            
            <Button data-testid="submit-edit-btn" type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Update Restaurant'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isLatestVisitsOpen} onOpenChange={setIsLatestVisitsOpen}>
        <DialogContent className="dialog-content">
          <DialogHeader>
            <DialogTitle>Latest Visits</DialogTitle>
          </DialogHeader>
          <div className="latest-visits-content">
            {latestVisits.length === 0 ? (
              <p className="no-visits">No visits recorded yet.</p>
            ) : (
              <div className="visits-table-wrapper">
                <table className="visits-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Restaurant</th>
                      <th>Location</th>
                      <th className="directions-col">Directions</th>
                      <th>Diner</th>
                      <th>Rating</th>
                      <th>Favorites</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {latestVisits.map((visit) => (
                      <tr key={visit.id} data-testid={`latest-visit-${visit.id}`}>
                        <td className="visit-date-cell">{formatDateDisplay(visit.visitDate)}</td>
                        <td className="visit-restaurant-cell">
                          <strong 
                            className="clickable-restaurant-name"
                            onClick={async () => {
                              // Find the restaurant by ID and show visit history
                              try {
                                const response = await axios.get(`${API_URL}/api/restaurants/${visit.restaurantId}`);
                                if (response.data) {
                                  showVisitHistory(response.data);
                                }
                              } catch (error) {
                                console.error('Error fetching restaurant:', error);
                              }
                            }}
                            style={{ cursor: 'pointer', color: '#3a7050' }}
                            title="Click to view visit history"
                          >
                            {visit.restaurantName}
                          </strong>
                        </td>
                        <td className="visit-location-cell">{visit.restaurantLocation}</td>
                        <td className="directions-cell">
                          {visit.restaurantLatitude && visit.restaurantLongitude ? (
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${visit.restaurantLatitude},${visit.restaurantLongitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="directions-link"
                              title="Get directions to this restaurant"
                            >
                              🧭
                            </a>
                          ) : (
                            <span className="no-directions">–</span>
                          )}
                        </td>
                        <td className="visit-user-cell">
                          {visit.userName ? (
                            <span className="user-email">{visit.userName}</span>
                          ) : (
                            <span className="no-data">–</span>
                          )}
                        </td>
                        <td className="visit-rating-cell">
                          {visit.rating ? (
                            <span className="visit-rating-display">⭐ {visit.rating.toFixed(1)}/5</span>
                          ) : (
                            <span className="no-data">–</span>
                          )}
                        </td>
                        <td className="visit-favorites-cell">
                          {visit.favoriteItems || <span className="no-data">–</span>}
                        </td>
                        <td className="visit-notes-cell">
                          {visit.notes || <span className="no-data">–</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-2xl settings-dialog">
          <DialogHeader>
            <DialogTitle>Account Settings</DialogTitle>
          </DialogHeader>
          
          <div className="settings-content">
            {settingsError && (
              <div className="settings-error">
                {settingsError}
              </div>
            )}
            {settingsSuccess && (
              <div className="settings-success">
                {settingsSuccess}
              </div>
            )}

            {/* Change Display Name */}
            <div className="settings-section">
              <h3 className="settings-section-title">Change Display Name</h3>
              <div className="settings-current">
                Current: <strong>{displayName}</strong>
              </div>
              <div className="settings-form-group">
                <Label htmlFor="newDisplayName">New Display Name</Label>
                <Input
                  id="newDisplayName"
                  type="text"
                  value={settingsForm.newDisplayName}
                  onChange={(e) => setSettingsForm({ ...settingsForm, newDisplayName: e.target.value })}
                  placeholder="Enter new display name"
                />
              </div>
              <Button onClick={handleUpdateDisplayName} className="settings-submit-btn">
                Update Display Name
              </Button>
            </div>

            {/* Change Email */}
            <div className="settings-section">
              <h3 className="settings-section-title">Change Email Address</h3>
              <div className="settings-current">
                Current: <strong>{userEmail}</strong>
              </div>
              <div className="settings-form-group">
                <Label htmlFor="newEmail">New Email</Label>
                <Input
                  id="newEmail"
                  type="email"
                  value={settingsForm.newEmail}
                  onChange={(e) => setSettingsForm({ ...settingsForm, newEmail: e.target.value })}
                  placeholder="Enter new email address"
                />
              </div>
              <Button onClick={handleUpdateEmail} className="settings-submit-btn">
                Update Email
              </Button>
            </div>

            {/* Groups */}
            <div className="settings-section">
              <h3 className="settings-section-title">Group Sharing</h3>
              
              {/* Your Group (the one you own) */}
              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                <Label style={{ fontWeight: '600', color: '#166534', marginBottom: '0.5rem', display: 'block' }}>
                  🏠 Your Group (Share your restaurants)
                </Label>
                <p style={{ fontSize: '0.75rem', color: '#166534', marginBottom: '0.75rem' }}>
                  Create a group name (like "TheSchofields") that others can join to see your restaurants.
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <Input
                    type="text"
                    value={ownedGroup}
                    onChange={(e) => setOwnedGroup(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                    placeholder="e.g., TheSchofields"
                    style={{ flex: 1 }}
                  />
                  <Button 
                    onClick={async () => {
                      if (!ownedGroup.trim()) {
                        setSettingsError('Please enter a group name');
                        return;
                      }
                      try {
                        const response = await axios.post(`${API_URL}/api/users/create-group`, 
                          { group_name: ownedGroup.trim() },
                          { headers: { Authorization: `Bearer ${authToken}` } }
                        );
                        setSettingsSuccess(response.data.message);
                        // Add to user groups if not already there
                        if (!userGroups.toLowerCase().includes(ownedGroup.toLowerCase())) {
                          setUserGroups(userGroups ? `${userGroups},${ownedGroup}` : ownedGroup);
                        }
                        // Refresh all data to reflect group changes
                        await fetchAllUsersRestaurants();
                        setTimeout(() => setSettingsSuccess(''), 3000);
                      } catch (error) {
                        setSettingsError(error.response?.data?.detail || 'Failed to create group');
                        setTimeout(() => setSettingsError(''), 3000);
                      }
                    }}
                    size="sm"
                    style={{ background: '#22c55e', color: 'white' }}
                  >
                    {ownedGroup ? 'Update' : 'Create'}
                  </Button>
                </div>
              </div>
              
              {/* Join a Group */}
              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#eff6ff', borderRadius: '8px', border: '1px solid #93c5fd' }}>
                <Label style={{ fontWeight: '600', color: '#1e40af', marginBottom: '0.5rem', display: 'block' }}>
                  👥 Join a Group (See others' restaurants)
                </Label>
                <p style={{ fontSize: '0.75rem', color: '#1e40af', marginBottom: '0.75rem' }}>
                  Enter a group name someone shared with you to see their restaurants in "All Restaurants".
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <Input
                    type="text"
                    value={joinGroupName}
                    onChange={(e) => setJoinGroupName(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                    placeholder="Enter group name to join..."
                    style={{ flex: 1 }}
                  />
                  <Button 
                    onClick={async () => {
                      if (!joinGroupName.trim()) {
                        setSettingsError('Please enter a group name to join');
                        return;
                      }
                      try {
                        const response = await axios.post(`${API_URL}/api/users/join-group`, 
                          { group_name: joinGroupName.trim() },
                          { headers: { Authorization: `Bearer ${authToken}` } }
                        );
                        setSettingsSuccess(response.data.message);
                        setUserGroups(response.data.groups);
                        setJoinGroupName('');
                        // Refresh all data to reflect group changes
                        await fetchAllUsersRestaurants();
                        setTimeout(() => setSettingsSuccess(''), 3000);
                      } catch (error) {
                        setSettingsError(error.response?.data?.detail || 'Failed to join group');
                        setTimeout(() => setSettingsError(''), 3000);
                      }
                    }}
                    size="sm"
                    style={{ background: '#3b82f6', color: 'white' }}
                  >
                    Join
                  </Button>
                </div>
              </div>
              
              {/* Current Group Memberships */}
              <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <Label style={{ fontWeight: '600', color: '#334155', marginBottom: '0.5rem', display: 'block' }}>
                  📋 Your Group Memberships
                </Label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                  {userGroups.split(',').filter(g => g.trim()).map((group, idx) => (
                    <div 
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.35rem 0.75rem',
                        background: group.trim() === 'ALLDATA' ? '#fef3c7' : 
                                   group.trim().toLowerCase() === ownedGroup.toLowerCase() ? '#dcfce7' : '#e0e7ff',
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        color: group.trim() === 'ALLDATA' ? '#92400e' : 
                               group.trim().toLowerCase() === ownedGroup.toLowerCase() ? '#166534' : '#3730a3'
                      }}
                    >
                      {group.trim() === 'ALLDATA' ? '🌐' : 
                       group.trim().toLowerCase() === ownedGroup.toLowerCase() ? '🏠' : '👥'} {group.trim()}
                      {group.trim() !== 'ALLDATA' && group.trim().toLowerCase() !== ownedGroup.toLowerCase() && (
                        <button
                          onClick={async () => {
                            try {
                              const response = await axios.post(`${API_URL}/api/users/leave-group`,
                                { group_name: group.trim() },
                                { headers: { Authorization: `Bearer ${authToken}` } }
                              );
                              setUserGroups(response.data.groups);
                              setSettingsSuccess(`Left group "${group.trim()}"`);
                              // Refresh all data to reflect group changes
                              await fetchAllUsersRestaurants();
                              setTimeout(() => setSettingsSuccess(''), 3000);
                            } catch (error) {
                              setSettingsError(error.response?.data?.detail || 'Failed to leave group');
                              setTimeout(() => setSettingsError(''), 3000);
                            }
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0 0.25rem',
                            fontSize: '0.9rem',
                            color: '#64748b'
                          }}
                          title="Leave group"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  {!userGroups && (
                    <span style={{ color: '#64748b', fontSize: '0.8rem' }}>No groups joined yet</span>
                  )}
                </div>
                <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.75rem' }}>
                  🌐 ALLDATA = see all users' restaurants | 🏠 = your group | 👥 = joined groups
                </p>
              </div>
            </div>

            {/* Biometric Settings - moved to be in same row as Change Password */}
            <div className="settings-section">
              <h3 className="settings-section-title">Biometric Authentication</h3>
              {(() => {
                const bioAvailable = isBiometricAvailable();
                const bioEnrolled = bioAvailable && hasBiometricEnrolled(userEmail);
                if (!bioAvailable) {
                  return (
                    <p className="settings-description" style={{ color: '#64748b', fontSize: '0.9rem' }}>
                      Fingerprint / Face ID login isn't available on this device or browser.
                    </p>
                  );
                }
                return (
                  <>
                    <p className="settings-description" style={{ marginBottom: '1rem', color: '#64748b', fontSize: '0.9rem' }}>
                      {bioEnrolled
                        ? 'Fingerprint / Face ID login is enabled on this browser. Reset it here to re-enroll.'
                        : 'Enable Fingerprint / Face ID for faster login on this device.'}
                    </p>
                    {!bioEnrolled ? (
                      <Button
                        data-testid="enroll-biometric-btn"
                        onClick={async () => {
                          setSettingsError('');
                          setSettingsSuccess('');
                          const result = await registerBiometric(userEmail, authToken);
                          if (result.success) {
                            setSettingsSuccess('Fingerprint / Face ID enabled! You can now use it to log in.');
                            setTimeout(() => setSettingsSuccess(''), 4000);
                          } else {
                            setSettingsError('Could not enable biometric: ' + (result.error || 'unknown error'));
                            setTimeout(() => setSettingsError(''), 5000);
                          }
                        }}
                        className="settings-submit-btn"
                        style={{ backgroundColor: '#10b981', color: 'white' }}
                      >
                        🔒 Enable Fingerprint / Face ID
                      </Button>
                    ) : (
                      <Button
                        data-testid="reset-biometric-btn"
                        onClick={async () => {
                          if (!window.confirm('Remove fingerprint login from this browser? You can re-enable it right after.')) return;
                          setSettingsError('');
                          setSettingsSuccess('');
                          await removeBiometric(userEmail, authToken);
                          setSettingsSuccess('Fingerprint reset. Tap "Enable Fingerprint / Face ID" to re-enroll.');
                          setTimeout(() => setSettingsSuccess(''), 4000);
                        }}
                        className="settings-submit-btn"
                        style={{ backgroundColor: '#f59e0b', color: 'white' }}
                      >
                        🔓 Reset Fingerprint
                      </Button>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Change Password/PIN */}
            <div className="settings-section">
              <h3 className="settings-section-title">Change Password or PIN</h3>
              <div className="settings-form-group">
                <Label htmlFor="oldPin">Current Password or PIN</Label>
                <Input
                  id="oldPin"
                  type="password"
                  value={settingsForm.oldPin}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 12);
                    setSettingsForm({ ...settingsForm, oldPin: value });
                  }}
                  placeholder="Enter 4-12 characters"
                  maxLength={12}
                  minLength={4}
                />
              </div>
              <div className="settings-form-group">
                <Label htmlFor="newPin">New Password or PIN</Label>
                <Input
                  id="newPin"
                  type="password"
                  value={settingsForm.newPin}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 12);
                    setSettingsForm({ ...settingsForm, newPin: value });
                  }}
                  placeholder="Enter 4-12 characters"
                  maxLength={12}
                  minLength={4}
                />
              </div>
              <div className="settings-form-group">
                <Label htmlFor="confirmPin">Confirm New Password or PIN</Label>
                <Input
                  id="confirmPin"
                  type="password"
                  value={settingsForm.confirmPin}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 12);
                    setSettingsForm({ ...settingsForm, confirmPin: value });
                  }}
                  placeholder="Enter 4-12 characters"
                  maxLength={12}
                  minLength={4}
                />
              </div>
              <Button onClick={handleChangePIN} className="settings-submit-btn">
                Change Password
              </Button>
            </div>

            {/* Divider - separates admin sections */}
            <div style={{ 
              gridColumn: '1 / -1', 
              borderTop: '2px solid #e2e8f0', 
              margin: '0.5rem 0',
              padding: 0
            }}></div>

            {/* Footer Settings removed - now hardcoded */}
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Restaurant Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Restaurant Already Exists</DialogTitle>
          </DialogHeader>
          
          <div className="duplicate-dialog-content">
            <p className="duplicate-message">
              A restaurant named <strong>"{duplicateRestaurant?.name}"</strong> already exists.
              {duplicateRestaurant?.user_email !== userEmail && duplicateRestaurant?.ownerDisplayName && (
                <span className="owner-note"> (Added by {duplicateRestaurant.ownerDisplayName})</span>
              )}
            </p>
            
            {duplicateRestaurant && (
              <div className="existing-restaurant-info">
                <p><strong>Existing Restaurant:</strong></p>
                <p>📍 Location: {duplicateRestaurant.location}</p>
                {duplicateRestaurant.ethnicity && <p>🍽️ Cuisine: {duplicateRestaurant.ethnicity}</p>}
                {duplicateRestaurant.price && <p>💵 Price: {duplicateRestaurant.price}</p>}
              </div>
            )}
            
            <p className="duplicate-question">
              {duplicateRestaurant?.user_email === userEmail 
                ? "Is this the same restaurant you're trying to add?"
                : "Is this the same restaurant? (If it's a different location, click 'Add as New')"}
            </p>
            
            <div className="duplicate-actions">
              <Button
                onClick={() => handleDuplicateResponse(true)}
                className="duplicate-btn-yes"
              >
                Yes, Record a Visit
              </Button>
              <Button
                onClick={() => handleDuplicateResponse(false)}
                className="duplicate-btn-no"
                variant="outline"
              >
                No, Add as New Restaurant
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>



      {/* About Us Section */}
      {activeTab === 'about' && (
        <div className="about-us-section">
          <div className="about-content">
            <div className="about-hero">
              <div className="about-text-content">
                <h1 className="about-title">A Word <u>From</u> Bill Casso</h1>
                
                <div className="creator-letter">
                  <p><u>DineASaurus</u> was born out of necessity — my necessity. I eat out a lot, and like many of us who are getting a little older, I sometimes forget what I ate this morning or last week... I also seem to forget which restaurant has the salsa I love, or which one serves that suspiciously pink "red" sauce or which ones I hate (those I <u>kinda</u> remember).</p>
                  
                  <p>Historically, my wife is there to save <u>me</u>. Somehow, she remembers what I like, what I don't, and <u>where</u> I swore I'd "never order the meatloaf again." But every now and then I'm on my own, and obviously I can't call her just to ask what I like — that would show weakness. And one time she <u>forgot</u> and I got some crappy lasagna.</p>
                  
                  <p>So, thanks to Emergent AI and a few beers, I built this app to help me keep track.</p>
                  
                  <p>I hope you find it as useful (and entertaining) as I do. If you have ideas to make it better, I'm all ears — send them my way and I'll do my best to work them in.</p>
                  
                  <p className="signature">Thanks for being part of this,</p>
                  <p className="signature" style={{ marginTop: '0.5rem', fontSize: '1.25rem', fontWeight: '700' }}>— Bill Casso</p>
                </div>
              </div>
              
              <div className="about-image-content">
                <img src="/bill-casso.png" alt="Bill Casso" className="bill-casso-photo" />
              </div>
            </div>
            
            {/* Photo Gallery at Bottom - Latest First, Auto-scrolling */}
            <div className="about-photos-section">
              <h2 className="photos-title">Recent Photos from Our Community</h2>
              {photoMontage && photoMontage.length > 0 ? (
                <div className="photo-gallery-scrollable">
                  <div className="photo-gallery-track">
                    {/* Render photos twice for seamless loop */}
                    {[...photoMontage, ...photoMontage]
                      .sort((a, b) => {
                        // Sort by upload date, newest first (assuming uploadedAt or createdAt field)
                        const dateA = new Date(a.uploadedAt || a.createdAt || 0);
                        const dateB = new Date(b.uploadedAt || b.createdAt || 0);
                        return dateB - dateA;
                      })
                      .map((photo, index) => (
                        <div key={`photo-${index}`} className="gallery-photo-card">
                          <img 
                            src={getPhotoUrl(photo.thumbnailUrl || photo.originalUrl)}
                            alt={photo.restaurantName || 'Restaurant'} 
                            className="gallery-photo-img"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <div className="gallery-photo-caption">
                            {photo.restaurantName}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* On Deck Section */}
      {activeTab === 'activity' && (
        <ActivityTab
          viewingAsUser={viewingAsUser}
          userEmail={viewingAsUser?.email || userEmail}
          onRestaurantClick={(restaurantId) => {
            const match = [...restaurants, ...allUsersRestaurants, ...allRestaurantsList].find((r) => r.id === restaurantId);
            if (match) openRestaurantDetailsDialog(match);
          }}
        />
      )}

      {activeTab === 'onDeck' && (
        <div className="on-deck-section">
          <div className="on-deck-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '1rem' }}>
              <div style={{ flex: '0 1 auto' }}>
                <h1 className="on-deck-title" style={{ fontSize: '1.8rem', margin: '0', lineHeight: '1', whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <UtensilsCrossed size={24} style={{ color: '#667eea' }} />
                    On Deck
                    <Target size={18} style={{ color: '#764ba2', marginLeft: '0.2rem' }} />
                  </div>
                </h1>
              </div>
              <Button onClick={() => setIsTryThisDialogOpen(true)} className="add-on-deck-btn" style={{ fontSize: '0.9rem', padding: '0.6rem 1rem', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Sparkles size={14} />
                  Add Restaurant
                </div>
              </Button>
            </div>
            <p className="on-deck-subtitle" style={{ fontSize: '0.85rem', margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <Star size={12} style={{ color: '#f59e0b' }} />
              Restaurants to try ({onDeckRestaurants.length})
              {onDeckRestaurants.length > 0 && <Rocket size={12} style={{ color: '#ef4444', marginLeft: '0.2rem' }} />}
            </p>
            
            {onDeckRestaurants.length === 0 ? (
              <div className="on-deck-placeholder">
                <div style={{ fontSize: '3rem', marginBottom: '1rem', display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                  <UtensilsCrossed size={48} style={{ color: '#667eea' }} />
                  <Target size={48} style={{ color: '#764ba2' }} />
                </div>
                <p style={{ fontSize: '1.2rem', color: '#64748b', textAlign: 'center', padding: '2rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem', marginBottom: '0.5rem' }}>
                    <Star size={18} style={{ color: '#f59e0b' }} />
                    No restaurants on deck yet!
                    <Star size={18} style={{ color: '#f59e0b' }} />
                  </div>
                  <span style={{ fontSize: '1rem', color: '#94a3b8', marginTop: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3rem' }}>
                    Click <Sparkles size={16} style={{ color: '#667eea' }} /> Add Restaurant or "Try This!" to start your culinary adventure!
                    <Rocket size={16} style={{ color: '#ef4444', marginLeft: '0.3rem' }} />
                  </span>
                </p>
              </div>
            ) : (
              <div className="table-container" style={{ width: '100%', maxWidth: '100%' }}>
                <table className="restaurants-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th 
                        onClick={() => setOnDeckSortBy('name')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        title="Click to sort by name"
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem' }}>
                          <Store size={12} />
                          Restaurant
                          {onDeckSortBy === 'name' && <span style={{ marginLeft: '0.2rem' }}>↕</span>}
                        </div>
                      </th>
                      <th>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem' }}>
                          <Calendar size={12} />
                          Added
                        </div>
                      </th>
                      <th 
                        onClick={() => setOnDeckSortBy('distance')}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        title="Click to sort by distance"
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem' }}>
                          <MapPin size={12} />
                          Distance
                          {onDeckSortBy === 'distance' && <span style={{ marginLeft: '0.2rem' }}>↕</span>}
                        </div>
                      </th>
                      <th>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem' }}>
                          <Map size={12} />
                          Location
                        </div>
                      </th>
                      <th>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem' }}>
                          <Info size={12} />
                          Info
                        </div>
                      </th>
                      <th>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem' }}>
                          <Zap size={12} />
                          Actions
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // Sort restaurants based on selected option
                      const sorted = [...onDeckRestaurants].sort((a, b) => {
                        if (onDeckSortBy === 'name') {
                          return a.name.localeCompare(b.name);
                        } else if (onDeckSortBy === 'distance') {
                          // Calculate distances
                          let distanceA = 999999;
                          let distanceB = 999999;
                          
                          if (userLocation && a.latitude && a.longitude) {
                            distanceA = calculateDistance(
                              userLocation.latitude,
                              userLocation.longitude,
                              a.latitude,
                              a.longitude
                            );
                          }
                          
                          if (userLocation && b.latitude && b.longitude) {
                            distanceB = calculateDistance(
                              userLocation.latitude,
                              userLocation.longitude,
                              b.latitude,
                              b.longitude
                            );
                          }
                          
                          return distanceA - distanceB;
                        }
                        return 0;
                      });
                      
                      return sorted.map((restaurant) => {
                      // Calculate distance if location available
                      let distance = null;
                      let distanceDisplay = '–';
                      
                      if (userLocation && restaurant.latitude && restaurant.longitude) {
                        distance = calculateDistance(
                          userLocation.latitude,
                          userLocation.longitude,
                          restaurant.latitude,
                          restaurant.longitude
                        );
                        distanceDisplay = `${distance.toFixed(1)} mi`;
                      } else if (restaurant.latitude && restaurant.longitude) {
                        // Has location but userLocation not available
                        distanceDisplay = 'Near you';
                      }

                      // Check if any Google Places info was found
                      const hasGoogleInfo = restaurant.website || restaurant.phone || restaurant.rating;
                      
                      return (
                        <tr key={restaurant.id} className="on-deck-row">
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                              <Utensils size={12} style={{ color: '#667eea' }} />
                              <strong style={{ color: '#1e293b', fontSize: '0.9rem' }}>{restaurant.name}</strong>
                            </div>
                          </td>
                          <td style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                              <Calendar size={10} />
                              {formatDateDisplay(restaurant.dateAdded)}
                            </div>
                          </td>
                          <td 
                            style={{ fontSize: '0.8rem', fontWeight: '500' }}
                            onClick={() => {
                              if (restaurant.latitude && restaurant.longitude) {
                                window.open(
                                  `https://www.google.com/maps/dir/?api=1&destination=${restaurant.latitude},${restaurant.longitude}`,
                                  '_blank'
                                );
                              }
                            }}
                            className="distance-cell-clickable"
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                              {distanceDisplay !== '–' && <MapPin size={10} />}
                              {distanceDisplay}
                            </div>
                          </td>
                          <td>
                            {restaurant.fullAddress ? (
                              <div style={{ fontSize: '0.75rem', color: '#64748b', lineHeight: '1.3', display: 'flex', alignItems: 'flex-start', gap: '0.2rem' }}>
                                <Map size={10} style={{ marginTop: '0.05rem', flexShrink: 0 }} />
                                <span style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                  {restaurant.fullAddress}
                                </span>
                              </div>
                            ) : (
                              <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.7rem' }}>–</span>
                            )}
                          </td>
                          <td>
                            {hasGoogleInfo ? (
                              <div style={{ fontSize: '0.75rem', lineHeight: '1.4' }}>
                                {/* Rating and Website on same line */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.15rem' }}>
                                  {restaurant.rating && (
                                    <span style={{ color: '#f59e0b', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '0.15rem' }}>
                                      <Star size={11} />
                                      {restaurant.rating.toFixed(1)}/5
                                    </span>
                                  )}
                                  {restaurant.website && (
                                    <a href={restaurant.website} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.15rem' }}>
                                      🌐 Website
                                    </a>
                                  )}
                                </div>
                                {/* Phone on separate line */}
                                {restaurant.phone && (
                                  <div style={{ fontSize: '0.72rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.15rem' }}>
                                    📞 {restaurant.phone}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                                <Info size={10} />
                                No info found
                              </div>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.15rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                              {restaurant.latitude && restaurant.longitude && (
                                <button
                                  onClick={() => window.open(
                                    `https://www.google.com/maps/dir/?api=1&destination=${restaurant.latitude},${restaurant.longitude}`,
                                    '_blank'
                                  )}
                                  title="Get directions"
                                  className="action-btn-tiny"
                                  style={{ padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '3px', background: 'white', cursor: 'pointer', fontSize: '0.7rem' }}
                                >
                                  <Navigation size={10} />
                                </button>
                              )}
                              <button
                                onClick={() => window.open(
                                  `https://www.google.com/search?q=${encodeURIComponent(restaurant.name)}`,
                                  '_blank'
                                )}
                                title="Search web"
                                className="action-btn-tiny"
                                style={{ padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '3px', background: 'white', cursor: 'pointer', fontSize: '0.7rem' }}
                              >
                                <Search size={10} />
                              </button>
                              <button
                                onClick={() => handleEditOnDeck(restaurant)}
                                title="Edit"
                                className="action-btn-tiny edit-btn-sm"
                                style={{ padding: '0.25rem', border: '1px solid #d1d5db', borderRadius: '3px', background: 'white', cursor: 'pointer', fontSize: '0.7rem' }}
                              >
                                <Edit2 size={10} />
                              </button>
                              <button
                                onClick={() => handleDeleteOnDeck(restaurant.id)}
                                title="Remove"
                                className="action-btn-tiny delete-btn-sm"
                                style={{ padding: '0.25rem', border: '1px solid #dc2626', borderRadius: '3px', background: 'white', cursor: 'pointer', color: '#dc2626', fontSize: '0.7rem' }}
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    });
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New version available banner */}
      {isAuthenticated && updateAvailable && (
        <div
          data-testid="update-available-banner"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            backgroundColor: '#2c5f2d',
            color: '#ffffff',
            padding: '0.6rem 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            fontSize: '0.9rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
          }}
        >
          <span>
            A new version (v{footerSettings.version}) is available.
          </span>
          <button
            data-testid="update-refresh-btn"
            onClick={() => {
              try {
                if ('caches' in window) {
                  caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
                }
              } catch (e) { /* ignore */ }
              window.location.reload();
            }}
            style={{
              backgroundColor: '#ffffff',
              color: '#2c5f2d',
              border: 'none',
              padding: '0.35rem 0.9rem',
              borderRadius: '6px',
              fontWeight: 'bold',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            Refresh
          </button>
          <button
            data-testid="update-dismiss-btn"
            onClick={() => setUpdateAvailable(false)}
            aria-label="Dismiss"
            style={{
              background: 'transparent',
              color: '#ffffff',
              border: '1px solid rgba(255,255,255,0.4)',
              padding: '0.2rem 0.55rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.75rem'
            }}
          >
            Later
          </button>
        </div>
      )}

      {/* Footer */}
      {isAuthenticated && (
        <footer className="app-footer" style={{ 
          backgroundColor: '#1e293b',
          padding: '1.5rem 2rem',
          borderTop: '3px solid #667eea',
          textAlign: 'center',
          marginTop: '2rem'
        }}>
          <div className="footer-content">
            <a 
              href={footerSettings.siteUrl}
              style={{
                color: '#f1f5f9',
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: '0.95rem'
              }}
              data-testid="app-footer-site-name"
            >
              © {footerSettings.copyrightYears || '2025'} {footerSettings.siteName}
              <VersionBadge version={footerSettings.version} />
            </a>
            <p style={{
              color: '#94a3b8',
              fontSize: '0.85rem',
              margin: '0.25rem 0 0 0'
            }} data-testid="app-footer-created-by">
              Created by {footerSettings.createdBy}
            </p>
          </div>
        </footer>
      )}

      {/* iOS Add-to-Home-Screen hint (first-time iPad/iPhone Safari only) */}
      <AddToHomeScreen />

      {/* Edit On Deck Restaurant Dialog */}
      <Dialog open={isEditOnDeckOpen} onOpenChange={setIsEditOnDeckOpen}>
        <DialogContent className="dialog-content" style={{ maxWidth: '500px', padding: '2rem' }}>
          <DialogHeader>
            <DialogTitle style={{ fontSize: '1.5rem', marginBottom: '1.5rem', fontWeight: '600' }}>Edit Restaurant</DialogTitle>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div>
              <label 
                htmlFor="edit-on-deck-name" 
                style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '500',
                  fontSize: '0.95rem',
                  color: '#374151'
                }}
              >
                Restaurant Name
              </label>
              <input
                id="edit-on-deck-name"
                type="text"
                value={editOnDeckName}
                onChange={(e) => setEditOnDeckName(e.target.value)}
                placeholder="Enter restaurant name"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  outline: 'none'
                }}
              />
            </div>
            <div>
              <label 
                htmlFor="edit-on-deck-address" 
                style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem', 
                  fontWeight: '500',
                  fontSize: '0.95rem',
                  color: '#374151'
                }}
              >
                Address
              </label>
              <textarea
                id="edit-on-deck-address"
                value={editOnDeckAddress}
                onChange={(e) => setEditOnDeckAddress(e.target.value)}
                placeholder="Enter address (optional)"
                rows="4"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <Button 
                onClick={() => {
                  setIsEditOnDeckOpen(false);
                  setSelectedOnDeckRestaurant(null);
                  setEditOnDeckName('');
                  setEditOnDeckAddress('');
                }}
                variant="outline"
                style={{ padding: '0.75rem 1.5rem' }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveOnDeckEdit}
                className="submit-btn"
                disabled={!editOnDeckName.trim() || isSubmitting}
                style={{ padding: '0.75rem 1.5rem' }}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Try This! Dialog with Voice Input */}
      <Dialog open={isTryThisDialogOpen} onOpenChange={setIsTryThisDialogOpen}>
        <DialogContent className="search-dialog">
          <DialogHeader>
            <DialogTitle>Add Restaurant to Try</DialogTitle>
          </DialogHeader>
          <div className="search-dialog-content">
            <p style={{ marginBottom: '1rem', color: '#64748b' }}>
              Enter or speak the name of a restaurant you'd like to try
            </p>
            <div className="search-input-container">
              <input
                type="text"
                placeholder="Restaurant name..."
                value={tryThisRestaurantName}
                onChange={(e) => setTryThisRestaurantName(e.target.value)}
                className="search-input-dialog"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && tryThisRestaurantName.trim()) {
                    handleAddToOnDeck();
                  }
                }}
              />
              <button 
                onClick={startVoiceTryThis}
                className={`voice-search-btn-dialog ${isListening ? 'listening' : ''}`}
                title={isListening ? "Listening..." : "Voice input"}
                disabled={isListening}
              >
                <Mic size={20} className={isListening ? 'mic-pulse' : ''} />
              </button>
            </div>
            {isListening && (
              <div className="listening-indicator">
                <div className="pulse-dot"></div>
                <span>Listening... speak now</span>
              </div>
            )}
            <div className="search-dialog-actions">
              <Button 
                onClick={() => {
                  setTryThisRestaurantName('');
                  setIsTryThisDialogOpen(false);
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAddToOnDeck}
                className="search-apply-btn"
                disabled={!tryThisRestaurantName.trim() || isSubmitting}
              >
                {isSubmitting ? 'Adding...' : 'Add to On Deck'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Search Dialog with Voice Input */}
      <Dialog open={isSearchDialogOpen} onOpenChange={setIsSearchDialogOpen}>
        <DialogContent className="search-dialog">
          <DialogHeader>
            <DialogTitle>Search Restaurants</DialogTitle>
          </DialogHeader>
          <div className="search-dialog-content">
            <div className="search-input-container">
              <Search className="search-icon-dialog" size={20} />
              <input
                type="text"
                placeholder="Restaurant name, location, cuisine, or favorite items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input-dialog"
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && searchQuery.trim()) {
                    setIsSearchDialogOpen(false);
                  }
                }}
              />
              <button 
                onClick={startVoiceSearch}
                className={`voice-search-btn-dialog ${isListening ? 'listening' : ''}`}
                title={isListening ? "Listening..." : "Voice search"}
                disabled={isListening}
              >
                <Mic size={20} className={isListening ? 'mic-pulse' : ''} />
              </button>
            </div>
            {isListening && (
              <div className="listening-indicator">
                <div className="pulse-dot"></div>
                <span>Listening... speak now</span>
              </div>
            )}
            <div className="search-dialog-actions">
              <Button 
                onClick={() => {
                  setSearchQuery('');
                  setIsSearchDialogOpen(false);
                }}
                variant="outline"
              >
                Clear & Close
              </Button>
              <Button 
                onClick={() => {
                  if (searchQuery.trim()) {
                    setIsSearchDialogOpen(false);
                  }
                }}
                className="search-apply-btn"
                disabled={!searchQuery.trim()}
              >
                Search
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


export default App;
